import { addDays, subDays, addHours, format } from "date-fns";

export interface Client {
  id?: string;
  _id?: string;
  name: string;
  email: string;
  phone: string;
  age: number;
  gender: "male" | "female" | "other";
  goal: string;
  image: string;
  height?: number; // Added height property
  weight?: number; // Added weight property
  dietType?: string; // Added dietType property
  allergies?: string[]; // Added allergies property
  foodPreferences?: string; // Added foodPreferences property
  profession?: string; // Added profession field
  hasWhatsApp?: boolean; // Added WhatsApp indicator
  visitCount?: { // Added visit tracking
    total: number;
    thisMonth: number;
    thisWeek: number;
  };
  outstandingBalance?: number; // Added outstanding balance field
}

export interface Meeting {
  id?: string;
  _id?: string;
  client: string;
  clientId?: string; // Keep for backward compatibility
  title: string;
  startTime: Date | string;
  endTime: Date | string;
  type: "initial" | "follow-up" | "assessment";
  notes?: string;
  status?: "scheduled" | "completed" | "cancelled" | "no-show";
}

export interface FoodItem {
  id?: string;
  name: string;
  category: string;
  refuse: number;
  water: number;
  energy: number;
  protein: number;
  fat: number;
  ash: number;
  fiber: number;
  carbohydrate: number;
  sodium: number;
  potassium: number;
  calcium: number;
  phosphorus: number;
  magnesium: number;
  iron: number;
  zinc: number | string;
  copper: number;
  vitaminA: number | string;
  vitaminC: number | string;
  thiamin: number;
  riboflavin: number;
  servingSize: string;
}

export interface Exercise {
  id?: string;
  _id?: string;
  name: string;
  category: string;
  targetMuscleGroup: string;
  difficulty: string;
  instructions: string;
  image?: string;
  video?: string;
  caloriesBurn?: number;
  equipment?: string[];
}

// Added new type for financial records
export interface FinancialRecord {
  id?: string;
  _id?: string;
  date: Date | string;
  type: "income" | "expense";
  amount: number;
  description: string;
  category: string;
  client?: string;
  clientId?: string; // Keep for backward compatibility
  paymentMethod?: string;
  status?: string;
}

const today = new Date();

export const clients: Client[] = [
  {
    id: "c1",
    name: "John Smith",
    email: "john.smith@example.com",
    phone: "(555) 123-4567",
    age: 35,
    gender: "male",
    goal: "Weight loss and muscle gain",
    image: "https://randomuser.me/api/portraits/men/1.jpg",
    height: 180,
    weight: 85,
    dietType: "Low carb",
    allergies: ["Peanuts", "Shellfish"],
    foodPreferences: "High protein meals, no red meat",
    profession: "Software Engineer",
    hasWhatsApp: true,
    visitCount: {
      total: 8,
      thisMonth: 2,
      thisWeek: 1
    },
    outstandingBalance: 150
  },
  {
    id: "c2",
    name: "Emma Johnson",
    email: "emma.j@example.com",
    phone: "(555) 987-6543",
    age: 28,
    gender: "female",
    goal: "Training for marathon",
    image: "https://randomuser.me/api/portraits/women/2.jpg",
    height: 165,
    weight: 58,
    dietType: "High carb",
    allergies: ["Lactose"],
    foodPreferences: "Plant-based, whole foods",
    profession: "Physical Therapist",
    hasWhatsApp: true,
    visitCount: {
      total: 12,
      thisMonth: 3,
      thisWeek: 1
    },
    outstandingBalance: 0
  },
  {
    id: "c3",
    name: "Michael Chen",
    email: "m.chen@example.com",
    phone: "(555) 456-7890",
    age: 42,
    gender: "male",
    goal: "Manage diabetes through diet",
    image: "https://randomuser.me/api/portraits/men/3.jpg",
    height: 175,
    weight: 78,
    dietType: "Low GI",
    allergies: ["Soy"],
    foodPreferences: "Low sugar, complex carbohydrates",
    profession: "Accountant",
    hasWhatsApp: false,
    visitCount: {
      total: 5,
      thisMonth: 1,
      thisWeek: 0
    },
    outstandingBalance: 75
  },
  {
    id: "c4",
    name: "Sophia Rodriguez",
    email: "sophia.r@example.com",
    phone: "(555) 234-5678",
    age: 31,
    gender: "female",
    goal: "Build strength and improve flexibility",
    image: "https://randomuser.me/api/portraits/women/4.jpg",
    height: 170,
    weight: 62,
    dietType: "Mediterranean",
    allergies: [],
    foodPreferences: "Fish, olive oil, nuts, and whole grains",
    profession: "Yoga Instructor",
    hasWhatsApp: true,
    visitCount: {
      total: 15,
      thisMonth: 4,
      thisWeek: 2
    },
    outstandingBalance: 0
  },
  {
    id: "c5",
    name: "James Wilson",
    email: "james.w@example.com",
    phone: "(555) 876-5432",
    age: 45,
    gender: "male",
    goal: "Lower cholesterol levels",
    image: "https://randomuser.me/api/portraits/men/5.jpg",
    height: 183,
    weight: 90,
    dietType: "Heart-healthy",
    allergies: ["Gluten"],
    foodPreferences: "Low-fat, high-fiber foods",
    profession: "Construction Manager",
    hasWhatsApp: false,
    visitCount: {
      total: 3,
      thisMonth: 1,
      thisWeek: 0
    },
    outstandingBalance: 200
  },
];

export const meetings: Meeting[] = [
  {
    id: "m1",
    client: "c1",
    clientId: "c1",
    title: "Initial Consultation - John Smith",
    startTime: addHours(today, 2),
    endTime: addHours(today, 3),
    type: "initial",
    notes: "First meeting to discuss weight loss goals and current health status."
  },
  {
    id: "m2",
    client: "c2",
    clientId: "c2",
    title: "Follow-up - Emma Johnson",
    startTime: addDays(today, 1),
    endTime: addDays(addHours(today, 1), 1),
    type: "follow-up",
    notes: "Check progress on marathon training diet."
  },
  {
    id: "m3",
    client: "c3",
    clientId: "c3",
    title: "Assessment - Michael Chen",
    startTime: addDays(today, 2),
    endTime: addDays(addHours(today, 2), 2),
    type: "assessment",
    notes: "Comprehensive assessment of current diabetes management plan."
  },
  {
    id: "m4",
    client: "c4",
    clientId: "c4",
    title: "Follow-up - Sophia Rodriguez",
    startTime: addDays(today, 3),
    endTime: addDays(addHours(today, 3), 3),
    type: "follow-up",
    notes: "Review progress on strength building and flexibility program."
  },
  {
    id: "m5",
    client: "c5",
    clientId: "c5",
    title: "Initial Consultation - James Wilson",
    startTime: addDays(today, 4),
    endTime: addDays(addHours(today, 4), 4),
    type: "initial",
    notes: "Discussion on heart-healthy diet options and lifestyle changes."
  },
];

export const foodItems: FoodItem[] = [
  {
    "id": "f1",
    "name": "Barley, grains",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 8.8,
    "energy": 335,
    "protein": 10.7,
    "fat": 1.5,
    "ash": 2.9,
    "fiber": 6.5,
    "carbohydrate": 69.6,
    "sodium": 55,
    "potassium": 299,
    "calcium": 104,
    "phosphorus": 184,
    "magnesium": 26,
    "iron": 4.63,
    "zinc": 2.25,
    "copper": 0.41,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.3,
    "riboflavin": 0.11,
    "servingSize": "100g"
  },
  {
    "id": "f2",
    "name": "Corn, grains (white)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 10.7,
    "energy": 364,
    "protein": 10.2,
    "fat": 4,
    "ash": 1.3,
    "fiber": 1.9,
    "carbohydrate": 71.9,
    "sodium": 8,
    "potassium": 125,
    "calcium": 27,
    "phosphorus": 254,
    "magnesium": 25,
    "iron": 2.42,
    "zinc": 1.7,
    "copper": 0.21,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.37,
    "riboflavin": 0.12,
    "servingSize": "100g"
  },
  {
    "id": "f3",
    "name": "Corn, grains (yellow)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 10.1,
    "energy": 368,
    "protein": 12.7,
    "fat": 5.5,
    "ash": 1.8,
    "fiber": 2.9,
    "carbohydrate": 67,
    "sodium": 10,
    "potassium": 161,
    "calcium": 26,
    "phosphorus": 239,
    "magnesium": 30,
    "iron": 2.6,
    "zinc": 1.5,
    "copper": 0.19,
    "vitaminA": 120,
    "vitaminC": 0,
    "thiamin": 0.39,
    "riboflavin": 0.11,
    "servingSize": "100g"
  },
  {
    "id": "f4",
    "name": "Cornflakes",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 5,
    "energy": 373,
    "protein": 6.2,
    "fat": 2.1,
    "ash": 2.4,
    "fiber": 2,
    "carbohydrate": 82.3,
    "sodium": 464,
    "potassium": 139,
    "calcium": 15,
    "phosphorus": 44,
    "magnesium": 11,
    "iron": 1.4,
    "zinc": 1,
    "copper": 0.23,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.5,
    "riboflavin": 0.09,
    "servingSize": "100g"
  },
  {
    "id": "f5",
    "name": "Corn, flour",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 11.1,
    "energy": 359,
    "protein": 8.9,
    "fat": 2.5,
    "ash": 1.3,
    "fiber": 1,
    "carbohydrate": 75.2,
    "sodium": 6,
    "potassium": 95,
    "calcium": 22,
    "phosphorus": 183,
    "magnesium": 20,
    "iron": 2.3,
    "zinc": 1.05,
    "copper": 0.19,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0,
    "servingSize": "100g"
  },
  {
    "id": "f6",
    "name": "Corn, kernels, canned",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 74.5,
    "energy": 100,
    "protein": 2.8,
    "fat": 0.9,
    "ash": 0.9,
    "fiber": 0.8,
    "carbohydrate": 20.1,
    "sodium": 301,
    "potassium": 82,
    "calcium": 5,
    "phosphorus": 65,
    "magnesium": null,
    "iron": 0.65,
    "zinc": 0.5,
    "copper": 0.05,
    "vitaminA": 25,
    "vitaminC": 7,
    "thiamin": 0.03,
    "riboflavin": 0.04,
    "servingSize": "100g"
  },
  {
    "id": "f7",
    "name": "Corn, kernels on cob, grilled",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 39,
    "water": 56.9,
    "energy": 161,
    "protein": 4,
    "fat": 1,
    "ash": 1,
    "fiber": 3,
    "carbohydrate": 34.1,
    "sodium": 4,
    "potassium": 69,
    "calcium": 7,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.02,
    "zinc": 1.2,
    "copper": 0.14,
    "vitaminA": 0,
    "vitaminC": 8,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f8",
    "name": "Corn, popped",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 1.8,
    "energy": 451,
    "protein": 9.1,
    "fat": 16.7,
    "ash": 3.9,
    "fiber": 2.5,
    "carbohydrate": 66,
    "sodium": 706,
    "potassium": 158,
    "calcium": 20,
    "phosphorus": 293,
    "magnesium": null,
    "iron": 1.8,
    "zinc": 1.5,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f9",
    "name": "Corn, puffed",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 2.7,
    "energy": 503,
    "protein": 7.8,
    "fat": 25.7,
    "ash": 2.2,
    "fiber": 1.5,
    "carbohydrate": 60.1,
    "sodium": 687,
    "potassium": 190,
    "calcium": 32,
    "phosphorus": 231,
    "magnesium": null,
    "iron": 2,
    "zinc": 1.3,
    "copper": null,
    "vitaminA": 30,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f10",
    "name": "Corn, starch",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 5,
    "energy": 380,
    "protein": 0.2,
    "fat": 0.1,
    "ash": 0.2,
    "fiber": 0,
    "carbohydrate": 94.5,
    "sodium": 5,
    "potassium": 7,
    "calcium": 0,
    "phosphorus": 30,
    "magnesium": 0,
    "iron": 0,
    "zinc": 0,
    "copper": 0,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0,
    "servingSize": "100g"
  },
  {
    "id": "f11",
    "name": "Macaroni",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 9.9,
    "energy": 361,
    "protein": 12,
    "fat": 1.1,
    "ash": 0.8,
    "fiber": 0.4,
    "carbohydrate": 75.8,
    "sodium": 10,
    "potassium": 176,
    "calcium": 35,
    "phosphorus": 128,
    "magnesium": 22,
    "iron": 1.4,
    "zinc": 1.65,
    "copper": 0.16,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.17,
    "riboflavin": 0.05,
    "servingSize": "100g"
  },
  {
    "id": "f12",
    "name": "Macaroni/tomato, cooked",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 59.3,
    "energy": 169,
    "protein": 5,
    "fat": 3,
    "ash": 1.5,
    "fiber": 0.7,
    "carbohydrate": 30.5,
    "sodium": 288,
    "potassium": 95,
    "calcium": 15,
    "phosphorus": 66,
    "magnesium": null,
    "iron": 0.7,
    "zinc": 0.56,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f13",
    "name": "Macaroni/tomato/meat, cooked",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 61,
    "energy": 175,
    "protein": 6.8,
    "fat": 5.6,
    "ash": 1.6,
    "fiber": 0.6,
    "carbohydrate": 24.4,
    "sodium": 342,
    "potassium": 184,
    "calcium": 30,
    "phosphorus": 130,
    "magnesium": null,
    "iron": 1.1,
    "zinc": 1.05,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f14",
    "name": "Noodles, fried",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 52,
    "energy": 218,
    "protein": 5.1,
    "fat": 6,
    "ash": 0.6,
    "fiber": 0.3,
    "carbohydrate": 36,
    "sodium": 250,
    "potassium": 88,
    "calcium": 18,
    "phosphorus": 42,
    "magnesium": null,
    "iron": 0.6,
    "zinc": 0.7,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f15",
    "name": "Rice, grains (long)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 11.2,
    "energy": 357,
    "protein": 8.7,
    "fat": 0.8,
    "ash": 0.3,
    "fiber": 0.3,
    "carbohydrate": 78.7,
    "sodium": 13,
    "potassium": 108,
    "calcium": 22,
    "phosphorus": 108,
    "magnesium": 15,
    "iron": 0.77,
    "zinc": 1.3,
    "copper": 0.23,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.09,
    "riboflavin": 0.04,
    "servingSize": "100g"
  },
  {
    "id": "f16",
    "name": "Rice, grains (short)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 12.5,
    "energy": 351,
    "protein": 7.3,
    "fat": 0.7,
    "ash": 0.4,
    "fiber": 0.3,
    "carbohydrate": 78.8,
    "sodium": 11,
    "potassium": 93,
    "calcium": 30,
    "phosphorus": 98,
    "magnesium": 23,
    "iron": 0.69,
    "zinc": 1.05,
    "copper": 0.31,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.1,
    "riboflavin": 0.03,
    "servingSize": "100g"
  },
  {
    "id": "f17",
    "name": "Rice, flour",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 11.6,
    "energy": 354,
    "protein": 7.3,
    "fat": 0.5,
    "ash": 0.3,
    "fiber": 0.3,
    "carbohydrate": 80,
    "sodium": 13,
    "potassium": 80,
    "calcium": 18,
    "phosphorus": 106,
    "magnesium": 10,
    "iron": 0.8,
    "zinc": 0.76,
    "copper": 0.2,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f18",
    "name": "Rice, fried",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 49.8,
    "energy": 214,
    "protein": 3.6,
    "fat": 3.9,
    "ash": 1.3,
    "fiber": 0.3,
    "carbohydrate": 41.1,
    "sodium": 278,
    "potassium": 67,
    "calcium": 7,
    "phosphorus": 43,
    "magnesium": null,
    "iron": 0.25,
    "zinc": 0.43,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f19",
    "name": "Rice, fried with noodles",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 49.4,
    "energy": 219,
    "protein": 3.5,
    "fat": 4.5,
    "ash": 1.2,
    "fiber": 0.3,
    "carbohydrate": 41.1,
    "sodium": 299,
    "potassium": 75,
    "calcium": 18,
    "phosphorus": 52,
    "magnesium": null,
    "iron": 0.7,
    "zinc": 0.45,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f20",
    "name": "Rice, fried with onions",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 53.7,
    "energy": 203,
    "protein": 3.1,
    "fat": 4.6,
    "ash": 0.8,
    "fiber": 0.6,
    "carbohydrate": 37.2,
    "sodium": 283,
    "potassium": 42,
    "calcium": 15,
    "phosphorus": 62,
    "magnesium": null,
    "iron": 1,
    "zinc": 0.8,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f21",
    "name": "Rice, fortified-baby food",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 4,
    "energy": 420,
    "protein": 16.2,
    "fat": 10,
    "ash": 2.9,
    "fiber": 0.5,
    "carbohydrate": 66.4,
    "sodium": 170,
    "potassium": 780,
    "calcium": 555,
    "phosphorus": 460,
    "magnesium": 13,
    "iron": 7,
    "zinc": 2.2,
    "copper": null,
    "vitaminA": 375,
    "vitaminC": 40,
    "thiamin": 0.6,
    "riboflavin": 0.4,
    "servingSize": "100g"
  },
  {
    "id": "f22",
    "name": "Rice (Koshari)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 61,
    "energy": 172,
    "protein": 6.5,
    "fat": 5.2,
    "ash": 1.5,
    "fiber": 1.1,
    "carbohydrate": 24.7,
    "sodium": 324,
    "potassium": 228,
    "calcium": 10,
    "phosphorus": 32,
    "magnesium": null,
    "iron": 1.4,
    "zinc": 1.3,
    "copper": 0.2,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f23",
    "name": "Rice (Koshari), market",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 63,
    "energy": 157,
    "protein": 4.5,
    "fat": 3.7,
    "ash": 1.6,
    "fiber": 0.9,
    "carbohydrate": 26.3,
    "sodium": 401,
    "potassium": 185,
    "calcium": 8,
    "phosphorus": 27,
    "magnesium": null,
    "iron": 0.82,
    "zinc": 1.11,
    "copper": 0.12,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f24",
    "name": "Rice, pudding with milk",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 70,
    "energy": 129,
    "protein": 3.3,
    "fat": 2.6,
    "ash": 0.5,
    "fiber": 0.5,
    "carbohydrate": 23.1,
    "sodium": 25,
    "potassium": 125,
    "calcium": 58,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.05,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f25",
    "name": "Rice, puffed/crisp",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 5,
    "energy": 379,
    "protein": 9,
    "fat": 0.7,
    "ash": 0.8,
    "fiber": 0.3,
    "carbohydrate": 84.2,
    "sodium": 115,
    "potassium": 141,
    "calcium": 14,
    "phosphorus": 105,
    "magnesium": 13,
    "iron": 1.01,
    "zinc": 0.98,
    "copper": 0.23,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f26",
    "name": "Semolina",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 11,
    "energy": 360,
    "protein": 12,
    "fat": 1.8,
    "ash": 0.8,
    "fiber": 0.4,
    "carbohydrate": 74,
    "sodium": 5,
    "potassium": 208,
    "calcium": 26,
    "phosphorus": 114,
    "magnesium": 10,
    "iron": 2.02,
    "zinc": 1.9,
    "copper": 0.15,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.2,
    "riboflavin": 0.1,
    "servingSize": "100g"
  },
  {
    "id": "f27",
    "name": "Sorghum, grains",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 8.9,
    "energy": 366,
    "protein": 10.9,
    "fat": 3.6,
    "ash": 1.8,
    "fiber": 2.2,
    "carbohydrate": 72.6,
    "sodium": 10,
    "potassium": 131,
    "calcium": 40,
    "phosphorus": 250,
    "magnesium": 23,
    "iron": 4.4,
    "zinc": 3.06,
    "copper": 0.41,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.21,
    "riboflavin": 0.1,
    "servingSize": "100g"
  },
  {
    "id": "f28",
    "name": "Wheat, bran",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 10.2,
    "energy": 313,
    "protein": 14.4,
    "fat": 3.9,
    "ash": 6.5,
    "fiber": 10,
    "carbohydrate": 55,
    "sodium": 60,
    "potassium": 1251,
    "calcium": 91,
    "phosphorus": 997,
    "magnesium": null,
    "iron": 10.5,
    "zinc": 8,
    "copper": 0.95,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.75,
    "riboflavin": 0.3,
    "servingSize": "100g"
  },
  {
    "id": "f29",
    "name": "Wheat, grains",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 12.1,
    "energy": 344,
    "protein": 12,
    "fat": 1.6,
    "ash": 1.6,
    "fiber": 2.4,
    "carbohydrate": 70.3,
    "sodium": 10,
    "potassium": 315,
    "calcium": 35,
    "phosphorus": 412,
    "magnesium": 88,
    "iron": 3.1,
    "zinc": 2.9,
    "copper": 0.33,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.61,
    "riboflavin": 0.12,
    "servingSize": "100g"
  },
  {
    "id": "f30",
    "name": "Wheat, grains (Ek-lila, canned)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 75.9,
    "energy": 95,
    "protein": 2.4,
    "fat": 0.6,
    "ash": 0.5,
    "fiber": 0.6,
    "carbohydrate": 20,
    "sodium": 72,
    "potassium": 79,
    "calcium": 15,
    "phosphorus": null,
    "magnesium": 12,
    "iron": 0.87,
    "zinc": 0.48,
    "copper": 0.05,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f31",
    "name": "Wheat, flour (brown)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 12.1,
    "energy": 350,
    "protein": 11.8,
    "fat": 1.3,
    "ash": 1,
    "fiber": 1.1,
    "carbohydrate": 72.7,
    "sodium": 8,
    "potassium": 265,
    "calcium": 28,
    "phosphorus": 118,
    "magnesium": 27,
    "iron": 2.6,
    "zinc": 1.82,
    "copper": 0.3,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0.11,
    "servingSize": "100g"
  },
  {
    "id": "f32",
    "name": "Wheat, flour (white)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 11.8,
    "energy": 354,
    "protein": 10,
    "fat": 1,
    "ash": 0.5,
    "fiber": 0.4,
    "carbohydrate": 76.3,
    "sodium": 6,
    "potassium": 120,
    "calcium": 18,
    "phosphorus": 92,
    "magnesium": 22,
    "iron": 0.85,
    "zinc": 1.5,
    "copper": 0.13,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0.05,
    "servingSize": "100g"
  },
  {
    "id": "f33",
    "name": "Wheat, fortified-baby food",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 3.5,
    "energy": 413,
    "protein": 17,
    "fat": 10,
    "ash": 3.5,
    "fiber": 2.2,
    "carbohydrate": 63.8,
    "sodium": 135,
    "potassium": 850,
    "calcium": 550,
    "phosphorus": 460,
    "magnesium": null,
    "iron": 8.25,
    "zinc": 2.5,
    "copper": null,
    "vitaminA": 375,
    "vitaminC": 40,
    "thiamin": 0.5,
    "riboflavin": 0.5,
    "servingSize": "100g"
  },
  {
    "id": "f34",
    "name": "Wheat, Koskos plain",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 47,
    "energy": 213,
    "protein": 5.8,
    "fat": 0.7,
    "ash": 0.4,
    "fiber": 0.3,
    "carbohydrate": 45.8,
    "sodium": 4,
    "potassium": 62,
    "calcium": 17,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.41,
    "zinc": 0.6,
    "copper": 0.05,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f35",
    "name": "Wheat, Koskos sweet",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 40.6,
    "energy": 283,
    "protein": 5.8,
    "fat": 9.8,
    "ash": 0.4,
    "fiber": 0.5,
    "carbohydrate": 42.9,
    "sodium": 7,
    "potassium": 65,
    "calcium": 15,
    "phosphorus": 92,
    "magnesium": null,
    "iron": 0.4,
    "zinc": 0.58,
    "copper": 0.6,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f36",
    "name": "Wheat, parboiled (Burghut)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 11.8,
    "energy": 352,
    "protein": 12.5,
    "fat": 1.8,
    "ash": 1.5,
    "fiber": 1,
    "carbohydrate": 71.4,
    "sodium": 37,
    "potassium": 350,
    "calcium": 43,
    "phosphorus": 405,
    "magnesium": 66,
    "iron": 3.2,
    "zinc": 4.2,
    "copper": 0.31,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": 0.35,
    "riboflavin": 0.14,
    "servingSize": "100g"
  },
  {
    "id": "f37",
    "name": "Wheat, rubbed (Fereek)",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 10.8,
    "energy": 351,
    "protein": 11.6,
    "fat": 1.7,
    "ash": 1.4,
    "fiber": 2.1,
    "carbohydrate": 72.4,
    "sodium": 22,
    "potassium": 300,
    "calcium": 41,
    "phosphorus": 320,
    "magnesium": 56,
    "iron": 3,
    "zinc": 3.5,
    "copper": 0.3,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": 0.57,
    "riboflavin": 0.12,
    "servingSize": "100g"
  },
  {
    "id": "f38",
    "name": "Wheat, rubbed, cooked",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 53.5,
    "energy": 199,
    "protein": 5.3,
    "fat": 4,
    "ash": 0.8,
    "fiber": 1,
    "carbohydrate": 35.4,
    "sodium": 309,
    "potassium": 115,
    "calcium": 15,
    "phosphorus": 120,
    "magnesium": null,
    "iron": 1.4,
    "zinc": 1.6,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f39",
    "name": "Wheat, rubbed/meat, cooked",
    "category": "Cereals and Cereal-based Foods",
    "refuse": 0,
    "water": 53,
    "energy": 213,
    "protein": 7.8,
    "fat": 6.4,
    "ash": 1.2,
    "fiber": 0.6,
    "carbohydrate": 31,
    "sodium": 354,
    "potassium": 154,
    "calcium": 35,
    "phosphorus": 188,
    "magnesium": null,
    "iron": 2.07,
    "zinc": 1.87,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": "100g"
  },
  {
    "id": "f40",
    "name": "Biscuit, chocolate coat",
    "category": "Bakery",
    "refuse": 0,
    "water": 2.7,
    "energy": 517,
    "protein": 10.5,
    "fat": 27.5,
    "ash": 1.5,
    "fiber": 1,
    "carbohydrate": 56.8,
    "sodium": 200,
    "potassium": 235,
    "calcium": 110,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.95,
    "zinc": 0.78,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.04,
    "riboflavin": 0.13,
    "servingSize": null
  },
  {
    "id": "f41",
    "name": "Biscuit, cream fill",
    "category": "Bakery",
    "refuse": 0,
    "water": 2.6,
    "energy": 509,
    "protein": 7.3,
    "fat": 25,
    "ash": 0.9,
    "fiber": 0.6,
    "carbohydrate": 63.6,
    "sodium": 130,
    "potassium": 135,
    "calcium": 62,
    "phosphorus": 168,
    "magnesium": null,
    "iron": 1.6,
    "zinc": 0.5,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.14,
    "riboflavin": 0.15,
    "servingSize": null
  },
  {
    "id": "f42",
    "name": "Biscuit, date fill",
    "category": "Bakery",
    "refuse": 0,
    "water": 4.5,
    "energy": 453,
    "protein": 6.4,
    "fat": 15.8,
    "ash": 1.1,
    "fiber": 1,
    "carbohydrate": 71.2,
    "sodium": 167,
    "potassium": 246,
    "calcium": 85,
    "phosphorus": null,
    "magnesium": 8,
    "iron": 2.85,
    "zinc": 0.53,
    "copper": 0.15,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.2,
    "riboflavin": 0.18,
    "servingSize": null
  },
  {
    "id": "f43",
    "name": "Biscuit, digestive",
    "category": "Bakery",
    "refuse": 0,
    "water": 3,
    "energy": 468,
    "protein": 7.5,
    "fat": 20,
    "ash": 1.9,
    "fiber": 3,
    "carbohydrate": 64.6,
    "sodium": 500,
    "potassium": 200,
    "calcium": 78,
    "phosphorus": null,
    "magnesium": 32,
    "iron": 3.2,
    "zinc": 0.7,
    "copper": 0.2,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.15,
    "riboflavin": 0.09,
    "servingSize": null
  },
  {
    "id": "f44",
    "name": "Biscuit, nice",
    "category": "Bakery",
    "refuse": 0,
    "water": 3.1,
    "energy": 454,
    "protein": 9.8,
    "fat": 15,
    "ash": 1.2,
    "fiber": 0.9,
    "carbohydrate": 70,
    "sodium": 162,
    "potassium": 137,
    "calcium": 120,
    "phosphorus": 155,
    "magnesium": null,
    "iron": 1.86,
    "zinc": 0.77,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.1,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f45",
    "name": "Biscuit, plain",
    "category": "Bakery",
    "refuse": 0,
    "water": 3.5,
    "energy": 438,
    "protein": 9.6,
    "fat": 11.8,
    "ash": 1,
    "fiber": 0.8,
    "carbohydrate": 73.3,
    "sodium": 129,
    "potassium": 140,
    "calcium": 109,
    "phosphorus": 173,
    "magnesium": null,
    "iron": 2,
    "zinc": 0.7,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.08,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f46",
    "name": "Biscuit, plain (Fe-fortified)",
    "category": "Bakery",
    "refuse": 0,
    "water": 3.3,
    "energy": 440,
    "protein": 9.4,
    "fat": 12,
    "ash": 1,
    "fiber": 0.8,
    "carbohydrate": 73.5,
    "sodium": 127,
    "potassium": 135,
    "calcium": 114,
    "phosphorus": 182,
    "magnesium": null,
    "iron": 4.6,
    "zinc": 0.8,
    "copper": 0.22,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": 0.7,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f47",
    "name": "Biscuit, salted",
    "category": "Bakery",
    "refuse": 0,
    "water": 3.9,
    "energy": 438,
    "protein": 10.1,
    "fat": 13.7,
    "ash": 3.4,
    "fiber": 0.3,
    "carbohydrate": 68.6,
    "sodium": 869,
    "potassium": 123,
    "calcium": 25,
    "phosphorus": 123,
    "magnesium": null,
    "iron": 1.41,
    "zinc": 1,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f48",
    "name": "Biscuit, salted (pretzel)",
    "category": "Bakery",
    "refuse": 0,
    "water": 2,
    "energy": 398,
    "protein": 9.8,
    "fat": 3,
    "ash": 2,
    "fiber": 0.3,
    "carbohydrate": 82.9,
    "sodium": 765,
    "potassium": 138,
    "calcium": 27,
    "phosphorus": 123,
    "magnesium": null,
    "iron": 1.5,
    "zinc": 1.1,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f49",
    "name": "Biscuit, wafers chocolate coat",
    "category": "Bakery",
    "refuse": 0,
    "water": 1.9,
    "energy": 535,
    "protein": 6.4,
    "fat": 29.8,
    "ash": 0.9,
    "fiber": 0.6,
    "carbohydrate": 60.4,
    "sodium": 180,
    "potassium": 160,
    "calcium": 103,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.1,
    "zinc": 0.6,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.1,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f50",
    "name": "Biscuit, wafers cream fill",
    "category": "Bakery",
    "refuse": 0,
    "water": 2,
    "energy": 515,
    "protein": 6.3,
    "fat": 25.4,
    "ash": 0.5,
    "fiber": 0.6,
    "carbohydrate": 65.2,
    "sodium": 93,
    "potassium": 150,
    "calcium": 45,
    "phosphorus": 83,
    "magnesium": null,
    "iron": 1.5,
    "zinc": 0.51,
    "copper": 0.16,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.09,
    "riboflavin": 0.08,
    "servingSize": null
  },
  {
    "id": "f51",
    "name": "Bread, Baiadi",
    "category": "Bakery",
    "refuse": 0,
    "water": 35.3,
    "energy": 254,
    "protein": 8.8,
    "fat": 1,
    "ash": 1.1,
    "fiber": 1.3,
    "carbohydrate": 52.5,
    "sodium": 338,
    "potassium": 236,
    "calcium": 42,
    "phosphorus": 134,
    "magnesium": 14,
    "iron": 2.89,
    "zinc": 1.75,
    "copper": 0.14,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.29,
    "riboflavin": 0.1,
    "servingSize": null
  },
  {
    "id": "f52",
    "name": "Bread, Baladi (wheat/corn)",
    "category": "Bakery",
    "refuse": 0,
    "water": 35.1,
    "energy": 256,
    "protein": 9,
    "fat": 1.3,
    "ash": 1.2,
    "fiber": 1.4,
    "carbohydrate": 52,
    "sodium": 369,
    "potassium": 208,
    "calcium": 47,
    "phosphorus": 197,
    "magnesium": 13,
    "iron": 3,
    "zinc": 2,
    "copper": 0.19,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.31,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f53",
    "name": "Bread, bran dry (sin)",
    "category": "Bakery",
    "refuse": 0,
    "water": 5,
    "energy": 369,
    "protein": 12.3,
    "fat": 2.1,
    "ash": 2.1,
    "fiber": 3.3,
    "carbohydrate": 75.2,
    "sodium": 449,
    "potassium": 430,
    "calcium": 42,
    "phosphorus": null,
    "magnesium": null,
    "iron": 4.5,
    "zinc": 2.9,
    "copper": 0.4,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f54",
    "name": "Bread, buns with sesame",
    "category": "Bakery",
    "refuse": 0,
    "water": 32.8,
    "energy": 273,
    "protein": 12.5,
    "fat": 3,
    "ash": 1.6,
    "fiber": 1.1,
    "carbohydrate": 49,
    "sodium": 330,
    "potassium": null,
    "calcium": 60,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.1,
    "zinc": 0.85,
    "copper": 0.52,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f55",
    "name": "Bread, cora/wheat (1:1)",
    "category": "Bakery",
    "refuse": 0,
    "water": 12,
    "energy": 355,
    "protein": 9.9,
    "fat": 2.5,
    "ash": 1.3,
    "fiber": 1.1,
    "carbohydrate": 73.2,
    "sodium": 452,
    "potassium": null,
    "calcium": 53,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.4,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.29,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f56",
    "name": "Bread, corn/wheat (1:2)",
    "category": "Bakery",
    "refuse": 0,
    "water": 10,
    "energy": 360,
    "protein": 10.1,
    "fat": 2.8,
    "ash": 1.5,
    "fiber": 1.9,
    "carbohydrate": 73.7,
    "sodium": 483,
    "potassium": 243,
    "calcium": 65,
    "phosphorus": 147,
    "magnesium": 19,
    "iron": 3.2,
    "zinc": 1.8,
    "copper": 0.18,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.28,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f57",
    "name": "Bread, corn/wheat (2:1)",
    "category": "Bakery",
    "refuse": 0,
    "water": 12.5,
    "energy": 351,
    "protein": 9.8,
    "fat": 2.9,
    "ash": 1.5,
    "fiber": 2,
    "carbohydrate": 71.3,
    "sodium": 474,
    "potassium": null,
    "calcium": 55,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.5,
    "zinc": null,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.28,
    "riboflavin": 0.11,
    "servingSize": null
  },
  {
    "id": "f58",
    "name": "Bread, crumb",
    "category": "Bakery",
    "refuse": 0,
    "water": 9.7,
    "energy": 356,
    "protein": 10.3,
    "fat": 2,
    "ash": 1.8,
    "fiber": 2,
    "carbohydrate": 74.2,
    "sodium": 501,
    "potassium": 200,
    "calcium": 76,
    "phosphorus": 115,
    "magnesium": 15,
    "iron": 2,
    "zinc": 1.53,
    "copper": 0.38,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.2,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f59",
    "name": "Bread, French",
    "category": "Bakery",
    "refuse": 0,
    "water": 28.8,
    "energy": 285,
    "protein": 8.7,
    "fat": 1,
    "ash": 0.9,
    "fiber": 0.3,
    "carbohydrate": 60.3,
    "sodium": 296,
    "potassium": 125,
    "calcium": 14,
    "phosphorus": 94,
    "magnesium": 18,
    "iron": 0.7,
    "zinc": 1.1,
    "copper": 0.12,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.1,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f60",
    "name": "Bread, Lebanese",
    "category": "Bakery",
    "refuse": 0,
    "water": 22,
    "energy": 311,
    "protein": 9.2,
    "fat": 0.7,
    "ash": 0.9,
    "fiber": 0.3,
    "carbohydrate": 66.9,
    "sodium": 307,
    "potassium": 104,
    "calcium": 15,
    "phosphorus": 85,
    "magnesium": 17,
    "iron": 0.72,
    "zinc": 1.2,
    "copper": 0.1,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.11,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f61",
    "name": "Bread, Lebanese (brown)",
    "category": "Bakery",
    "refuse": 0,
    "water": 25.8,
    "energy": 293,
    "protein": 9.4,
    "fat": 2.1,
    "ash": 1.1,
    "fiber": 2.5,
    "carbohydrate": 59.1,
    "sodium": 336,
    "potassium": 186,
    "calcium": 27,
    "phosphorus": null,
    "magnesium": null,
    "iron": 3.94,
    "zinc": 2,
    "copper": 0.44,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f62",
    "name": "Bread, Shami",
    "category": "Bakery",
    "refuse": 0,
    "water": 27.2,
    "energy": 292,
    "protein": 8.6,
    "fat": 0.9,
    "ash": 0.8,
    "fiber": 0.2,
    "carbohydrate": 62.3,
    "sodium": 350,
    "potassium": 120,
    "calcium": 16,
    "phosphorus": 82,
    "magnesium": 16,
    "iron": 0.7,
    "zinc": 1,
    "copper": 0.1,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.1,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f63",
    "name": "Bread, Shamsy",
    "category": "Bakery",
    "refuse": 0,
    "water": 35,
    "energy": 259,
    "protein": 9.2,
    "fat": 1.1,
    "ash": 1,
    "fiber": 0.6,
    "carbohydrate": 53.1,
    "sodium": 375,
    "potassium": 175,
    "calcium": 44,
    "phosphorus": 120,
    "magnesium": 14,
    "iron": 2.1,
    "zinc": 1.9,
    "copper": 0.1,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.27,
    "riboflavin": 0.11,
    "servingSize": null
  },
  {
    "id": "f64",
    "name": "Bread, sliced (brown toast)",
    "category": "Bakery",
    "refuse": 0,
    "water": 34.5,
    "energy": 257,
    "protein": 8.9,
    "fat": 1.9,
    "ash": 1.2,
    "fiber": 2.4,
    "carbohydrate": 51.1,
    "sodium": 405,
    "potassium": 201,
    "calcium": 49,
    "phosphorus": 185,
    "magnesium": 18,
    "iron": 2.86,
    "zinc": 1.1,
    "copper": 0.59,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.2,
    "riboflavin": 0.08,
    "servingSize": null
  },
  {
    "id": "f65",
    "name": "Bread, sliced (white toast)",
    "category": "Bakery",
    "refuse": 0,
    "water": 33,
    "energy": 270,
    "protein": 8.4,
    "fat": 1.5,
    "ash": 1,
    "fiber": 0.5,
    "carbohydrate": 55.6,
    "sodium": 400,
    "potassium": 152,
    "calcium": 30,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1,
    "zinc": 0.9,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.18,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f66",
    "name": "Bread, sticks with sesame",
    "category": "Bakery",
    "refuse": 0,
    "water": 6.1,
    "energy": 379,
    "protein": 11.7,
    "fat": 2.9,
    "ash": 1.5,
    "fiber": 1.3,
    "carbohydrate": 76.5,
    "sodium": 410,
    "potassium": 203,
    "calcium": 79,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.15,
    "zinc": 1.99,
    "copper": 0.42,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f67",
    "name": "Bread, sticks (Fayesh)",
    "category": "Bakery",
    "refuse": 0,
    "water": 8.1,
    "energy": 398,
    "protein": 12,
    "fat": 7.5,
    "ash": 0.9,
    "fiber": 0.9,
    "carbohydrate": 70.6,
    "sodium": 188,
    "potassium": 150,
    "calcium": 85,
    "phosphorus": null,
    "magnesium": null,
    "iron": 3.8,
    "zinc": 1.5,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f68" ,
    "name": "Cakes, cap",
    "category": "Bakery",
    "refuse": 0,
    "water": 26.8,
    "energy": 379,
    "protein": 7,
    "fat": 18.4,
    "ash": 1.2,
    "fiber": 0.3,
    "carbohydrate": 46.3,
    "sodium": 350,
    "potassium": 83,
    "calcium": 58,
    "phosphorus": 100,
    "magnesium": null,
    "iron": 0.5,
    "zinc": null,
    "copper": null,
    "vitaminA": 53,
    "vitaminC": 0,
    "thiamin": 0.02,
    "riboflavin": 0.08,
    "servingSize": null
  },
  {
    "id": "f69",
    "name": "Cakes, sponge",
    "category": "Bakery",
    "refuse": 0,
    "water": 32,
    "energy": 290,
    "protein": 7.5,
    "fat": 4.2,
    "ash": 0.5,
    "fiber": 0.2,
    "carbohydrate": 55.6,
    "sodium": 159,
    "potassium": 90,
    "calcium": 25,
    "phosphorus": 120,
    "magnesium": null,
    "iron": 1.19,
    "zinc": 1.04,
    "copper": null,
    "vitaminA": 93,
    "vitaminC": 0,
    "thiamin": 0.1,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f70",
    "name": "Flaky pastry",
    "category": "Bakery",
    "refuse": 0,
    "water": 22,
    "energy": 476,
    "protein": 5.5,
    "fat": 34,
    "ash": 1.1,
    "fiber": 0.3,
    "carbohydrate": 37.1,
    "sodium": 372,
    "potassium": 201,
    "calcium": 90,
    "phosphorus": 200,
    "magnesium": 14,
    "iron": 1.05,
    "zinc": 0.8,
    "copper": 0.2,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f71",
    "name": "Pie, date (school)",
    "category": "Bakery",
    "refuse": 0,
    "water": 21,
    "energy": 361,
    "protein": 9.5,
    "fat": 10.9,
    "ash": 1.2,
    "fiber": 1.3,
    "carbohydrate": 56.1,
    "sodium": 285,
    "potassium": 297,
    "calcium": 59,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.11,
    "zinc": 0.18,
    "copper": null,
    "vitaminA": 32,
    "vitaminC": 8,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f72",
    "name": "Pie, Fiteer, Meshaltet",
    "category": "Bakery",
    "refuse": 0,
    "water": 35.5,
    "energy": 402,
    "protein": 6.7,
    "fat": 30,
    "ash": 1.1,
    "fiber": 0.5,
    "carbohydrate": 26.2,
    "sodium": 300,
    "potassium": null,
    "calcium": 25,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.4,
    "zinc": 0.45,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f73",
    "name": "Pie, Fiteer, market",
    "category": "Bakery",
    "refuse": 0,
    "water": 28.6,
    "energy": 341,
    "protein": 7.6,
    "fat": 12,
    "ash": 0.7,
    "fiber": 0.4,
    "carbohydrate": 50.7,
    "sodium": 256,
    "potassium": null,
    "calcium": 15,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.6,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f74",
    "name": "Pie, Rucakwith meat",
    "category": "Bakery",
    "refuse": 0,
    "water": 51.4,
    "energy": 279,
    "protein": 8.5,
    "fat": 18.6,
    "ash": 1.1,
    "fiber": 1,
    "carbohydrate": 19.4,
    "sodium": 422,
    "potassium": 102,
    "calcium": 72,
    "phosphorus": 75,
    "magnesium": null,
    "iron": 1.2,
    "zinc": 0.64,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f75",
    "name": "Pizza, cheese topping",
    "category": "Bakery",
    "refuse": 0,
    "water": 48.5,
    "energy": 243,
    "protein": 12,
    "fat": 9.9,
    "ash": 2.1,
    "fiber": 1,
    "carbohydrate": 26.5,
    "sodium": 583,
    "potassium": 124,
    "calcium": 231,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.2,
    "zinc": 1.6,
    "copper": 0.1,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f76"   ,
    "name": "Pizza, meat/sausage topping",
    "category": "Bakery",
    "refuse": 0,
    "water": 41.5,
    "energy": 267,
    "protein": 13.4,
    "fat": 9.3,
    "ash": 2.4,
    "fiber": 1,
    "carbohydrate": 32.4,
    "sodium": 598,
    "potassium": 157,
    "calcium": 160,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.5,
    "zinc": 1.1,
    "copper": 0.12,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f77",
    "name": "Pizza, seafood topping",
    "category": "Bakery",
    "refuse": 0,
    "water": 44,
    "energy": 252,
    "protein": 10,
    "fat": 8,
    "ash": 2.1,
    "fiber": 0.8,
    "carbohydrate": 35.1,
    "sodium": 498,
    "potassium": 130,
    "calcium": 201,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.15,
    "zinc": 0.99,
    "copper": 0.19,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f78",
    "name": "Pizza, vegetarian",
    "category": "Bakery",
    "refuse": 0,
    "water": 47.8,
    "energy": 230,
    "protein": 5.9,
    "fat": 7.1,
    "ash": 2.6,
    "fiber": 1,
    "carbohydrate": 35.6,
    "sodium": 476,
    "potassium": 192,
    "calcium": 154,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.15,
    "zinc": 0.95,
    "copper": 0.8,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f79",
    "name": "Sandwiches, cheese white",
    "category": "Bakery",
    "refuse": 0,
    "water": 57,
    "energy": 180,
    "protein": 7.5,
    "fat": 4,
    "ash": 2,
    "fiber": 1,
    "carbohydrate": 28.5,
    "sodium": 496,
    "potassium": 190,
    "calcium": 196,
    "phosphorus": null,
    "magnesium": 17,
    "iron": 1.1,
    "zinc": 1.1,
    "copper": 0.04,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f80",
    "name": "Sandwiches, cheese yellow",
    "category": "Bakery",
    "refuse": 0,
    "water": 40,
    "energy": 262,
    "protein": 10.5,
    "fat": 7,
    "ash": 2,
    "fiber": 1.2,
    "carbohydrate": 39.3,
    "sodium": 684,
    "potassium": 183,
    "calcium": 202,
    "phosphorus": null,
    "magnesium": 20,
    "iron": 0.99,
    "zinc": 1.3,
    "copper": 0.01,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f81",
    "name": "Sandwiches, fried brain",
    "category": "Bakery",
    "refuse": 0,
    "water": 55,
    "energy": 232,
    "protein": 10.1,
    "fat": 11.5,
    "ash": 1.2,
    "fiber": 0.3,
    "carbohydrate": 21.9,
    "sodium": 403,
    "potassium": 286,
    "calcium": 63,
    "phosphorus": null,
    "magnesium": 21,
    "iron": 2.16,
    "zinc": 0.87,
    "copper": 0.21,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f82",
    "name": "Sandwiches, fried chicken",
    "category": "Bakery",
    "refuse": 0,
    "water": 45,
    "energy": 259,
    "protein": 9.6,
    "fat": 10,
    "ash": 1.7,
    "fiber": 1,
    "carbohydrate": 32.7,
    "sodium": 590,
    "potassium": 280,
    "calcium": 20,
    "phosphorus": null,
    "magnesium": 29,
    "iron": 1.93,
    "zinc": 1,
    "copper": 0.2,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f83"   ,
    "name": "Sandwiches, fried eggplant",
    "category": "Bakery",
    "refuse": 0,
    "water": 56,
    "energy": 123,
    "protein": 4,
    "fat": 5.5,
    "ash": 1.8,
    "fiber": 0.8,
    "carbohydrate": 31.9,
    "sodium": 615,
    "potassium": 386,
    "calcium": 27,
    "phosphorus": null,
    "magnesium": 12,
    "iron": 1.35,
    "zinc": 0.96,
    "copper": 0.06,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f84",
    "name": "Sandwiches, fried liver",
    "category": "Bakery",
    "refuse": 0,
    "water": 46.7,
    "energy": 238,
    "protein": 14.4,
    "fat": 6.8,
    "ash": 1.8,
    "fiber": 0.6,
    "carbohydrate": 29.7,
    "sodium": 599,
    "potassium": 270,
    "calcium": 53,
    "phosphorus": null,
    "magnesium": 50,
    "iron": 5.6,
    "zinc": 2.3,
    "copper": 1.87,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f85",
    "name": "Sandwiches, fried shrimp",
    "category": "Bakery",
    "refuse": 0,
    "water": 47.5,
    "energy": 252,
    "protein": 10.2,
    "fat": 10.5,
    "ash": 2,
    "fiber": 0.7,
    "carbohydrate": 29.1,
    "sodium": 525,
    "potassium": 285,
    "calcium": 54,
    "phosphorus": null,
    "magnesium": 27,
    "iron": 2.82,
    "zinc": 1.1,
    "copper": 0.19,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f86",
    "name": "Sandwiches, fried Taamia",
    "category": "Bakery",
    "refuse": 0,
    "water": 53,
    "energy": 205,
    "protein": 6.1,
    "fat": 5.8,
    "ash": 1.6,
    "fiber": 1.3,
    "carbohydrate": 32.2,
    "sodium": 587,
    "potassium": 273,
    "calcium": 33,
    "phosphorus": null,
    "magnesium": 30,
    "iron": 2.6,
    "zinc": 1.36,
    "copper": 0.14,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f87",
    "name": "Sandwiches, grilled chicken",
    "category": "Bakery",
    "refuse": 0,
    "water": 51.5,
    "energy": 223,
    "protein": 11.4,
    "fat": 8,
    "ash": 1.8,
    "fiber": 0.9,
    "carbohydrate": 26.4,
    "sodium": 522,
    "potassium": 316,
    "calcium": 28,
    "phosphorus": null,
    "magnesium": 34,
    "iron": 1.83,
    "zinc": 1.4,
    "copper": 0.15,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f88",
    "name": "Sandwiches, grilled Kofta",
    "category": "Bakery",
    "refuse": 0,
    "water": 50.9,
    "energy": 224,
    "protein": 12,
    "fat": 7.5,
    "ash": 1.8,
    "fiber": 0.6,
    "carbohydrate": 27.2,
    "sodium": 491,
    "potassium": 226,
    "calcium": 23,
    "phosphorus": null,
    "magnesium": 30,
    "iron": 2.86,
    "zinc": 1.03,
    "copper": 0.18,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f89",
    "name": "Sandwiches, hamburger",
    "category": "Bakery",
    "refuse": 0,
    "water": 52.8,
    "energy": 216,
    "protein": 8.8,
    "fat": 7.5,
    "ash": 1.7,
    "fiber": 0.8,
    "carbohydrate": 28.4,
    "sodium": 689,
    "potassium": 307,
    "calcium": 38,
    "phosphorus": null,
    "magnesium": 24,
    "iron": 2,
    "zinc": 0.63,
    "copper": 0.11,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f90",
    "name": "Sandwiches, hotdog",
    "category": "Bakery",
    "refuse": 0,
    "water": 50.2,
    "energy": 221,
    "protein": 8.8,
    "fat": 6.5,
    "ash": 2.1,
    "fiber": 0.5,
    "carbohydrate": 31.9,
    "sodium": 615,
    "potassium": 250,
    "calcium": 20,
    "phosphorus": null,
    "magnesium": 31,
    "iron": 1.58,
    "zinc": 1,
    "copper": 0.21,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f91",
    "name": "Sandwiches, shawerma meat",
    "category": "Bakery",
    "refuse": 0,
    "water": 51.1,
    "energy": 220,
    "protein": 10.7,
    "fat": 7,
    "ash": 1.9,
    "fiber": 0.7,
    "carbohydrate": 28.6,
    "sodium": 698,
    "potassium": 273,
    "calcium": 46,
    "phosphorus": null,
    "magnesium": 32,
    "iron": 2.9,
    "zinc": 1.91,
    "copper": 0.33,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f92",
    "name": "Sandwiches, slewed Foul",
    "category": "Bakery",
    "refuse": 0,
    "water": 56.2,
    "energy": 173,
    "protein": 5,
    "fat": 2.2,
    "ash": 1.8,
    "fiber": 1.5,
    "carbohydrate": 33.3,
    "sodium": 669,
    "potassium": 300,
    "calcium": 43,
    "phosphorus": null,
    "magnesium": 29,
    "iron": 2.05,
    "zinc": 1.24,
    "copper": 0.12,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f93",
    "name": "Chufa tuber (Hab-alaziz)",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 5.3,
    "energy": 413,
    "protein": 5.7,
    "fat": 16.2,
    "ash": 2.4,
    "fiber": 9.3,
    "carbohydrate": 61.1,
    "sodium": 51,
    "potassium": null,
    "calcium": 27,
    "phosphorus": null,
    "magnesium": null,
    "iron": 5.8,
    "zinc": 2.19,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f94",
    "name": "Chufa tuber, wet",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 35.7,
    "energy": 278,
    "protein": 4,
    "fat": 9.3,
    "ash": 1.5,
    "fiber": 5,
    "carbohydrate": 44.6,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": 3.5,
    "zinc": 1.32,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f95",
    "name": "Colocasia",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 23,
    "water": 79.1,
    "energy": 77,
    "protein": 2.1,
    "fat": 0.2,
    "ash": 1.2,
    "fiber": 0.8,
    "carbohydrate": 16.6,
    "sodium": 14,
    "potassium": 495,
    "calcium": 62,
    "phosphorus": 60,
    "magnesium": 13,
    "iron": 1,
    "zinc": 0.5,
    "copper": 0.08,
    "vitaminA": 11,
    "vitaminC": 6,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f96",
    "name": "Colocasia, cooked",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 81,
    "energy": 91,
    "protein": 2.5,
    "fat": 4.4,
    "ash": 1.5,
    "fiber": 0.3,
    "carbohydrate": 10.3,
    "sodium": 375,
    "potassium": 340,
    "calcium": 58,
    "phosphorus": 64,
    "magnesium": null,
    "iron": 1,
    "zinc": 0.32,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f97",
    "name": "Potatoes",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 80.5,
    "energy": 73,
    "protein": 1.6,
    "fat": 0.1,
    "ash": 0.8,
    "fiber": 0.6,
    "carbohydrate": 16.4,
    "sodium": 8,
    "potassium": 420,
    "calcium": 7,
    "phosphorus": 46,
    "magnesium": 16,
    "iron": 0.6,
    "zinc": 0.32,
    "copper": 0.15,
    "vitaminA": 2,
    "vitaminC": 18,
    "thiamin": 0.09,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f98",
    "name": "Potatoes, chips",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 1.5,
    "energy": 557,
    "protein": 5.5,
    "fat": 37.5,
    "ash": 4.5,
    "fiber": 1.6,
    "carbohydrate": 49.4,
    "sodium": 654,
    "potassium": 1100,
    "calcium": 37,
    "phosphorus": 143,
    "magnesium": 53,
    "iron": 1.7,
    "zinc": 0.9,
    "copper": 0.25,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.21,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f99",
    "name": "Potatoes, fries",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 45.2,
    "energy": 281,
    "protein": 4.1,
    "fat": 14.5,
    "ash": 1.8,
    "fiber": 1,
    "carbohydrate": 33.4,
    "sodium": 290,
    "potassium": 627,
    "calcium": 13,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.2,
    "zinc": 0.3,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f100",
    "name": "Potatoes, cooked with tomato",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 76.6,
    "energy": 104,
    "protein": 1.2,
    "fat": 3.5,
    "ash": 1.4,
    "fiber": 0.4,
    "carbohydrate": 16.9,
    "sodium": 369,
    "potassium": 394,
    "calcium": 10,
    "phosphorus": 72,
    "magnesium": null,
    "iron": 0.7,
    "zinc": 0.5,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f101",
    "name": "Potatoes, mashed with milk",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 81,
    "energy": 80,
    "protein": 2.2,
    "fat": 1.9,
    "ash": 0.9,
    "fiber": 0.4,
    "carbohydrate": 13.6,
    "sodium": 317,
    "potassium": 321,
    "calcium": 48,
    "phosphorus": 54,
    "magnesium": null,
    "iron": 0.65,
    "zinc": 0.56,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f102",
    "name": "Potatoes, mashed/meat/egg",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 76.7,
    "energy": 121,
    "protein": 6,
    "fat": 7,
    "ash": 1.4,
    "fiber": 0.5,
    "carbohydrate": 8.4,
    "sodium": 377,
    "potassium": 422,
    "calcium": 26,
    "phosphorus": 78,
    "magnesium": null,
    "iron": 0.5,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f103",
    "name": "Potatoes, meat stuff",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 0,
    "water": 76.2,
    "energy": 115,
    "protein": 3.1,
    "fat": 5,
    "ash": 0.6,
    "fiber": 0.6,
    "carbohydrate": 14.5,
    "sodium": 399,
    "potassium": 407,
    "calcium": 18,
    "phosphorus": 54,
    "magnesium": null,
    "iron": 1.8,
    "zinc": 0.77,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f104",
    "name": "Sweetpotatos",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 19,
    "water": 70.5,
    "energy": 112,
    "protein": 1.8,
    "fat": 0.3,
    "ash": 1,
    "fiber": 0.9,
    "carbohydrate": 25.5,
    "sodium": 13,
    "potassium": 398,
    "calcium": 39,
    "phosphorus": 40,
    "magnesium": 25,
    "iron": 0.8,
    "zinc": 0.3,
    "copper": 0.14,
    "vitaminA": 570,
    "vitaminC": 24,
    "thiamin": 0.11,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f104",
    "name": "Sweetpotatos",
    "category": "TUBERS AND TUBER - BASED FOODS",
    "refuse": 19,
    "water": 70.5,
    "energy": 112,
    "protein": 1.8,
    "fat": 0.3,
    "ash": 1,
    "fiber": 0.9,
    "carbohydrate": 25.5,
    "sodium": 13,
    "potassium": 398,
    "calcium": 39,
    "phosphorus": 40,
    "magnesium": 25,
    "iron": 0.8,
    "zinc": 0.3,
    "copper": 0.14,
    "vitaminA": 570,
    "vitaminC": 24,
    "thiamin": 0.11,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f105",
    "name": "Beans, broad",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 10.3,
    "energy": 326,
    "protein": 24.1,
    "fat": 1.5,
    "ash": 3.2,
    "fiber": 6.9,
    "carbohydrate": 54,
    "sodium": 35,
    "potassium": 724,
    "calcium": 85,
    "phosphorus": 386,
    "magnesium": 148,
    "iron": 5.8,
    "zinc": 3.14,
    "copper": 0.29,
    "vitaminA": 9,
    "vitaminC": 5,
    "thiamin": 0.48,
    "riboflavin": 0.28,
    "servingSize": null
  },
  {
    "id": "f106",
    "name": "Beans, broad (Bisara)",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 68,
    "energy": 130,
    "protein": 6,
    "fat": 4,
    "ash": 1.6,
    "fiber": 3,
    "carbohydrate": 17.4,
    "sodium": 290,
    "potassium": 187,
    "calcium": 25,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.2,
    "zinc": 0.2,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 8,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f107",
    "name": "Beans, broad, decorticated",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 10.4,
    "energy": 346,
    "protein": 25.9,
    "fat": 2,
    "ash": 2.7,
    "fiber": 2.8,
    "carbohydrate": 56.2,
    "sodium": 39,
    "potassium": 640,
    "calcium": 92,
    "phosphorus": 359,
    "magnesium": 126,
    "iron": 4.8,
    "zinc": 2.9,
    "copper": 0.18,
    "vitaminA": 13,
    "vitaminC": 2,
    "thiamin": 0.41,
    "riboflavin": 0.13,
    "servingSize": null
  },
  {
    "id": "f108",
    "name": "Beans, broad (Foul), canned",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 70,
    "energy": 132,
    "protein": 6.1,
    "fat": 5,
    "ash": 1.1,
    "fiber": 2.1,
    "carbohydrate": 15.7,
    "sodium": 333,
    "potassium": 275,
    "calcium": 44,
    "phosphorus": 175,
    "magnesium": 49,
    "iron": 2.7,
    "zinc": 0.9,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.12,
    "riboflavin": 0.08,
    "servingSize": null
  },
  {
    "id": "f109",
    "name": "Beans, broad (FoulMedames)",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 73.7,
    "energy": 98,
    "protein": 5.6,
    "fat": 0.7,
    "ash": 0.8,
    "fiber": 2,
    "carbohydrate": 17.2,
    "sodium": 24,
    "potassium": 218,
    "calcium": 37,
    "phosphorus": 183,
    "magnesium": null,
    "iron": 2.4,
    "zinc": 1,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f110",
    "name": "Beans, broad (Menabet)",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 61,
    "energy": 145,
    "protein": 10.4,
    "fat": 0.7,
    "ash": 0.8,
    "fiber": 2.9,
    "carbohydrate": 24.2,
    "sodium": 14,
    "potassium": 290,
    "calcium": 34,
    "phosphorus": 154,
    "magnesium": 59,
    "iron": 2.3,
    "zinc": 1.05,
    "copper": 0.12,
    "vitaminA": 5,
    "vitaminC": 3,
    "thiamin": 0.2,
    "riboflavin": 0.22,
    "servingSize": null
  },
  {
    "id": "f111",
    "name": "Beans, broad (Nabet) cooked",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 2,
    "water": 70,
    "energy": 114,
    "protein": 8.8,
    "fat": 0.6,
    "ash": 1.2,
    "fiber": 1,
    "carbohydrate": 18.4,
    "sodium": 330,
    "potassium": 218,
    "calcium": 37,
    "phosphorus": 166,
    "magnesium": null,
    "iron": 2.4,
    "zinc": 1,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f112",
    "name": "Beans, broad (Taamia), fried",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 32.7,
    "energy": 355,
    "protein": 10.9,
    "fat": 20.1,
    "ash": 2.2,
    "fiber": 1.5,
    "carbohydrate": 32.6,
    "sodium": 524,
    "potassium": 258,
    "calcium": 24,
    "phosphorus": 118,
    "magnesium": 41,
    "iron": 2.7,
    "zinc": 1.16,
    "copper": 0.4,
    "vitaminA": null,
    "vitaminC": 6,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f113",
    "name": "Beans, kidney (red)",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 11.6,
    "energy": 323,
    "protein": 23.1,
    "fat": 1.3,
    "ash": 3.4,
    "fiber": 5.8,
    "carbohydrate": 54.8,
    "sodium": 20,
    "potassium": 1309,
    "calcium": 120,
    "phosphorus": 374,
    "magnesium": 136,
    "iron": 6.5,
    "zinc": 2.01,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 3,
    "thiamin": 0.62,
    "riboflavin": 0.21,
    "servingSize": null
  },
  {
    "id": "f114",
    "name": "Beans, kidney (white)",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 10.8,
    "energy": 33,
    "protein": 22.1,
    "fat": 1.4,
    "ash": 3.5,
    "fiber": 4.2,
    "carbohydrate": 58,
    "sodium": 36,
    "potassium": 1270,
    "calcium": 148,
    "phosphorus": 415,
    "magnesium": 142,
    "iron": 7.2,
    "zinc": 3.4,
    "copper": 0.78,
    "vitaminA": 0,
    "vitaminC": 2,
    "thiamin": 0.58,
    "riboflavin": 0.26,
    "servingSize": null
  },
  {
    "id": "f115",
    "name": "Beans, kidney white, cooked",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 66,
    "energy": 134,
    "protein": 8.9,
    "fat": 2.4,
    "ash": 1.5,
    "fiber": 1.9,
    "carbohydrate": 19.3,
    "sodium": 324,
    "potassium": 504,
    "calcium": 57,
    "phosphorus": 139,
    "magnesium": null,
    "iron": 2.5,
    "zinc": 1.13,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f116",
    "name": "Beans, kidney white/meat",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 65.6,
    "energy": 152,
    "protein": 11.4,
    "fat": 5.6,
    "ash": 1.4,
    "fiber": 2,
    "carbohydrate": 14,
    "sodium": 371,
    "potassium": 566,
    "calcium": 59,
    "phosphorus": 167,
    "magnesium": null,
    "iron": 2.96,
    "zinc": 1.41,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f117",
    "name": "Beans, mung",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 7.2,
    "energy": 352,
    "protein": 21.4,
    "fat": 2,
    "ash": 3.3,
    "fiber": 4,
    "carbohydrate": 62.1,
    "sodium": 32,
    "potassium": 1354,
    "calcium": 168,
    "phosphorus": 293,
    "magnesium": 98,
    "iron": 5,
    "zinc": 2.9,
    "copper": null,
    "vitaminA": 12,
    "vitaminC": 4,
    "thiamin": 0.27,
    "riboflavin": 0.19,
    "servingSize": null
  },
  {
    "id": "f118",
    "name": "Chickpeas",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 8.6,
    "energy": 352,
    "protein": 20.3,
    "fat": 4.2,
    "ash": 3.3,
    "fiber": 5.3,
    "carbohydrate": 58.3,
    "sodium": 50,
    "potassium": 962,
    "calcium": 127,
    "phosphorus": 352,
    "magnesium": 122,
    "iron": 6.7,
    "zinc": 4.7,
    "copper": 1.25,
    "vitaminA": 10,
    "vitaminC": 4,
    "thiamin": 0.41,
    "riboflavin": 0.29,
    "servingSize": null
  },
  {
    "id": "f119",
    "name": "Chickpeas, (Homos Sham)",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 64.2,
    "energy": 138,
    "protein": 7,
    "fat": 1.6,
    "ash": 1.2,
    "fiber": 2.1,
    "carbohydrate": 23.9,
    "sodium": 405,
    "potassium": 380,
    "calcium": 48,
    "phosphorus": 151,
    "magnesium": 43,
    "iron": 2.5,
    "zinc": 1.82,
    "copper": 0.47,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.12,
    "riboflavin": 0.29,
    "servingSize": null
  },
  {
    "id": "f120",
    "name": "Chickpeas, yellow",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 10.5,
    "energy": 354,
    "protein": 19.6,
    "fat": 4.4,
    "ash": 3.1,
    "fiber": 3.4,
    "carbohydrate": 59,
    "sodium": 34,
    "potassium": 855,
    "calcium": 155,
    "phosphorus": 430,
    "magnesium": 130,
    "iron": 5.8,
    "zinc": 3.4,
    "copper": 1.3,
    "vitaminA": 19,
    "vitaminC": 0,
    "thiamin": 0.48,
    "riboflavin": 0.21,
    "servingSize": null
  },
  {
    "id": "f121",
    "name": "Cowpeas",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 11.3,
    "energy": 330,
    "protein": 23,
    "fat": 1.2,
    "ash": 3.1,
    "fiber": 4.7,
    "carbohydrate": 56.7,
    "sodium": 30,
    "potassium": 1217,
    "calcium": 104,
    "phosphorus": 347,
    "magnesium": 136,
    "iron": 6.8,
    "zinc": 3.22,
    "copper": 0.46,
    "vitaminA": 3,
    "vitaminC": 2,
    "thiamin": 0.75,
    "riboflavin": 0.21,
    "servingSize": null
  },
  {
    "id": "f122",
    "name": "Cowpeas, cooked",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 68,
    "energy": 131,
    "protein": 8.5,
    "fat": 3.5,
    "ash": 1.6,
    "fiber": 2,
    "carbohydrate": 16.4,
    "sodium": 343,
    "potassium": 579,
    "calcium": 37,
    "phosphorus": 96,
    "magnesium": 34,
    "iron": 2.2,
    "zinc": 1.07,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f123",
    "name": "Cowpeas, cooked with meat",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 66,
    "energy": 146,
    "protein": 11,
    "fat": 5.5,
    "ash": 2,
    "fiber": 2.5,
    "carbohydrate": 13,
    "sodium": 317,
    "potassium": 456,
    "calcium": 36,
    "phosphorus": 142,
    "magnesium": null,
    "iron": 6.5,
    "zinc": 1.54,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f124",
    "name": "Fenugreek",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 9.4,
    "energy": 356,
    "protein": 25.6,
    "fat": 6.7,
    "ash": 3.5,
    "fiber": 6.5,
    "carbohydrate": 48.3,
    "sodium": 93,
    "potassium": 966,
    "calcium": 194,
    "phosphorus": 365,
    "magnesium": 115,
    "iron": 14,
    "zinc": 4.6,
    "copper": 0.4,
    "vitaminA": 132,
    "vitaminC": 4,
    "thiamin": 0.27,
    "riboflavin": 0.52,
    "servingSize": null
  },
  {
    "id": "f125",
    "name": "Lentils, un-peeled",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 10,
    "energy": 340,
    "protein": 22.4,
    "fat": 1.1,
    "ash": 2.7,
    "fiber": 3.8,
    "carbohydrate": 60,
    "sodium": 30,
    "potassium": 725,
    "calcium": 48,
    "phosphorus": 327,
    "magnesium": 86,
    "iron": 7.3,
    "zinc": 4.2,
    "copper": 0.25,
    "vitaminA": 12,
    "vitaminC": 0,
    "thiamin": 0.4,
    "riboflavin": 0.22,
    "servingSize": null
  },
  {
    "id": "f126",
    "name": "Lentils, un-peeled, cooked",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 65,
    "energy": 151,
    "protein": 5.8,
    "fat": 5.6,
    "ash": 2,
    "fiber": 2.3,
    "carbohydrate": 19.3,
    "sodium": 440,
    "potassium": 372,
    "calcium": 43,
    "phosphorus": 164,
    "magnesium": null,
    "iron": 3.1,
    "zinc": 1.5,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f127"    ,
    "name": "Lentils, peeled (yellow)",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 11.5,
    "energy": 340,
    "protein": 22.9,
    "fat": 0.7,
    "ash": 2.3,
    "fiber": 2.1,
    "carbohydrate": 60.5,
    "sodium": 41,
    "potassium": 765,
    "calcium": 50,
    "phosphorus": 330,
    "magnesium": 82,
    "iron": 6.8,
    "zinc": 3.2,
    "copper": 0.35,
    "vitaminA": 15,
    "vitaminC": 0,
    "thiamin": 0.41,
    "riboflavin": 0.18,
    "servingSize": null
  },
  {
    "id": "f128",
    "name": "Lentils, peeled/rice cooked",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 68.9,
    "energy": 140,
    "protein": 6.2,
    "fat": 5.2,
    "ash": 1.4,
    "fiber": 1.2,
    "carbohydrate": 17.1,
    "sodium": 312,
    "potassium": 384,
    "calcium": 12,
    "phosphorus": 57,
    "magnesium": null,
    "iron": 1.1,
    "zinc": 0.8,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f129",
    "name": "Lentils, peeled soup",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 82.5,
    "energy": 68,
    "protein": 4.8,
    "fat": 1.5,
    "ash": 1.7,
    "fiber": 0.7,
    "carbohydrate": 8.8,
    "sodium": 318,
    "potassium": 245,
    "calcium": 26,
    "phosphorus": 76,
    "magnesium": null,
    "iron": 1.8,
    "zinc": 0.6,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f130",
    "name": "Lupines",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 8,
    "energy": 355,
    "protein": 38,
    "fat": 9,
    "ash": 3.5,
    "fiber": 11,
    "carbohydrate": 30.5,
    "sodium": 94,
    "potassium": 605,
    "calcium": 279,
    "phosphorus": 490,
    "magnesium": 189,
    "iron": 5.82,
    "zinc": 2.87,
    "copper": 0.87,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.41,
    "riboflavin": 0.48,
    "servingSize": null
  },
  {
    "id": "f131",
    "name": "Lupines (Termis)",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 3,
    "water": 70.7,
    "energy": 111,
    "protein": 9.6,
    "fat": 2.7,
    "ash": 2,
    "fiber": 3,
    "carbohydrate": 12,
    "sodium": 580,
    "potassium": 200,
    "calcium": 89,
    "phosphorus": 128,
    "magnesium": 53,
    "iron": 1.58,
    "zinc": 0.8,
    "copper": 0.24,
    "vitaminA": "T",
    "vitaminC": 5,
    "thiamin": 0.15,
    "riboflavin": 0.1,
    "servingSize": null
  },
  {
    "id": "f132",
    "name": "Peas",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 10,
    "energy": 345,
    "protein": 22.1,
    "fat": 2.1,
    "ash": 2.5,
    "fiber": 3.9,
    "carbohydrate": 59.4,
    "sodium": 32,
    "potassium": 928,
    "calcium": 82,
    "phosphorus": 352,
    "magnesium": 125,
    "iron": 5.8,
    "zinc": 3.15,
    "copper": 0.75,
    "vitaminA": 34,
    "vitaminC": 4,
    "thiamin": 0.84,
    "riboflavin": 0.25,
    "servingSize": null
  },
  {
    "id": "f133",
    "name": "Soybeans",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 9.3,
    "energy": 426,
    "protein": 31.7,
    "fat": 19.9,
    "ash": 4.7,
    "fiber": 4.3,
    "carbohydrate": 30.1,
    "sodium": 21,
    "potassium": 1618,
    "calcium": 305,
    "phosphorus": 563,
    "magnesium": 258,
    "iron": 7.5,
    "zinc": 2,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.8,
    "riboflavin": 0.32,
    "servingSize": null
  },
  {
    "id": "f134",
    "name": "Soybeans, flour",
    "category": "LEGUMES AND LEGUME - BASED FOODS",
    "refuse": 0,
    "water": 7.5,
    "energy": 362,
    "protein": 44.5,
    "fat": 3.9,
    "ash": 4.2,
    "fiber": 2.8,
    "carbohydrate": 37.1,
    "sodium": 28,
    "potassium": 1870,
    "calcium": 268,
    "phosphorus": 424,
    "magnesium": 303,
    "iron": 8.33,
    "zinc": 1.2,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.83,
    "riboflavin": 0.29,
    "servingSize": null
  },
  {
    "id": "f135",
    "name": "Almonds",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 0,
    "water": 4.6,
    "energy": 640,
    "protein": 17.6,
    "fat": 55.8,
    "ash": 2.7,
    "fiber": 2.5,
    "carbohydrate": 16.8,
    "sodium": 7,
    "potassium": 793,
    "calcium": 215,
    "phosphorus": 485,
    "magnesium": 230,
    "iron": 4.5,
    "zinc": 3.2,
    "copper": 0.13,
    "vitaminA": 21,
    "vitaminC": 0,
    "thiamin": 0.26,
    "riboflavin": 0.72,
    "servingSize": null
  },
  {
    "id": "f136",
    "name": "Coconuts, dry",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 0,
    "water": 3,
    "energy": 689,
    "protein": 6,
    "fat": 65,
    "ash": 2,
    "fiber": 4,
    "carbohydrate": 20,
    "sodium": 23,
    "potassium": 560,
    "calcium": 20,
    "phosphorus": 176,
    "magnesium": 44,
    "iron": 2.9,
    "zinc": 1.5,
    "copper": 0.4,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.07,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f137",
    "name": "Hazelnuts",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 0,
    "water": 5,
    "energy": 633,
    "protein": 18.6,
    "fat": 55.7,
    "ash": 2.6,
    "fiber": 3.7,
    "carbohydrate": 14.4,
    "sodium": 5,
    "potassium": 600,
    "calcium": 180,
    "phosphorus": 300,
    "magnesium": 130,
    "iron": 3.5,
    "zinc": 2,
    "copper": 1.1,
    "vitaminA": 24,
    "vitaminC": 0,
    "thiamin": 0.45,
    "riboflavin": 0.25,
    "servingSize": null
  },
  {
    "id": "f138",
    "name": "Peanuts",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 30,
    "water": 4.6,
    "energy": 585,
    "protein": 26.4,
    "fat": 44.9,
    "ash": 2.4,
    "fiber": 2.9,
    "carbohydrate": 18.8,
    "sodium": 4,
    "potassium": 670,
    "calcium": 55,
    "phosphorus": 380,
    "magnesium": 180,
    "iron": 2.5,
    "zinc": 2.6,
    "copper": 0.29,
    "vitaminA": 84,
    "vitaminC": 0,
    "thiamin": 0.85,
    "riboflavin": 0.14,
    "servingSize": null
  },
  {
    "id": "f139",
    "name": "Pinenuts",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 0,
    "water": 4.6,
    "energy": 614,
    "protein": 30.2,
    "fat": 50.2,
    "ash": 3.7,
    "fiber": 1,
    "carbohydrate": 10.3,
    "sodium": 53,
    "potassium": 888,
    "calcium": 15,
    "phosphorus": 500,
    "magnesium": 240,
    "iron": 4.9,
    "zinc": 2.4,
    "copper": 1.4,
    "vitaminA": 5,
    "vitaminC": 0,
    "thiamin": 1,
    "riboflavin": 0.22,
    "servingSize": null
  },
  {
    "id": "f140",
    "name": "Pistachio nuts",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 44,
    "water": 5.5,
    "energy": 631,
    "protein": 20.9,
    "fat": 54.1,
    "ash": 2.6,
    "fiber": 1.8,
    "carbohydrate": 15.1,
    "sodium": 15,
    "potassium": 850,
    "calcium": 122,
    "phosphorus": 503,
    "magnesium": 150,
    "iron": 6.9,
    "zinc": 1.9,
    "copper": 1.2,
    "vitaminA": 54,
    "vitaminC": 0,
    "thiamin": 0.73,
    "riboflavin": 0.19,
    "servingSize": null
  },
  {
    "id": "f141",
    "name": "Pumpkin seeds/salted",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 23,
    "water": 4.3,
    "energy": 591,
    "protein": 23.9,
    "fat": 48,
    "ash": 3.5,
    "fiber": 4.5,
    "carbohydrate": 15.8,
    "sodium": 695,
    "potassium": 790,
    "calcium": 55,
    "phosphorus": 1027,
    "magnesium": null,
    "iron": 9.5,
    "zinc": 5.5,
    "copper": null,
    "vitaminA": 21,
    "vitaminC": 0,
    "thiamin": 0.3,
    "riboflavin": 0.39,
    "servingSize": null
  },
  {
    "id": "f142",
    "name": "Sesame seeds",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 0,
    "water": 4.6,
    "energy": 600,
    "protein": 19,
    "fat": 51.8,
    "ash": 4.6,
    "fiber": 5.5,
    "carbohydrate": 14.5,
    "sodium": 65,
    "potassium": 506,
    "calcium": 887,
    "phosphorus": 515,
    "magnesium": 174,
    "iron": 10.2,
    "zinc": 8.6,
    "copper": 1.23,
    "vitaminA": 9,
    "vitaminC": 0,
    "thiamin": 0.1,
    "riboflavin": 0.32,
    "servingSize": null
  },
  {
    "id": "f143",
    "name": "Sesame butter (Tehina)",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 0,
    "water": 3,
    "energy": 660,
    "protein": 20,
    "fat": 60,
    "ash": 3,
    "fiber": 4,
    "carbohydrate": 10,
    "sodium": 84,
    "potassium": 205,
    "calcium": 340,
    "phosphorus": 665,
    "magnesium": 44,
    "iron": 6.1,
    "zinc": 5.5,
    "copper": 0.8,
    "vitaminA": 11,
    "vitaminC": 0,
    "thiamin": 0.94,
    "riboflavin": 0.26,
    "servingSize": null
  },
  {
    "id": "f144",
    "name": "Walnuts",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 0,
    "water": 3.4,
    "energy": 695,
    "protein": 14.7,
    "fat": 64.9,
    "ash": 1.8,
    "fiber": 2.2,
    "carbohydrate": 13,
    "sodium": 5,
    "potassium": 600,
    "calcium": 75,
    "phosphorus": 390,
    "magnesium": 120,
    "iron": 2,
    "zinc": 2.8,
    "copper": 0.3,
    "vitaminA": 7,
    "vitaminC": 5,
    "thiamin": 0.35,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f145",
    "name": "Watermelon seeds/salted",
    "category": "NUTS, SEEDS AND PRODUCTS",
    "refuse": 45,
    "water": 5.5,
    "energy": 592,
    "protein": 34.7,
    "fat": 48,
    "ash": 3.5,
    "fiber": 3,
    "carbohydrate": 5.3,
    "sodium": 505,
    "potassium": null,
    "calcium": 45,
    "phosphorus": 896,
    "magnesium": null,
    "iron": 8,
    "zinc": 0.3,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": 0.22,
    "riboflavin": 0.16,
    "servingSize": null
  },
  {
    "id": "f146",
    "name": "Artichokes",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 52,
    "water": 85.8,
    "energy": 45,
    "protein": 3.5,
    "fat": 0.2,
    "ash": 1.1,
    "fiber": 2,
    "carbohydrate": 7.4,
    "sodium": 32,
    "potassium": 350,
    "calcium": 55,
    "phosphorus": 96,
    "magnesium": 30,
    "iron": 0.9,
    "zinc": 0.5,
    "copper": 0.03,
    "vitaminA": 15,
    "vitaminC": 11,
    "thiamin": 0.07,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f147",
    "name": "Artichokes, salad",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 79.7,
    "energy": 89,
    "protein": 2.7,
    "fat": 4.7,
    "ash": 2.5,
    "fiber": 1.4,
    "carbohydrate": 9,
    "sodium": 314,
    "potassium": 380,
    "calcium": 35,
    "phosphorus": 90,
    "magnesium": null,
    "iron": 0.86,
    "zinc": 0.41,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f148",
    "name": "Beans, broad (green)",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 68,
    "water": 77.3,
    "energy": 79,
    "protein": 5.7,
    "fat": 0.4,
    "ash": 1,
    "fiber": 2.4,
    "carbohydrate": 13.2,
    "sodium": 6,
    "potassium": 250,
    "calcium": 31,
    "phosphorus": 110,
    "magnesium": 30,
    "iron": 1.1,
    "zinc": 0.8,
    "copper": 0.3,
    "vitaminA": 34,
    "vitaminC": 27,
    "thiamin": 0.22,
    "riboflavin": 0.15,
    "servingSize": null
  },
  {
    "id": "f149",
    "name": "Beans, green",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 12,
    "water": 91,
    "energy": 30,
    "protein": 1.8,
    "fat": 0.2,
    "ash": 0.7,
    "fiber": 1.1,
    "carbohydrate": 5.2,
    "sodium": 8,
    "potassium": 274,
    "calcium": 35,
    "phosphorus": 45,
    "magnesium": 12,
    "iron": 1.75,
    "zinc": 0.51,
    "copper": 0.24,
    "vitaminA": 57,
    "vitaminC": 18,
    "thiamin": 0.09,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f150",
    "name": "Beans, green, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 86.2,
    "energy": 61,
    "protein": 1.9,
    "fat": 3.2,
    "ash": 1.4,
    "fiber": 1.1,
    "carbohydrate": 6.2,
    "sodium": 390,
    "potassium": 346,
    "calcium": 34,
    "phosphorus": 33,
    "magnesium": null,
    "iron": 1.4,
    "zinc": 0.39,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f151",
    "name": "Beetroots",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 15,
    "water": 86.8,
    "energy": 45,
    "protein": 1.5,
    "fat": 0.1,
    "ash": 1,
    "fiber": 1.1,
    "carbohydrate": 9.5,
    "sodium": 70,
    "potassium": 320,
    "calcium": 22,
    "phosphorus": 35,
    "magnesium": 25,
    "iron": 0.9,
    "zinc": 0.63,
    "copper": 0.07,
    "vitaminA": 5,
    "vitaminC": 8,
    "thiamin": 0.02,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f152",
    "name": "Broccoli",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 20,
    "water": 88.2,
    "energy": 38,
    "protein": 3.5,
    "fat": 0.3,
    "ash": 0.1,
    "fiber": 1.5,
    "carbohydrate": 5.4,
    "sodium": 35,
    "potassium": 368,
    "calcium": 57,
    "phosphorus": 80,
    "magnesium": 20,
    "iron": 1.23,
    "zinc": 0.84,
    "copper": 0.09,
    "vitaminA": 150,
    "vitaminC": 95,
    "thiamin": 0.1,
    "riboflavin": 0.24,
    "servingSize": null
  },
  {
    "id": "f153",
    "name": "Brussels sprouts",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 10,
    "water": 85.6,
    "energy": 50,
    "protein": 4.6,
    "fat": 0.4,
    "ash": 1.2,
    "fiber": 1.3,
    "carbohydrate": 6.9,
    "sodium": 12,
    "potassium": 440,
    "calcium": 43,
    "phosphorus": 85,
    "magnesium": 20,
    "iron": 0.95,
    "zinc": 0.79,
    "copper": 0.2,
    "vitaminA": 75,
    "vitaminC": 65,
    "thiamin": 0.11,
    "riboflavin": 0.19,
    "servingSize": null
  },
  {
    "id": "f154",
    "name": "Cabbage, red",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 10,
    "water": 90.2,
    "energy": 31,
    "protein": 1.6,
    "fat": 0.3,
    "ash": 1,
    "fiber": 1.5,
    "carbohydrate": 5.4,
    "sodium": 31,
    "potassium": 298,
    "calcium": 55,
    "phosphorus": null,
    "magnesium": 3,
    "iron": 0.74,
    "zinc": 0.36,
    "copper": 0.05,
    "vitaminA": 8,
    "vitaminC": 54,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f155",
    "name": "Cabbage, white",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 25,
    "water": 91.6,
    "energy": 27,
    "protein": 13,
    "fat": 0.2,
    "ash": 0.7,
    "fiber": 1.2,
    "carbohydrate": 5,
    "sodium": 35,
    "potassium": 250,
    "calcium": 40,
    "phosphorus": 32,
    "magnesium": 15,
    "iron": 0.58,
    "zinc": 0.3,
    "copper": 0.04,
    "vitaminA": 5,
    "vitaminC": 35,
    "thiamin": 0.07,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f156",
    "name": "Cabbage, rice stuff",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 71,
    "energy": 127,
    "protein": 3.5,
    "fat": 4,
    "ash": 1.1,
    "fiber": 1.2,
    "carbohydrate": 19.2,
    "sodium": 325,
    "potassium": 250,
    "calcium": 42,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.5,
    "zinc": 0.5,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f157",
    "name": "Cabbage, rice/meat stuff",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 70,
    "energy": 140,
    "protein": 5,
    "fat": 6.2,
    "ash": 1.5,
    "fiber": 1.2,
    "carbohydrate": 16.1,
    "sodium": 318,
    "potassium": 219,
    "calcium": 48,
    "phosphorus": 62,
    "magnesium": null,
    "iron": 0.8,
    "zinc": 0.65,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f158",
    "name": "Carrots",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 2,
    "water": 89.4,
    "energy": 36,
    "protein": 1.2,
    "fat": 0.2,
    "ash": 0.8,
    "fiber": 1,
    "carbohydrate": 7.4,
    "sodium": 62,
    "potassium": 300,
    "calcium": 40,
    "phosphorus": 42,
    "magnesium": 18,
    "iron": 0.58,
    "zinc": 0.42,
    "copper": 0.06,
    "vitaminA": 1871,
    "vitaminC": 8,
    "thiamin": 0.06,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f159",
    "name": "Cauliflower",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 28,
    "water": 91.5,
    "energy": 28,
    "protein": 2.3,
    "fat": 0.3,
    "ash": 0.8,
    "fiber": 1,
    "carbohydrate": 4.1,
    "sodium": 15,
    "potassium": 3.4,
    "calcium": 30,
    "phosphorus": 56,
    "magnesium": 20,
    "iron": 0.5,
    "zinc": 0.3,
    "copper": 0.19,
    "vitaminA": 4,
    "vitaminC": 58,
    "thiamin": 0.05,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f160",
    "name": "Cauliflower, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 89,
    "energy": 49,
    "protein": 2,
    "fat": 2.1,
    "ash": 0.7,
    "fiber": 0.8,
    "carbohydrate": 5.4,
    "sodium": 420,
    "potassium": 269,
    "calcium": 27,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.5,
    "zinc": 0.4,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f161",
    "name": "Cauliflorver/meat, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 87,
    "energy": 62,
    "protein": 3.7,
    "fat": 3.1,
    "ash": 0.6,
    "fiber": 0.7,
    "carbohydrate": 4.9,
    "sodium": 405,
    "potassium": 313,
    "calcium": 32,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.77,
    "zinc": 0.5,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f162",
    "name": "Celery",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 33,
    "water": 85.2,
    "energy": 52,
    "protein": 0.9,
    "fat": 0.2,
    "ash": 1.3,
    "fiber": 0.7,
    "carbohydrate": 11.7,
    "sodium": 74,
    "potassium": 315,
    "calcium": 40,
    "phosphorus": 48,
    "magnesium": 25,
    "iron": 0.95,
    "zinc": 0.21,
    "copper": 0.02,
    "vitaminA": 15,
    "vitaminC": 7,
    "thiamin": 0.04,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f163",
    "name": "Chard Swiss",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 17,
    "water": 91.2,
    "energy": 27,
    "protein": 2.3,
    "fat": 0.4,
    "ash": 1.7,
    "fiber": 0.9,
    "carbohydrate": 3.5,
    "sodium": 164,
    "potassium": 505,
    "calcium": 85,
    "phosphorus": 32,
    "magnesium": 57,
    "iron": 3.05,
    "zinc": 2,
    "copper": 0.18,
    "vitaminA": 666,
    "vitaminC": 18,
    "thiamin": 0.05,
    "riboflavin": 0.14,
    "servingSize": null
  },
  {
    "id": "f164",
    "name": "Chickpeas green",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 77,
    "water": 64,
    "energy": 139,
    "protein": 4.7,
    "fat": 1.3,
    "ash": 1.3,
    "fiber": 1.7,
    "carbohydrate": 27,
    "sodium": null,
    "potassium": null,
    "calcium": 75,
    "phosphorus": null,
    "magnesium": null,
    "iron": 3.5,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 40,
    "thiamin": 0.08,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f165",
    "name": "Chicory",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 18,
    "water": 91.7,
    "energy": 27,
    "protein": 1.8,
    "fat": 0.4,
    "ash": 1.2,
    "fiber": 0.9,
    "carbohydrate": 4,
    "sodium": null,
    "potassium": null,
    "calcium": 140,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.8,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 35,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f166",
    "name": "Corander",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 12,
    "water": 88.5,
    "energy": 34,
    "protein": 3.7,
    "fat": 0.6,
    "ash": 2,
    "fiber": 1.7,
    "carbohydrate": 3.5,
    "sodium": 54,
    "potassium": 366,
    "calcium": 182,
    "phosphorus": 64,
    "magnesium": 9,
    "iron": 3.3,
    "zinc": 3.3,
    "copper": 0.07,
    "vitaminA": 470,
    "vitaminC": 75,
    "thiamin": 0.13,
    "riboflavin": 0.31,
    "servingSize": null
  },
  {
    "id": "f167",
    "name": "Cowpeas",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 8,
    "water": 85.4,
    "energy": 50,
    "protein": 3.5,
    "fat": 0.3,
    "ash": 0.8,
    "fiber": 1.6,
    "carbohydrate": 8.4,
    "sodium": 5,
    "potassium": 295,
    "calcium": 52,
    "phosphorus": 86,
    "magnesium": 28,
    "iron": 1.4,
    "zinc": 0.7,
    "copper": 0.25,
    "vitaminA": 45,
    "vitaminC": 25,
    "thiamin": 1.05,
    "riboflavin": 0.21,
    "servingSize": null
  },
  {
    "id": "f168",
    "name": "Cucumber",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 10,
    "water": 95,
    "energy": 16,
    "protein": 0.7,
    "fat": 0.1,
    "ash": 0.5,
    "fiber": 0.6,
    "carbohydrate": 3.1,
    "sodium": 5,
    "potassium": 130,
    "calcium": 18,
    "phosphorus": 31,
    "magnesium": 8,
    "iron": 0.6,
    "zinc": 0.18,
    "copper": 0.07,
    "vitaminA": 4,
    "vitaminC": 9,
    "thiamin": 0.04,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f169",
    "name": "Cucumber, pickled",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 90,
    "energy": 22,
    "protein": 1.1,
    "fat": 0,
    "ash": 4,
    "fiber": 0.4,
    "carbohydrate": 4.5,
    "sodium": 991,
    "potassium": 185,
    "calcium": 21,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.8,
    "zinc": 0.21,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f170",
    "name": "Dill",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 10,
    "water": 88.9,
    "energy": 36,
    "protein": 2.9,
    "fat": 0.5,
    "ash": 1.5,
    "fiber": 1.2,
    "carbohydrate": 5,
    "sodium": 60,
    "potassium": 305,
    "calcium": 150,
    "phosphorus": 41,
    "magnesium": 20,
    "iron": 2.1,
    "zinc": 3.6,
    "copper": 0.07,
    "vitaminA": 300,
    "vitaminC": 34,
    "thiamin": 0.12,
    "riboflavin": 0.38,
    "servingSize": null
  },
  {
    "id": "f171",
    "name": "Eggplant, black or white",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 16,
    "water": 91.3,
    "energy": 28,
    "protein": 1.7,
    "fat": 0.2,
    "ash": 0.7,
    "fiber": 1.2,
    "carbohydrate": 4.9,
    "sodium": 7,
    "potassium": 272,
    "calcium": 15,
    "phosphorus": 25,
    "magnesium": 13,
    "iron": 0.5,
    "zinc": 0.33,
    "copper": 0.07,
    "vitaminA": 8,
    "vitaminC": 6,
    "thiamin": 0.04,
    "riboflavin": 0.03,
    "servingSize": null
  },{
    "id": "f172",
    "name": "Eggplant, round",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 15,
    "water": 91,
    "energy": 27,
    "protein": 1.8,
    "fat": 0.1,
    "ash": 0.9,
    "fiber": 1.4,
    "carbohydrate": 4.8,
    "sodium": 8,
    "potassium": 270,
    "calcium": 17,
    "phosphorus": 24,
    "magnesium": 12,
    "iron": 0.38,
    "zinc": 0.36,
    "copper": 0.07,
    "vitaminA": 6,
    "vitaminC": 8,
    "thiamin": 0.03,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f173",
    "name": "Eggplant, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 87,
    "energy": 73,
    "protein": 1.6,
    "fat": 5.3,
    "ash": 0.6,
    "fiber": 0.8,
    "carbohydrate": 4.7,
    "sodium": 396,
    "potassium": 229,
    "calcium": 13,
    "phosphorus": 20,
    "magnesium": null,
    "iron": 0.34,
    "zinc": 0.25,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f174",
    "name": "Eggplant, cooked with meat",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 85,
    "energy": 84,
    "protein": 3.6,
    "fat": 6,
    "ash": 0.7,
    "fiber": 0.7,
    "carbohydrate": 4,
    "sodium": 364,
    "potassium": 264,
    "calcium": 21,
    "phosphorus": 33,
    "magnesium": null,
    "iron": 0.72,
    "zinc": 0.5,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f175",
    "name": "Eggplant, fried",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 75.1,
    "energy": 175,
    "protein": 1.5,
    "fat": 17,
    "ash": 1.5,
    "fiber": 1,
    "carbohydrate": 3.9,
    "sodium": 4.9,
    "potassium": 257,
    "calcium": 15,
    "phosphorus": 22,
    "magnesium": null,
    "iron": 0.33,
    "zinc": 0.29,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f176",
    "name": "Eggplant, rice stuff",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 71,
    "energy": 126,
    "protein": 3,
    "fat": 4,
    "ash": 1.5,
    "fiber": 1,
    "carbohydrate": 19.5,
    "sodium": 390,
    "potassium": 238,
    "calcium": 19,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.77,
    "zinc": 0.52,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f177",
    "name": "Fennel",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 5,
    "water": 88.6,
    "energy": 37,
    "protein": 2.6,
    "fat": 0.5,
    "ash": 1.4,
    "fiber": 1.5,
    "carbohydrate": 5.4,
    "sodium": 52,
    "potassium": 226,
    "calcium": 52,
    "phosphorus": 48,
    "magnesium": 26,
    "iron": 1.34,
    "zinc": 0.15,
    "copper": 0.08,
    "vitaminA": null,
    "vitaminC": 28,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f178",
    "name": "Fenugreek, leaves",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 30,
    "water": 86.6,
    "energy": 45,
    "protein": 3.5,
    "fat": 0.5,
    "ash": 1.5,
    "fiber": 1.4,
    "carbohydrate": 6.5,
    "sodium": 68,
    "potassium": 260,
    "calcium": 135,
    "phosphorus": 47,
    "magnesium": 12,
    "iron": 6.4,
    "zinc": 4.5,
    "copper": 0.7,
    "vitaminA": 700,
    "vitaminC": 60,
    "thiamin": 0.12,
    "riboflavin": 0.25,
    "servingSize": null
  },
  {
    "id": "f179",
    "name": "Garden rocket",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 25,
    "water": 89.9,
    "energy": 32,
    "protein": 2.5,
    "fat": 0.5,
    "ash": 1.7,
    "fiber": 1,
    "carbohydrate": 4.4,
    "sodium": 33,
    "potassium": 257,
    "calcium": 127,
    "phosphorus": 40,
    "magnesium": 13,
    "iron": 8.3,
    "zinc": 0.8,
    "copper": 0.3,
    "vitaminA": 364,
    "vitaminC": 125,
    "thiamin": 0.14,
    "riboflavin": 0.21,
    "servingSize": null
  },
  {
    "id": "f180",
    "name": "Garlic, balb",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 5,
    "water": 62.3,
    "energy": 142,
    "protein": 5.6,
    "fat": 0.3,
    "ash": 1.5,
    "fiber": 1.2,
    "carbohydrate": 29.1,
    "sodium": 22,
    "potassium": 515,
    "calcium": 34,
    "phosphorus": 168,
    "magnesium": 30,
    "iron": 1.6,
    "zinc": 0.72,
    "copper": 0.22,
    "vitaminA": null,
    "vitaminC": 5,
    "thiamin": 0.22,
    "riboflavin": 0.07,
    "servingSize": null
  },
  {
    "id": "f181",
    "name": "Grape leaves",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 5,
    "water": 76.4,
    "energy": 80,
    "protein": 4,
    "fat": 0.6,
    "ash": 2,
    "fiber": 2.3,
    "carbohydrate": 14.7,
    "sodium": 55,
    "potassium": 370,
    "calcium": 394,
    "phosphorus": 45,
    "magnesium": 15,
    "iron": 2.2,
    "zinc": 0.3,
    "copper": 0.05,
    "vitaminA": 1287,
    "vitaminC": 90,
    "thiamin": 0.25,
    "riboflavin": 0.1,
    "servingSize": null
  },
  {
    "id": "f182",
    "name": "Grape leaves, pickled",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 75.4,
    "energy": 71,
    "protein": 7.3,
    "fat": 0.6,
    "ash": 4.6,
    "fiber": 3,
    "carbohydrate": 9.1,
    "sodium": 894,
    "potassium": null,
    "calcium": 385,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2,
    "zinc": 0.3,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 13,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f183",
    "name": "Grape leaves, rice stuff",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 69.4,
    "energy": 131,
    "protein": 4,
    "fat": 4.2,
    "ash": 1.5,
    "fiber": 1.5,
    "carbohydrate": 19.4,
    "sodium": 385,
    "potassium": 205,
    "calcium": 96,
    "phosphorus": 52,
    "magnesium": 14,
    "iron": 2.5,
    "zinc": 0.3,
    "copper": 0.13,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f184",
    "name": "Jew's mallow",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 70,
    "water": 83.9,
    "energy": 54,
    "protein": 5,
    "fat": 1,
    "ash": 2.3,
    "fiber": 1.5,
    "carbohydrate": 6.3,
    "sodium": 42,
    "potassium": 380,
    "calcium": 270,
    "phosphorus": 61,
    "magnesium": 17,
    "iron": 4.2,
    "zinc": 0.4,
    "copper": 0.04,
    "vitaminA": 1150,
    "vitaminC": 80,
    "thiamin": 0.5,
    "riboflavin": 0.32,
    "servingSize": null
  },
  {
    "id": "f185",
    "name": "Jew's mallow, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 81,
    "energy": 91,
    "protein": 3.5,
    "fat": 6,
    "ash": 2.3,
    "fiber": 1.5,
    "carbohydrate": 5.7,
    "sodium": 300,
    "potassium": 250,
    "calcium": 200,
    "phosphorus": 74,
    "magnesium": null,
    "iron": 1.4,
    "zinc": 0.13,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f186",
    "name": "Leeks, bulb",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 50,
    "water": 93.9,
    "energy": 18,
    "protein": 1.8,
    "fat": 0.3,
    "ash": 1.1,
    "fiber": 0.9,
    "carbohydrate": 2,
    "sodium": 18,
    "potassium": 270,
    "calcium": 87,
    "phosphorus": 35,
    "magnesium": 15,
    "iron": 1.2,
    "zinc": 0.4,
    "copper": 0.03,
    "vitaminA": 8,
    "vitaminC": 19,
    "thiamin": 0.04,
    "riboflavin": 0.11,
    "servingSize": null
  },
  {
    "id": "f187",
    "name": "Lettuce, Ice-barg",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 10,
    "water": 95.5,
    "energy": 15,
    "protein": 0.9,
    "fat": 0.2,
    "ash": 0.6,
    "fiber": 0.5,
    "carbohydrate": 2.3,
    "sodium": 9,
    "potassium": 213,
    "calcium": 12,
    "phosphorus": 29,
    "magnesium": 3,
    "iron": 1.1,
    "zinc": 0.15,
    "copper": 0.09,
    "vitaminA": 12,
    "vitaminC": 8,
    "thiamin": 0.06,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f188",
    "name": "Lettuce, leaves",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 46,
    "water": 95,
    "energy": 16,
    "protein": 1.1,
    "fat": 0.3,
    "ash": 0.8,
    "fiber": 0.7,
    "carbohydrate": 2.1,
    "sodium": 7,
    "potassium": 125,
    "calcium": 28,
    "phosphorus": 22,
    "magnesium": 10,
    "iron": 1,
    "zinc": 0.15,
    "copper": 0.08,
    "vitaminA": 181,
    "vitaminC": 10,
    "thiamin": 0.05,
    "riboflavin": 0.07,
    "servingSize": null
  },
  {
    "id": "f189",
    "name": "Lettuce, rice stuff",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 72,
    "energy": 124,
    "protein": 3.1,
    "fat": 4,
    "ash": 1.1,
    "fiber": 1,
    "carbohydrate": 18.8,
    "sodium": 320,
    "potassium": 185,
    "calcium": 20,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.7,
    "zinc": 0.3,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f190",
    "name": "Lettuce, rice/meat stuff",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 70,
    "energy": 147,
    "protein": 5,
    "fat": 7,
    "ash": 1.1,
    "fiber": 0.9,
    "carbohydrate": 16,
    "sodium": 308,
    "potassium": 188,
    "calcium": 27,
    "phosphorus": 67,
    "magnesium": null,
    "iron": 0.9,
    "zinc": 0.62,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f191",
    "name": "Mallow",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 10,
    "water": 83.3,
    "energy": 55,
    "protein": 5,
    "fat": 0.6,
    "ash": 2.3,
    "fiber": 1.3,
    "carbohydrate": 7.5,
    "sodium": 48,
    "potassium": 296,
    "calcium": 258,
    "phosphorus": 77,
    "magnesium": 16,
    "iron": 4.5,
    "zinc": 3.6,
    "copper": 0.12,
    "vitaminA": 1272,
    "vitaminC": 34,
    "thiamin": 0.12,
    "riboflavin": 0.16,
    "servingSize": null
  },
  {
    "id": "f192",
    "name": "Mallow, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 80,
    "energy": 99,
    "protein": 3,
    "fat": 5.6,
    "ash": 1.4,
    "fiber": 0.9,
    "carbohydrate": 9.1,
    "sodium": 352,
    "potassium": 154,
    "calcium": 98,
    "phosphorus": 65,
    "magnesium": null,
    "iron": 2.7,
    "zinc": 1.64,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f193",
    "name": "Mint",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 53,
    "water": 84.6,
    "energy": 52,
    "protein": 3.7,
    "fat": 1.2,
    "ash": 1.2,
    "fiber": 2.8,
    "carbohydrate": 6.5,
    "sodium": 2,
    "potassium": 160,
    "calcium": 160,
    "phosphorus": 74,
    "magnesium": 16,
    "iron": 1.2,
    "zinc": 0.4,
    "copper": 0.14,
    "vitaminA": 394,
    "vitaminC": 26,
    "thiamin": 0.09,
    "riboflavin": 0.22,
    "servingSize": null
  },
  {
    "id": "f194",
    "name": "Mnshrooms",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 90.8,
    "energy": 31,
    "protein": 2.5,
    "fat": 0.1,
    "ash": 1,
    "fiber": 0.5,
    "carbohydrate": 5.1,
    "sodium": 10,
    "potassium": 346,
    "calcium": 6,
    "phosphorus": 120,
    "magnesium": 11,
    "iron": 0.6,
    "zinc": 0.72,
    "copper": 0.65,
    "vitaminA": 0,
    "vitaminC": 2,
    "thiamin": 0.1,
    "riboflavin": 0.4,
    "servingSize": null
  },
  {
    "id": "f195",
    "name": "Mashrooms, canned",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 91.6,
    "energy": 27,
    "protein": 3.2,
    "fat": 0.1,
    "ash": 1.2,
    "fiber": 0.5,
    "carbohydrate": 3.4,
    "sodium": 491,
    "potassium": 228,
    "calcium": 20,
    "phosphorus": 56,
    "magnesium": 16,
    "iron": 0.45,
    "zinc": 0.52,
    "copper": 0.46,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.02,
    "riboflavin": 0.25,
    "servingSize": null
  },
  {
    "id": "f196",
    "name": "Okra",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 20,
    "water": 86.5,
    "energy": 47,
    "protein": 2,
    "fat": 0.2,
    "ash": 1,
    "fiber": 1,
    "carbohydrate": 9.3,
    "sodium": 11,
    "potassium": 210,
    "calcium": 98,
    "phosphorus": 74,
    "magnesium": 25,
    "iron": 1.9,
    "zinc": 0.58,
    "copper": 0.16,
    "vitaminA": 13,
    "vitaminC": 20,
    "thiamin": 0.1,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f197",
    "name": "Okra, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 85,
    "energy": 72,
    "protein": 2.8,
    "fat": 3.6,
    "ash": 0.7,
    "fiber": 0.7,
    "carbohydrate": 7.2,
    "sodium": 375,
    "potassium": 213,
    "calcium": 59,
    "phosphorus": 50,
    "magnesium": null,
    "iron": 1.23,
    "zinc": 0.38,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f198",
    "name": "Okra, cooked with meat",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 82.9,
    "energy": 89,
    "protein": 5.1,
    "fat": 5,
    "ash": 0.6,
    "fiber": 0.6,
    "carbohydrate": 5.8,
    "sodium": 386,
    "potassium": 246,
    "calcium": 42,
    "phosphorus": 40,
    "magnesium": null,
    "iron": 1.58,
    "zinc": 0.49,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f199",
    "name": "Olives, black pickled",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 20,
    "water": 69.5,
    "energy": 158,
    "protein": 1.5,
    "fat": 12,
    "ash": 5.1,
    "fiber": 1,
    "carbohydrate": 10.9,
    "sodium": 905,
    "potassium": 137,
    "calcium": 158,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.5,
    "zinc": null,
    "copper": null,
    "vitaminA": 30,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f200",
    "name": "Olives, green pickled",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 20,
    "water": 82.7,
    "energy": 88,
    "protein": 1,
    "fat": 8,
    "ash": 4.4,
    "fiber": 1,
    "carbohydrate": 2.9,
    "sodium": 899,
    "potassium": 150,
    "calcium": 105,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.4,
    "zinc": null,
    "copper": null,
    "vitaminA": 23,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f201",
    "name": "Onions, green",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 40,
    "water": 86.6,
    "energy": 49,
    "protein": 1.1,
    "fat": 0.2,
    "ash": 0.5,
    "fiber": 0.9,
    "carbohydrate": 10.7,
    "sodium": 10,
    "potassium": 224,
    "calcium": 125,
    "phosphorus": 50,
    "magnesium": 8,
    "iron": 1.05,
    "zinc": 0.07,
    "copper": 0.1,
    "vitaminA": 60,
    "vitaminC": 18,
    "thiamin": 0.04,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f202",
    "name": "Onions, mature balb",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 8,
    "water": 83.9,
    "energy": 60,
    "protein": 1.2,
    "fat": 0.2,
    "ash": 0.7,
    "fiber": 0.7,
    "carbohydrate": 13.3,
    "sodium": 13,
    "potassium": 139,
    "calcium": 35,
    "phosphorus": 56,
    "magnesium": 12,
    "iron": 0.5,
    "zinc": 0.1,
    "copper": 0.15,
    "vitaminA": 9,
    "vitaminC": 7,
    "thiamin": 0.03,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f203",
    "name": "Onions, pickled",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 82.5,
    "energy": 53,
    "protein": 0.5,
    "fat": 0.2,
    "ash": 3.2,
    "fiber": 1.4,
    "carbohydrate": 12.2,
    "sodium": 840,
    "potassium": 66,
    "calcium": 30,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.1,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f204",
    "name": "Parsley",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 35,
    "water": 84.6,
    "energy": 50,
    "protein": 3.3,
    "fat": 0.4,
    "ash": 2.2,
    "fiber": 1.3,
    "carbohydrate": 8.2,
    "sodium": 30,
    "potassium": 723,
    "calcium": 210,
    "phosphorus": 54,
    "magnesium": 39,
    "iron": 6.55,
    "zinc": 1.85,
    "copper": 0.18,
    "vitaminA": 1272,
    "vitaminC": 156,
    "thiamin": 0.12,
    "riboflavin": 0.32,
    "servingSize": null
  },
  {
    "id": "f205",
    "name": "Peas",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 60,
    "water": 75.3,
    "energy": 89,
    "protein": 6.2,
    "fat": 0.4,
    "ash": 0.9,
    "fiber": 2.1,
    "carbohydrate": 15.1,
    "sodium": 3,
    "potassium": 286,
    "calcium": 20,
    "phosphorus": 122,
    "magnesium": 19,
    "iron": 1.7,
    "zinc": 1.26,
    "copper": 0.03,
    "vitaminA": 55,
    "vitaminC": 25,
    "thiamin": 0.32,
    "riboflavin": 0.18,
    "servingSize": null
  },
  {
    "id": "f206",
    "name": "Peas/carrot, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 86,
    "energy": 62,
    "protein": 4.1,
    "fat": 3,
    "ash": 1,
    "fiber": 1.2,
    "carbohydrate": 4.7,
    "sodium": 415,
    "potassium": 260,
    "calcium": 17,
    "phosphorus": 61,
    "magnesium": null,
    "iron": 1.1,
    "zinc": 1,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f207",
    "name": "Peppers, green",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 15,
    "water": 92.2,
    "energy": 24,
    "protein": 1.3,
    "fat": 0.3,
    "ash": 0.6,
    "fiber": 1.5,
    "carbohydrate": 4.1,
    "sodium": 25,
    "potassium": 195,
    "calcium": 15,
    "phosphorus": 25,
    "magnesium": 14,
    "iron": 0.9,
    "zinc": 0.3,
    "copper": 0.1,
    "vitaminA": 30,
    "vitaminC": 110,
    "thiamin": 0.06,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f208",
    "name": "Peppers, orange/red/yellow",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 10,
    "water": 90,
    "energy": 34,
    "protein": 1.2,
    "fat": 0.3,
    "ash": 0.7,
    "fiber": 1.2,
    "carbohydrate": 6.6,
    "sodium": 40,
    "potassium": 62,
    "calcium": 5,
    "phosphorus": null,
    "magnesium": 3,
    "iron": 0.95,
    "zinc": 0.21,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 82,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f209",
    "name": "Pumpkin",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 35,
    "water": 91.8,
    "energy": 26,
    "protein": 1,
    "fat": 0.1,
    "ash": 0.8,
    "fiber": 1.1,
    "carbohydrate": 5.2,
    "sodium": 2,
    "potassium": 310,
    "calcium": 25,
    "phosphorus": 33,
    "magnesium": 15,
    "iron": 0.7,
    "zinc": 0.24,
    "copper": 0.02,
    "vitaminA": 330,
    "vitaminC": 7,
    "thiamin": 0.04,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f210",
    "name": "Purslane",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 28,
    "water": 90.5,
    "energy": 31,
    "protein": 2,
    "fat": 0.5,
    "ash": 1.5,
    "fiber": 1,
    "carbohydrate": 4.5,
    "sodium": 30,
    "potassium": 610,
    "calcium": 95,
    "phosphorus": 28,
    "magnesium": 6,
    "iron": 3.2,
    "zinc": 0.1,
    "copper": null,
    "vitaminA": 606,
    "vitaminC": 65,
    "thiamin": 0.14,
    "riboflavin": 0.13,
    "servingSize": null
  },
  {
    "id": "f211",
    "name": "Radishes, red",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 45,
    "water": 94,
    "energy": 19,
    "protein": 1.2,
    "fat": 0.1,
    "ash": 0.7,
    "fiber": 0.8,
    "carbohydrate": 3.2,
    "sodium": 21,
    "potassium": 246,
    "calcium": 27,
    "phosphorus": 33,
    "magnesium": 17,
    "iron": 0.99,
    "zinc": 0.33,
    "copper": 0.15,
    "vitaminA": null,
    "vitaminC": 24,
    "thiamin": 0.03,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f212",
    "name": "Radishes, white",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 28,
    "water": 91.6,
    "energy": 27,
    "protein": 1.4,
    "fat": 0.1,
    "ash": 0.9,
    "fiber": 1,
    "carbohydrate": 5,
    "sodium": 18,
    "potassium": 292,
    "calcium": 56,
    "phosphorus": 27,
    "magnesium": 12,
    "iron": 0.8,
    "zinc": 0.17,
    "copper": 0.11,
    "vitaminA": null,
    "vitaminC": 30,
    "thiamin": 0.02,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f213",
    "name": "Radishes, white root/leaves",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 91,
    "energy": 26,
    "protein": 2,
    "fat": 0.4,
    "ash": 1.8,
    "fiber": 1.1,
    "carbohydrate": 3.7,
    "sodium": 20,
    "potassium": 320,
    "calcium": 213,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.8,
    "zinc": 0.3,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 35,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f214",
    "name": "Spinach",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 20,
    "water": 92.4,
    "energy": 22,
    "protein": 2.5,
    "fat": 0.3,
    "ash": 1.7,
    "fiber": 0.7,
    "carbohydrate": 2.4,
    "sodium": 58,
    "potassium": 540,
    "calcium": 92,
    "phosphorus": 47,
    "magnesium": 54,
    "iron": 4.2,
    "zinc": 0.4,
    "copper": 0.19,
    "vitaminA": 1058,
    "vitaminC": 32,
    "thiamin": 0.11,
    "riboflavin": 0.19,
    "servingSize": null
  },
  {
    "id": "f215",
    "name": "Spinach, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 88.6,
    "energy": 57,
    "protein": 2,
    "fat": 3.9,
    "ash": 1.1,
    "fiber": 1,
    "carbohydrate": 3.4,
    "sodium": 405,
    "potassium": 323,
    "calcium": 55,
    "phosphorus": 28,
    "magnesium": null,
    "iron": 2.91,
    "zinc": 0.28,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f216",
    "name": "Spinach, cooked with meat",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 86,
    "energy": 75,
    "protein": 4,
    "fat": 5.2,
    "ash": 1,
    "fiber": 0.7,
    "carbohydrate": 3.1,
    "sodium": 394,
    "potassium": 375,
    "calcium": 63,
    "phosphorus": 34,
    "magnesium": null,
    "iron": 3.2,
    "zinc": 0.24,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f217",
    "name": "Squash",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 15,
    "water": 92.8,
    "energy": 24,
    "protein": 1.3,
    "fat": 0.2,
    "ash": 0.7,
    "fiber": 0.8,
    "carbohydrate": 4.2,
    "sodium": 6,
    "potassium": 200,
    "calcium": 25,
    "phosphorus": 31,
    "magnesium": 12,
    "iron": 0.5,
    "zinc": 0.19,
    "copper": 0.05,
    "vitaminA": 12,
    "vitaminC": 12,
    "thiamin": 0.04,
    "riboflavin": 0.09,
    "servingSize": null
  },
  {
    "id": "f218",
    "name": "Squash, cooked",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 89.6,
    "energy": 55,
    "protein": 1.9,
    "fat": 3.4,
    "ash": 0.5,
    "fiber": 0.8,
    "carbohydrate": 4.3,
    "sodium": 389,
    "potassium": 212,
    "calcium": 20,
    "phosphorus": 29,
    "magnesium": null,
    "iron": 0.43,
    "zinc": 0.18,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f219",
    "name": "Squash, cooked with meat",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 88.1,
    "energy": 67,
    "protein": 3.4,
    "fat": 4.6,
    "ash": 0.5,
    "fiber": 0.5,
    "carbohydrate": 2.9,
    "sodium": 350,
    "potassium": 204,
    "calcium": 18,
    "phosphorus": 22,
    "magnesium": null,
    "iron": 0.8,
    "zinc": 0.2,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f220",
    "name": "Squash, rice stuff",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 75.5,
    "energy": 103,
    "protein": 2.1,
    "fat": 2.7,
    "ash": 1.1,
    "fiber": 1,
    "carbohydrate": 17.6,
    "sodium": 440,
    "potassium": 245,
    "calcium": 13,
    "phosphorus": 76,
    "magnesium": null,
    "iron": 0.7,
    "zinc": 0.5,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f221",
    "name": "Squash, rice/meat stuff",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 75,
    "energy": 112,
    "protein": 3,
    "fat": 4,
    "ash": 1.1,
    "fiber": 0.9,
    "carbohydrate": 16,
    "sodium": 404,
    "potassium": 22,
    "calcium": 12,
    "phosphorus": 75,
    "magnesium": null,
    "iron": 0.8,
    "zinc": 0.52,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f222",
    "name": "Tomatoes",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 3,
    "water": 94.3,
    "energy": 20,
    "protein": 1.1,
    "fat": 0.3,
    "ash": 0.6,
    "fiber": 0.6,
    "carbohydrate": 3.1,
    "sodium": 10,
    "potassium": 328,
    "calcium": 15,
    "phosphorus": 30,
    "magnesium": 12,
    "iron": 0.5,
    "zinc": 0.2,
    "copper": 0.12,
    "vitaminA": 95,
    "vitaminC": 21,
    "thiamin": 0.05,
    "riboflavin": 0.5,
    "servingSize": null
  },
  {
    "id": "f223",
    "name": "Tomato, ketchup",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 66.7,
    "energy": 126,
    "protein": 1.6,
    "fat": 0.6,
    "ash": 2.2,
    "fiber": 0.3,
    "carbohydrate": 28.6,
    "sodium": 743,
    "potassium": 587,
    "calcium": 20,
    "phosphorus": 40,
    "magnesium": 22,
    "iron": 1.2,
    "zinc": 0.3,
    "copper": 0.4,
    "vitaminA": null,
    "vitaminC": 13,
    "thiamin": 0.07,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f224",
    "name": "Tomato, paste",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 78,
    "energy": 78,
    "protein": 3.8,
    "fat": 0.4,
    "ash": 2.5,
    "fiber": 0.5,
    "carbohydrate": 14.8,
    "sodium": 807,
    "potassium": 514,
    "calcium": 25,
    "phosphorus": 83,
    "magnesium": 28,
    "iron": 3.1,
    "zinc": 0.51,
    "copper": 0.2,
    "vitaminA": 285,
    "vitaminC": 15,
    "thiamin": 0.19,
    "riboflavin": 0.11,
    "servingSize": null
  },
  {
    "id": "f225",
    "name": "Turnips, roots",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 23,
    "water": 91.8,
    "energy": 27,
    "protein": 1.2,
    "fat": 0.2,
    "ash": 0.9,
    "fiber": 0.7,
    "carbohydrate": 5.2,
    "sodium": 48,
    "potassium": 270,
    "calcium": 36,
    "phosphorus": 33,
    "magnesium": 10,
    "iron": 0.5,
    "zinc": 0.15,
    "copper": 0.05,
    "vitaminA": 0,
    "vitaminC": 30,
    "thiamin": 0.04,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f226",
    "name": "Vegetables, pickled",
    "category": "VEGETABLES AND VEGETABLE-BASED FOODS",
    "refuse": 0,
    "water": 89,
    "energy": 16,
    "protein": 1.1,
    "fat": 0,
    "ash": 6.5,
    "fiber": 0.4,
    "carbohydrate": 3,
    "sodium": 956,
    "potassium": 258,
    "calcium": 150,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.9,
    "zinc": 0.09,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 6,
    "thiamin": 0.03,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f227",
    "name": "Apples",
    "category": "FRUITS",
    "refuse": 11,
    "water": 84.5,
    "energy": 57,
    "protein": 0.4,
    "fat": 0.2,
    "ash": 0.6,
    "fiber": 0.8,
    "carbohydrate": 13.5,
    "sodium": 5,
    "potassium": 125,
    "calcium": 5,
    "phosphorus": 12,
    "magnesium": 6,
    "iron": 0.3,
    "zinc": 0.08,
    "copper": 0.07,
    "vitaminA": 5,
    "vitaminC": 7,
    "thiamin": 0.05,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f228",
    "name": "Apricots",
    "category": "FRUITS",
    "refuse": 10,
    "water": 84.6,
    "energy": 58,
    "protein": 0.9,
    "fat": 0.4,
    "ash": 0.7,
    "fiber": 0.7,
    "carbohydrate": 12.7,
    "sodium": 3,
    "potassium": 304,
    "calcium": 15,
    "phosphorus": 25,
    "magnesium": 11,
    "iron": 0.5,
    "zinc": 0.2,
    "copper": 0.1,
    "vitaminA": 265,
    "vitaminC": 12,
    "thiamin": 0.04,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f229",
    "name": "Apricots, dried",
    "category": "FRUITS",
    "refuse": 0,
    "water": 24,
    "energy": 282,
    "protein": 4.5,
    "fat": 0.6,
    "ash": 3.1,
    "fiber": 3.2,
    "carbohydrate": 64.6,
    "sodium": 18,
    "potassium": 1615,
    "calcium": 86,
    "phosphorus": 120,
    "magnesium": 40,
    "iron": 5,
    "zinc": 0.63,
    "copper": 0.3,
    "vitaminA": 590,
    "vitaminC": 2,
    "thiamin": 0.01,
    "riboflavin": 0.18,
    "servingSize": null
  },
  {
    "id": "f230",
    "name": "Avocado",
    "category": "FRUITS",
    "refuse": 30,
    "water": 73,
    "energy": 165,
    "protein": 2.2,
    "fat": 14,
    "ash": 1.8,
    "fiber": 1.5,
    "carbohydrate": 7.5,
    "sodium": 3,
    "potassium": 594,
    "calcium": 15,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.05,
    "zinc": 0.9,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 12,
    "thiamin": 0.12,
    "riboflavin": 0.19,
    "servingSize": null
  },
  {
    "id": "f231",
    "name": "Bananas",
    "category": "FRUITS",
    "refuse": 33,
    "water": 75.2,
    "energy": 95,
    "protein": 1.3,
    "fat": 0.3,
    "ash": 0.9,
    "fiber": 0.6,
    "carbohydrate": 21.7,
    "sodium": 3,
    "potassium": 350,
    "calcium": 10,
    "phosphorus": 25,
    "magnesium": 30,
    "iron": 0.6,
    "zinc": 0.21,
    "copper": 0.18,
    "vitaminA": 28,
    "vitaminC": 8,
    "thiamin": 0.04,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f232",
    "name": "Cantaloupe",
    "category": "FRUITS",
    "refuse": 42,
    "water": 90.8,
    "energy": 33,
    "protein": 0.8,
    "fat": 0.1,
    "ash": 0.6,
    "fiber": 0.4,
    "carbohydrate": 7.3,
    "sodium": 18,
    "potassium": 280,
    "calcium": 15,
    "phosphorus": 18,
    "magnesium": 20,
    "iron": 0.5,
    "zinc": 0.15,
    "copper": 0.05,
    "vitaminA": 219,
    "vitaminC": 32,
    "thiamin": 0.05,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f233",
    "name": "Custard-apple",
    "category": "FRUITS",
    "refuse": 38,
    "water": 76.5,
    "energy": 85,
    "protein": 2.9,
    "fat": 0.5,
    "ash": 1.4,
    "fiber": 1.4,
    "carbohydrate": 17.3,
    "sodium": 25,
    "potassium": 436,
    "calcium": 20,
    "phosphorus": 38,
    "magnesium": 44,
    "iron": 0.77,
    "zinc": 0.9,
    "copper": 0.2,
    "vitaminA": null,
    "vitaminC": 27,
    "thiamin": 0.1,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f234",
    "name": "Dates",
    "category": "FRUITS",
    "refuse": 20,
    "water": 70,
    "energy": 115,
    "protein": 0.9,
    "fat": 0.2,
    "ash": 0.7,
    "fiber": 0.9,
    "carbohydrate": 27.3,
    "sodium": 2,
    "potassium": 300,
    "calcium": 22,
    "phosphorus": 26,
    "magnesium": 18,
    "iron": 1.1,
    "zinc": 0.13,
    "copper": 0.12,
    "vitaminA": 4,
    "vitaminC": 11,
    "thiamin": 0.05,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f235",
    "name": "Dates, dried (Agwa)",
    "category": "FRUITS",
    "refuse": 0,
    "water": 18,
    "energy": 315,
    "protein": 2.3,
    "fat": 0.5,
    "ash": 1.7,
    "fiber": 2.2,
    "carbohydrate": 75.3,
    "sodium": 3,
    "potassium": 690,
    "calcium": 62,
    "phosphorus": 61,
    "magnesium": 44,
    "iron": 3.2,
    "zinc": 0.4,
    "copper": 0.3,
    "vitaminA": 10,
    "vitaminC": 0,
    "thiamin": 0.08,
    "riboflavin": 0.01,
    "servingSize": null
  },
  {
    "id": "f236",
    "name": "Figs",
    "category": "FRUITS",
    "refuse": 5,
    "water": 80.4,
    "energy": 72,
    "protein": 1.3,
    "fat": 0.4,
    "ash": 0.6,
    "fiber": 1.5,
    "carbohydrate": 15.8,
    "sodium": 4,
    "potassium": 180,
    "calcium": 62,
    "phosphorus": 28,
    "magnesium": 25,
    "iron": 1.05,
    "zinc": 0.22,
    "copper": 0.05,
    "vitaminA": 27,
    "vitaminC": 4,
    "thiamin": 0.07,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f237",
    "name": "Figs, dried",
    "category": "FRUITS",
    "refuse": 0,
    "water": 20.3,
    "energy": 293,
    "protein": 4.1,
    "fat": 1.1,
    "ash": 2.2,
    "fiber": 5.7,
    "carbohydrate": 66.6,
    "sodium": 13,
    "potassium": 901,
    "calcium": 130,
    "phosphorus": 80,
    "magnesium": 65,
    "iron": 3.53,
    "zinc": 0.9,
    "copper": 0.23,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": 0.12,
    "riboflavin": 0.11,
    "servingSize": null
  },
  {
    "id": "f238",
    "name": "Grapefruits",
    "category": "FRUITS",
    "refuse": 37,
    "water": 88.7,
    "energy": 43,
    "protein": 0.6,
    "fat": 0.1,
    "ash": 0.4,
    "fiber": 0.4,
    "carbohydrate": 9.8,
    "sodium": 5,
    "potassium": 140,
    "calcium": 15,
    "phosphorus": 18,
    "magnesium": 12,
    "iron": 0.2,
    "zinc": 0.12,
    "copper": 0.03,
    "vitaminA": 2,
    "vitaminC": 35,
    "thiamin": 0.03,
    "riboflavin": 0.02,
    "servingSize": null
  },
  {
    "id": "f239",
    "name": "Grapes, seedless",
    "category": "FRUITS",
    "refuse": 0,
    "water": 80.4,
    "energy": 77,
    "protein": 0.6,
    "fat": 0.6,
    "ash": 0.5,
    "fiber": 0.7,
    "carbohydrate": 17.2,
    "sodium": 5,
    "potassium": 242,
    "calcium": 17,
    "phosphorus": 22,
    "magnesium": 9,
    "iron": 0.36,
    "zinc": 0.09,
    "copper": 0.12,
    "vitaminA": 3,
    "vitaminC": 5,
    "thiamin": 0.07,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f240",
    "name": "Guavas",
    "category": "FRUITS",
    "refuse": 5,
    "water": 81.2,
    "energy": 62,
    "protein": 0.8,
    "fat": 0.5,
    "ash": 0.6,
    "fiber": 3.4,
    "carbohydrate": 13.5,
    "sodium": 8,
    "potassium": 255,
    "calcium": 23,
    "phosphorus": 44,
    "magnesium": 25,
    "iron": 0.8,
    "zinc": 0.46,
    "copper": 0.12,
    "vitaminA": 20,
    "vitaminC": 227,
    "thiamin": 0.05,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f241",
    "name": "Husk tomatoes",
    "category": "FRUITS",
    "refuse": 3,
    "water": 79.9,
    "energy": 75,
    "protein": 2.7,
    "fat": 0.3,
    "ash": 0.8,
    "fiber": 1,
    "carbohydrate": 15.3,
    "sodium": 15,
    "potassium": 146,
    "calcium": 27,
    "phosphorus": 42,
    "magnesium": null,
    "iron": 0.47,
    "zinc": 0.52,
    "copper": null,
    "vitaminA": 7,
    "vitaminC": 13,
    "thiamin": 0.09,
    "riboflavin": 0.08,
    "servingSize": null
  },
  {
    "id": "f242",
    "name": "Jujuba",
    "category": "FRUITS",
    "refuse": 25,
    "water": 76,
    "energy": 89,
    "protein": 1.4,
    "fat": 0.2,
    "ash": 0.7,
    "fiber": 1.4,
    "carbohydrate": 20.3,
    "sodium": 5,
    "potassium": 280,
    "calcium": 34,
    "phosphorus": 27,
    "magnesium": null,
    "iron": 0.8,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 65,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f243",
    "name": "Kiwifruit",
    "category": "FRUITS",
    "refuse": 12,
    "water": 85,
    "energy": 54,
    "protein": 1.9,
    "fat": 0.6,
    "ash": 0.7,
    "fiber": 1.6,
    "carbohydrate": 10.2,
    "sodium": 3,
    "potassium": 210,
    "calcium": 30,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.3,
    "zinc": 0.1,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 95,
    "thiamin": 0.01,
    "riboflavin": 0.02,
    "servingSize": null
  },
  {
    "id": "f244",
    "name": "Lemons",
    "category": "FRUITS",
    "refuse": 38,
    "water": 91.3,
    "energy": 32,
    "protein": 0.8,
    "fat": 0.2,
    "ash": 0.4,
    "fiber": 0.6,
    "carbohydrate": 6.7,
    "sodium": 4,
    "potassium": 165,
    "calcium": 32,
    "phosphorus": 14,
    "magnesium": 12,
    "iron": 0.5,
    "zinc": 0.15,
    "copper": 0.22,
    "vitaminA": 4,
    "vitaminC": 75,
    "thiamin": 0.05,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f245",
    "name": "Lemons sweet",
    "category": "FRUITS",
    "refuse": 36,
    "water": 90.6,
    "energy": 37,
    "protein": 0.6,
    "fat": 0.5,
    "ash": 0.3,
    "fiber": 0.4,
    "carbohydrate": 7.6,
    "sodium": 6,
    "potassium": 140,
    "calcium": 29,
    "phosphorus": 20,
    "magnesium": 11,
    "iron": 0.35,
    "zinc": 0.24,
    "copper": 0.23,
    "vitaminA": 4,
    "vitaminC": 36,
    "thiamin": 0.03,
    "riboflavin": 0.02,
    "servingSize": null
  },
  {
    "id": "f246",
    "name": "Lime",
    "category": "FRUITS",
    "refuse": 42,
    "water": 91.1,
    "energy": 33,
    "protein": 0.6,
    "fat": 0.3,
    "ash": 0.4,
    "fiber": 0.7,
    "carbohydrate": 6.9,
    "sodium": 5,
    "potassium": 160,
    "calcium": 27,
    "phosphorus": 22,
    "magnesium": 15,
    "iron": 0.4,
    "zinc": 0.12,
    "copper": 0.21,
    "vitaminA": null,
    "vitaminC": 50,
    "thiamin": 0.04,
    "riboflavin": 0.01,
    "servingSize": null
  },
  {
    "id": "f247",
    "name": "Loquate",
    "category": "FRUITS",
    "refuse": 50,
    "water": 87,
    "energy": 51,
    "protein": 0.3,
    "fat": 0.6,
    "ash": 0.4,
    "fiber": 0.7,
    "carbohydrate": 11,
    "sodium": 12,
    "potassium": 238,
    "calcium": 15,
    "phosphorus": 25,
    "magnesium": null,
    "iron": 0.43,
    "zinc": null,
    "copper": null,
    "vitaminA": 250,
    "vitaminC": 5,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f248",
    "name": "Mandari",
    "category": "FRUITS",
    "refuse": 31,
    "water": 86.9,
    "energy": 48,
    "protein": 0.7,
    "fat": 0.2,
    "ash": 0.5,
    "fiber": 0.9,
    "carbohydrate": 10.8,
    "sodium": 7,
    "potassium": 147,
    "calcium": 39,
    "phosphorus": 22,
    "magnesium": 16,
    "iron": 0.5,
    "zinc": 0.4,
    "copper": 0.04,
    "vitaminA": 13,
    "vitaminC": 26,
    "thiamin": 0.09,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f249",
    "name": "Mangos",
    "category": "FRUITS",
    "refuse": 43,
    "water": 81.8,
    "energy": 68,
    "protein": 0.8,
    "fat": 0.3,
    "ash": 0.5,
    "fiber": 1.1,
    "carbohydrate": 15.5,
    "sodium": 10,
    "potassium": 200,
    "calcium": 15,
    "phosphorus": 17,
    "magnesium": 8,
    "iron": 0.5,
    "zinc": 0.18,
    "copper": 0.11,
    "vitaminA": 250,
    "vitaminC": 30,
    "thiamin": 0.04,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f250",
    "name": "Melons",
    "category": "FRUITS",
    "refuse": 28,
    "water": 91.5,
    "energy": 30,
    "protein": 0.8,
    "fat": 0.2,
    "ash": 0.6,
    "fiber": 0.6,
    "carbohydrate": 6.3,
    "sodium": 8,
    "potassium": 270,
    "calcium": 10,
    "phosphorus": 14,
    "magnesium": 8,
    "iron": 0.4,
    "zinc": 0.2,
    "copper": 0.12,
    "vitaminA": 18,
    "vitaminC": 22,
    "thiamin": 0.05,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f251",
    "name": "Mulberry",
    "category": "FRUITS",
    "refuse": 5,
    "water": 79.6,
    "energy": 76,
    "protein": 1.6,
    "fat": 0.5,
    "ash": 0.8,
    "fiber": 1.2,
    "carbohydrate": 16.3,
    "sodium": 25,
    "potassium": 236,
    "calcium": 55,
    "phosphorus": 35,
    "magnesium": 17,
    "iron": 1.8,
    "zinc": 1.3,
    "copper": 0.06,
    "vitaminA": 3,
    "vitaminC": 10,
    "thiamin": 0.05,
    "riboflavin": 0.07,
    "servingSize": null
  },
  {
    "id": "f252",
    "name": "Oranges",
    "category": "FRUITS",
    "refuse": 28,
    "water": 85.5,
    "energy": 56,
    "protein": 1.1,
    "fat": 0.3,
    "ash": 0.4,
    "fiber": 0.6,
    "carbohydrate": 12.1,
    "sodium": 3,
    "potassium": 181,
    "calcium": 37,
    "phosphorus": 28,
    "magnesium": 7,
    "iron": 0.3,
    "zinc": 0.2,
    "copper": 0.07,
    "vitaminA": 8,
    "vitaminC": 55,
    "thiamin": 0.08,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f253",
    "name": "Papaya",
    "category": "FRUITS",
    "refuse": 25,
    "water": 88,
    "energy": 44,
    "protein": 0.6,
    "fat": 0.2,
    "ash": 0.6,
    "fiber": 0.6,
    "carbohydrate": 10,
    "sodium": 5,
    "potassium": 237,
    "calcium": 25,
    "phosphorus": 18,
    "magnesium": 22,
    "iron": 0.57,
    "zinc": 0.3,
    "copper": null,
    "vitaminA": 124,
    "vitaminC": 59,
    "thiamin": 0.03,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f254",
    "name": "Peaches",
    "category": "FRUITS",
    "refuse": 11,
    "water": 86.2,
    "energy": 51,
    "protein": 0.7,
    "fat": 0.2,
    "ash": 0.5,
    "fiber": 0.7,
    "carbohydrate": 11.7,
    "sodium": 3,
    "potassium": 280,
    "calcium": 15,
    "phosphorus": 22,
    "magnesium": 11,
    "iron": 0.8,
    "zinc": 0.2,
    "copper": 0.04,
    "vitaminA": 43,
    "vitaminC": 12,
    "thiamin": 0.02,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f255",
    "name": "Pears",
    "category": "FRUITS",
    "refuse": 22,
    "water": 83.8,
    "energy": 58,
    "protein": 0.3,
    "fat": 0.2,
    "ash": 0.7,
    "fiber": 1.3,
    "carbohydrate": 13.7,
    "sodium": 5,
    "potassium": 144,
    "calcium": 10,
    "phosphorus": 12,
    "magnesium": 10,
    "iron": 0.4,
    "zinc": 0.14,
    "copper": 0.12,
    "vitaminA": 2,
    "vitaminC": 2,
    "thiamin": 0.03,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f256",
    "name": "Persimmon",
    "category": "FRUITS",
    "refuse": 30,
    "water": 77,
    "energy": 85,
    "protein": 0.9,
    "fat": 0.5,
    "ash": 1.3,
    "fiber": 1,
    "carbohydrate": 19.3,
    "sodium": 4,
    "potassium": 179,
    "calcium": 12,
    "phosphorus": 22,
    "magnesium": 10,
    "iron": 0.4,
    "zinc": 0.14,
    "copper": 0.11,
    "vitaminA": 157,
    "vitaminC": 12,
    "thiamin": 0.05,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f257",
    "name": "Pineapple",
    "category": "FRUITS",
    "refuse": 46,
    "water": 85.7,
    "energy": 55,
    "protein": 0.5,
    "fat": 0.2,
    "ash": 0.4,
    "fiber": 0.5,
    "carbohydrate": 12.7,
    "sodium": 3,
    "potassium": 219,
    "calcium": 15,
    "phosphorus": 12,
    "magnesium": 16,
    "iron": 0.55,
    "zinc": 0.22,
    "copper": 0.06,
    "vitaminA": 8,
    "vitaminC": 15,
    "thiamin": 0.1,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f258",
    "name": "Plums",
    "category": "FRUITS",
    "refuse": 15,
    "water": 86.8,
    "energy": 50,
    "protein": 0.7,
    "fat": 0.2,
    "ash": 0.4,
    "fiber": 0.6,
    "carbohydrate": 11.3,
    "sodium": 2,
    "potassium": 260,
    "calcium": 19,
    "phosphorus": 21,
    "magnesium": 11,
    "iron": 0.6,
    "zinc": 0.05,
    "copper": 0.08,
    "vitaminA": 71,
    "vitaminC": 5,
    "thiamin": 0.04,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f259",
    "name": "Pomegranate",
    "category": "FRUITS",
    "refuse": 40,
    "water": 80.7,
    "energy": 68,
    "protein": 0.7,
    "fat": 0.4,
    "ash": 0.6,
    "fiber": 2.1,
    "carbohydrate": 15.5,
    "sodium": 5,
    "potassium": 280,
    "calcium": 15,
    "phosphorus": 30,
    "magnesium": 8,
    "iron": 0.3,
    "zinc": 0.14,
    "copper": 0.07,
    "vitaminA": 2,
    "vitaminC": 16,
    "thiamin": 0.08,
    "riboflavin": 0.06,
    "servingSize": null
  },
  {
    "id": "f260",
    "name": "Prickly peare",
    "category": "FRUITS",
    "refuse": 45,
    "water": 86.5,
    "energy": 31,
    "protein": 1,
    "fat": 0.9,
    "ash": 0.3,
    "fiber": 6.5,
    "carbohydrate": 4.8,
    "sodium": 5,
    "potassium": 150,
    "calcium": 50,
    "phosphorus": 35,
    "magnesium": 18,
    "iron": 1.4,
    "zinc": 0.72,
    "copper": 0.24,
    "vitaminA": 13,
    "vitaminC": 18,
    "thiamin": 0.03,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f261",
    "name": "Prunes, dried",
    "category": "FRUITS",
    "refuse": 0,
    "water": 22.5,
    "energy": 299,
    "protein": 2.3,
    "fat": 0.5,
    "ash": 1,
    "fiber": 2.5,
    "carbohydrate": 71.2,
    "sodium": 12,
    "potassium": 850,
    "calcium": 41,
    "phosphorus": 61,
    "magnesium": null,
    "iron": 2.2,
    "zinc": 0.5,
    "copper": null,
    "vitaminA": 145,
    "vitaminC": 2,
    "thiamin": 0.12,
    "riboflavin": 0.25,
    "servingSize": null
  },
  {
    "id": "f262",
    "name": "Raisins",
    "category": "FRUITS",
    "refuse": 0,
    "water": 17,
    "energy": 320,
    "protein": 2.4,
    "fat": 0.3,
    "ash": 1.8,
    "fiber": 1.6,
    "carbohydrate": 76.9,
    "sodium": 30,
    "potassium": 740,
    "calcium": 63,
    "phosphorus": 90,
    "magnesium": 34,
    "iron": 1.64,
    "zinc": 0.16,
    "copper": 0.18,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": 0.12,
    "riboflavin": 0.09,
    "servingSize": null
  },
   {
    "id": "f263",
    "name": "Raspberries",
    "category": "FRUITS",
    "refuse": 5,
    "water": 83.5,
    "energy": 60,
    "protein": 1.3,
    "fat": 0.5,
    "ash": 0.7,
    "fiber": 1.5,
    "carbohydrate": 12.5,
    "sodium": 2,
    "potassium": 155,
    "calcium": 30,
    "phosphorus": 27,
    "magnesium": 25,
    "iron": 1.2,
    "zinc": 0.35,
    "copper": 0.11,
    "vitaminA": null,
    "vitaminC": 22,
    "thiamin": 0.04,
    "riboflavin": 0.07,
    "servingSize": null
  },
  {
    "id": "f264",
    "name": "Strawberries",
    "category": "FRUITS",
    "refuse": 7,
    "water": 90.3,
    "energy": 34,
    "protein": 0.8,
    "fat": 0.4,
    "ash": 0.6,
    "fiber": 1.2,
    "carbohydrate": 6.7,
    "sodium": 2,
    "potassium": 185,
    "calcium": 26,
    "phosphorus": 24,
    "magnesium": 15,
    "iron": 0.9,
    "zinc": 0.1,
    "copper": 0.11,
    "vitaminA": 6,
    "vitaminC": 40,
    "thiamin": 0.04,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f265",
    "name": "Sycamore",
    "category": "FRUITS",
    "refuse": 2,
    "water": 82,
    "energy": 66,
    "protein": 0.9,
    "fat": 0.3,
    "ash": 1.3,
    "fiber": 0.5,
    "carbohydrate": 15,
    "sodium": 9,
    "potassium": 183,
    "calcium": 19,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.7,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 13,
    "thiamin": 0.03,
    "riboflavin": 0.09,
    "servingSize": null
  },
  {
    "id": "f267",
    "name": "Candy, Assalia",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 2.5,
    "energy": 387,
    "protein": 1.6,
    "fat": 0.7,
    "ash": 1.6,
    "fiber": 0,
    "carbohydrate": 93.6,
    "sodium": 30,
    "potassium": null,
    "calcium": 26,
    "phosphorus": null,
    "magnesium": null,
    "iron": 7.2,
    "zinc": 0.63,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.2,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f268",
    "name": "Candy, fmit flavor (hard)",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 1.5,
    "energy": 392,
    "protein": 0,
    "fat": 0,
    "ash": 0.5,
    "fiber": 0,
    "carbohydrate": 98,
    "sodium": 75,
    "potassium": 10,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0,
    "servingSize": null
  },
  {
    "id": "f269",
    "name": "Candy, Folia",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 2.3,
    "energy": 444,
    "protein": 7.8,
    "fat": 12,
    "ash": 0.9,
    "fiber": 0.9,
    "carbohydrate": 76.1,
    "sodium": 77,
    "potassium": null,
    "calcium": 19,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.75,
    "zinc": 0.74,
    "copper": null,
    "vitaminA": 20,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0,
    "servingSize": null
  },
  {
    "id": "f270",
    "name": "Candy, Homosia",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 3.5,
    "energy": 387,
    "protein": 6.5,
    "fat": 1.7,
    "ash": 0.9,
    "fiber": 0.9,
    "carbohydrate": 86.5,
    "sodium": 27,
    "potassium": 405,
    "calcium": 77,
    "phosphorus": 215,
    "magnesium": null,
    "iron": 2.3,
    "zinc": 1.7,
    "copper": null,
    "vitaminA": 9,
    "vitaminC": 0,
    "thiamin": 0.24,
    "riboflavin": 0.1,
    "servingSize": null
  },
  {
    "id": "f271",
    "name": "Candy, jelly beaus",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 12,
    "energy": 353,
    "protein": 2.6,
    "fat": 0.3,
    "ash": 0.2,
    "fiber": 0,
    "carbohydrate": 84.9,
    "sodium": 46,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": 0,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0,
    "servingSize": null
  },
  {
    "id": "f272",
    "name": "Candy. Koozassal",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 2.3,
    "energy": 387,
    "protein": 2.4,
    "fat": 0.5,
    "ash": 1.5,
    "fiber": 0,
    "carbohydrate": 93.3,
    "sodium": 33,
    "potassium": null,
    "calcium": 41,
    "phosphorus": null,
    "magnesium": null,
    "iron": 9.2,
    "zinc": 0.87,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f273",
    "name": "Candy, Malban",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 5,
    "energy": 382,
    "protein": 0.8,
    "fat": 0.5,
    "ash": 0.2,
    "fiber": 0,
    "carbohydrate": 93.5,
    "sodium": 40,
    "potassium": null,
    "calcium": 9,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.05,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0,
    "servingSize": null
  },
  {
    "id": "f274",
    "name": "Candy, Nogha with nuts",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 3.7,
    "energy": 397,
    "protein": 7.7,
    "fat": 3.3,
    "ash": 1.1,
    "fiber": 0,
    "carbohydrate": 84.2,
    "sodium": 66,
    "potassium": null,
    "calcium": 90,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.6,
    "zinc": 1.5,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0,
    "servingSize": null
  },
  {
    "id": "f275",
    "name": "Gandy, Semsemia",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 1.9,
    "energy": 443,
    "protein": 6,
    "fat": 12,
    "ash": 1.1,
    "fiber": 1.3,
    "carbohydrate": 77.7,
    "sodium": 16,
    "potassium": null,
    "calcium": 177,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.8,
    "zinc": 2.1,
    "copper": null,
    "vitaminA": 5,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f276",
    "name": "Candy, Toffee",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 5,
    "energy": 392,
    "protein": 4,
    "fat": 4,
    "ash": 2,
    "fiber": 0,
    "carbohydrate": 85,
    "sodium": 178,
    "potassium": 122,
    "calcium": 105,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.3,
    "zinc": 0.2,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0,
    "riboflavin": 0,
    "servingSize": null
  },
  {
    "id": "f277",
    "name": "Chewing gum",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": null,
    "energy": 320,
    "protein": null,
    "fat": null,
    "ash": null,
    "fiber": null,
    "carbohydrate": 80,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f278",
    "name": "Chocolate, milk",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 2.5,
    "energy": 513,
    "protein": 8,
    "fat": 27,
    "ash": 2,
    "fiber": 1,
    "carbohydrate": 59.5,
    "sodium": 142,
    "potassium": 373,
    "calcium": 170,
    "phosphorus": 202,
    "magnesium": 45,
    "iron": 1.4,
    "zinc": 0.2,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.06,
    "riboflavin": 0.21,
    "servingSize": null
  },
  {
    "id": "f279",
    "name": "Chocolate, coconuts",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 5.3,
    "energy": 469,
    "protein": 4,
    "fat": 20,
    "ash": 1.2,
    "fiber": 1.3,
    "carbohydrate": 68.2,
    "sodium": 155,
    "potassium": 300,
    "calcium": 73,
    "phosphorus": 130,
    "magnesium": 40,
    "iron": 1.2,
    "zinc": 0.3,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.04,
    "riboflavin": 0.1,
    "servingSize": null
  },
  {
    "id": "f280",
    "name": "Chocolate, nuts",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 2.1,
    "energy": 530,
    "protein": 9.3,
    "fat": 30,
    "ash": 1.9,
    "fiber": 1,
    "carbohydrate": 55.6,
    "sodium": 103,
    "potassium": 310,
    "calcium": 132,
    "phosphorus": 234,
    "magnesium": 12,
    "iron": 1.6,
    "zinc": 0.6,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.09,
    "riboflavin": 0.4,
    "servingSize": null
  },
  {
    "id": "f281",
    "name": "Corn syrup",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 20.5,
    "energy": 317,
    "protein": 0,
    "fat": 0,
    "ash": 0.2,
    "fiber": null,
    "carbohydrate": 79.3,
    "sodium": 20,
    "potassium": 3,
    "calcium": 8,
    "phosphorus": 16,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f282",
    "name": "Halawa Tchinia",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 2.1,
    "energy": 520,
    "protein": 11.2,
    "fat": 27.5,
    "ash": 1,
    "fiber": 1.2,
    "carbohydrate": 57,
    "sodium": 22,
    "potassium": 95,
    "calcium": 42,
    "phosphorus": 386,
    "magnesium": 20,
    "iron": 3,
    "zinc": 2.2,
    "copper": 0.4,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.42,
    "riboflavin": 0.12,
    "servingSize": null
  },
  {
    "id": "f283",
    "name": "Halawa Tebinia with nuts",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 1.8,
    "energy": 519,
    "protein": 12.1,
    "fat": 28,
    "ash": 1.5,
    "fiber": 2,
    "carbohydrate": 54.6,
    "sodium": 48,
    "potassium": null,
    "calcium": 76,
    "phosphorus": null,
    "magnesium": null,
    "iron": 3.2,
    "zinc": null,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f284",
    "name": "Honey",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 17.4,
    "energy": 329,
    "protein": 0.3,
    "fat": null,
    "ash": 0.3,
    "fiber": null,
    "carbohydrate": 82,
    "sodium": 6,
    "potassium": 50,
    "calcium": 11,
    "phosphorus": 10,
    "magnesium": 4,
    "iron": 0.6,
    "zinc": 0.08,
    "copper": 0.04,
    "vitaminA": "T",
    "vitaminC": 3,
    "thiamin": 0.02,
    "riboflavin": 0.05,
    "servingSize": null
  },
  {
    "id": "f285",
    "name": "Jams",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 28.8,
    "energy": 283,
    "protein": 0.7,
    "fat": 0.1,
    "ash": 0.4,
    "fiber": 0.3,
    "carbohydrate": 69.7,
    "sodium": 14,
    "potassium": 95,
    "calcium": 16,
    "phosphorus": 10,
    "magnesium": 9,
    "iron": 1.2,
    "zinc": 0.03,
    "copper": 0.2,
    "vitaminA": "T",
    "vitaminC": 9,
    "thiamin": 0.02,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f286",
    "name": "Jelly powder",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 1.8,
    "energy": 390,
    "protein": 10,
    "fat": 0,
    "ash": 0.8,
    "fiber": null,
    "carbohydrate": 87.4,
    "sodium": 325,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f287",
    "name": "Molasses",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 26,
    "energy": 280,
    "protein": 0,
    "fat": 0,
    "ash": 4,
    "fiber": 0,
    "carbohydrate": 70,
    "sodium": 88,
    "potassium": 1400,
    "calcium": 29,
    "phosphorus": null,
    "magnesium": null,
    "iron": 5.71,
    "zinc": 0.68,
    "copper": 1.8,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": 0.07,
    "riboflavin": 0.13,
    "servingSize": null
  },
  {
    "id": "f288",
    "name": "Oriental Baldava",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 15,
    "energy": 450,
    "protein": 4.8,
    "fat": 23,
    "ash": 0.7,
    "fiber": 0.5,
    "carbohydrate": 56,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f289",
    "name": "Oriental Bakiava (assorted)",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 8.2,
    "energy": 531,
    "protein": 6.3,
    "fat": 34.3,
    "ash": 1,
    "fiber": 0.9,
    "carbohydrate": 49.3,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f290",
    "name": "Oriental Balah El-sham",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 10,
    "energy": 420,
    "protein": 5.3,
    "fat": 12.7,
    "ash": 0.4,
    "fiber": 0.4,
    "carbohydrate": 71.2,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f291",
    "name": "Oriental Basbusa",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 17.5,
    "energy": 355,
    "protein": 4.9,
    "fat": 6,
    "ash": 0.6,
    "fiber": 0.7,
    "carbohydrate": 70.3,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f292",
    "name": "Oriental Basbusa, nuts",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 15,
    "energy": 375,
    "protein": 5.4,
    "fat": 8,
    "ash": 0.5,
    "fiber": 0.8,
    "carbohydrate": 70.3,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f293",
    "name": "Oriental Eish saraya (cream)",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 7.7,
    "energy": 426,
    "protein": 2.5,
    "fat": 12.5,
    "ash": 0.9,
    "fiber": 0.5,
    "carbohydrate": 75.9,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f294",
    "name": "Oriental Gohraiba",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 4.3,
    "energy": 533,
    "protein": 5.5,
    "fat": 31,
    "ash": 0.5,
    "fiber": 0.7,
    "carbohydrate": 58,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f295",
    "name": "Oriental Kaak Eid",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 3.9,
    "energy": 518,
    "protein": 7,
    "fat": 27.6,
    "ash": 0.5,
    "fiber": 0.5,
    "carbohydrate": 60.5,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f296",
    "name": "Oriental Kataif",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 28,
    "energy": 348,
    "protein": 5,
    "fat": 12.8,
    "ash": 0.5,
    "fiber": 0.6,
    "carbohydrate": 53.1,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f297",
    "name": "Oriental Kunafa, cream",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 27.2,
    "energy": 361,
    "protein": 3,
    "fat": 14.5,
    "ash": 0.3,
    "fiber": 0.5,
    "carbohydrate": 54.5,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f298",
    "name": "Oriental Maamoul",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 3.3,
    "energy": 500,
    "protein": 6,
    "fat": 24.3,
    "ash": 1.1,
    "fiber": 1,
    "carbohydrate": 64.3,
    "sodium": 171,
    "potassium": null,
    "calcium": 76,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f299",
    "name": "Oriental Kunafa, nuts",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 6.6,
    "energy": 487,
    "protein": 5.8,
    "fat": 23.7,
    "ash": 0.6,
    "fiber": 0.8,
    "carbohydrate": 62.5,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },

  {
    "id": "f300",
    "name": "Oriental Maamoul",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 3.3,
    "energy": 500,
    "protein": 6,
    "fat": 24.3,
    "ash": 1.1,
    "fiber": 1,
    "carbohydrate": 64.3,
    "sodium": 171,
    "potassium": null,
    "calcium": 76,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f301",
    "name": "Sugar, sucrose",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 0.5,
    "energy": 398,
    "protein": 0,
    "fat": 0,
    "ash": 0,
    "fiber": 0,
    "carbohydrate": 99.5,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f302",
    "name": "Sugar, fructose",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 1,
    "energy": 396,
    "protein": null,
    "fat": null,
    "ash": 0.1,
    "fiber": null,
    "carbohydrate": 98.9,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f303",
    "name": "Sweeteners",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 1.5,
    "energy": 399,
    "protein": 0,
    "fat": 0.9,
    "ash": 0,
    "fiber": 0,
    "carbohydrate": 97.6,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f304",
    "name": "Topping (Chocolate)",
    "category": "SWEETS AND CONFECTIONARY",
    "refuse": 0,
    "water": 24.2,
    "energy": 400,
    "protein": 1.9,
    "fat": 20.7,
    "ash": 1.7,
    "fiber": 0,
    "carbohydrate": 51.5,
    "sodium": null,
    "potassium": 180,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f305",
    "name": "Beef, Basterma",
    "category": "Meat",
    "refuse": 0,
    "water": 47.2,
    "energy": 201,
    "protein": 28.6,
    "fat": 5.8,
    "ash": 8.3,
    "fiber": 1.6,
    "carbohydrate": 8.5,
    "sodium": 2200,
    "potassium": 285,
    "calcium": 110,
    "phosphorus": 318,
    "magnesium": 36,
    "iron": 6.9,
    "zinc": 7.5,
    "copper": 0.72,
    "vitaminA": 13,
    "vitaminC": 0,
    "thiamin": 0.15,
    "riboflavin": 0.27,
    "servingSize": null
  },
  {
    "id": "f306",
    "name": "Beef, Basterma meat",
    "category": "Meat",
    "refuse": 0,
    "water": 58.6,
    "energy": 156,
    "protein": 29.9,
    "fat": 4,
    "ash": 7.5,
    "fiber": 0,
    "carbohydrate": 0,
    "sodium": 1500,
    "potassium": 212,
    "calcium": 45,
    "phosphorus": 270,
    "magnesium": 29,
    "iron": 4,
    "zinc": 7.1,
    "copper": 0.15,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f307",
    "name": "Beef, brain",
    "category": "Meat",
    "refuse": 0,
    "water": 79.3,
    "energy": 121,
    "protein": 10.4,
    "fat": 8.5,
    "ash": 1.2,
    "fiber": 0,
    "carbohydrate": 0.6,
    "sodium": 122,
    "potassium": 225,
    "calcium": 10,
    "phosphorus": 374,
    "magnesium": 15,
    "iron": 1.4,
    "zinc": 1.2,
    "copper": 0.24,
    "vitaminA": 83,
    "vitaminC": 12,
    "thiamin": 0.18,
    "riboflavin": 0.24,
    "servingSize": null
  },
  {
    "id": "f308",
    "name": "Beef, brain (fried)",
    "category": "Meat",
    "refuse": 0,
    "water": 53,
    "energy": 251,
    "protein": 13.7,
    "fat": 13.9,
    "ash": 1.7,
    "fiber": 0,
    "carbohydrate": 17.7,
    "sodium": 665,
    "potassium": 273,
    "calcium": 87,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1.52,
    "zinc": 1.09,
    "copper": 0.3,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f309",
    "name": "Beef, burger",
    "category": "Meat",
    "refuse": 0,
    "water": 59.4,
    "energy": 256,
    "protein": 18,
    "fat": 20,
    "ash": 1.6,
    "fiber": 0,
    "carbohydrate": 1,
    "sodium": 613,
    "potassium": 276,
    "calcium": 75,
    "phosphorus": 166,
    "magnesium": 19,
    "iron": 2.8,
    "zinc": 3.5,
    "copper": 0.24,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.05,
    "riboflavin": 0.18,
    "servingSize": null
  },
  {
    "id": "f310",
    "name": "Beef, burger (grilled)",
    "category": "Meat",
    "refuse": 0,
    "water": 55.3,
    "energy": 263,
    "protein": 21.1,
    "fat": 19,
    "ash": 2.6,
    "fiber": 0,
    "carbohydrate": 2,
    "sodium": 750,
    "potassium": 150,
    "calcium": 32,
    "phosphorus": null,
    "magnesium": null,
    "iron": 3.1,
    "zinc": 4,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f311",
    "name": "Beef, frankfurter",
    "category": "Meat",
    "refuse": 0,
    "water": 60,
    "energy": 224,
    "protein": 15.2,
    "fat": 16.1,
    "ash": 3.1,
    "fiber": 1,
    "carbohydrate": 4.6,
    "sodium": 1025,
    "potassium": 230,
    "calcium": 68,
    "phosphorus": 127,
    "magnesium": 74,
    "iron": 2.5,
    "zinc": 3.7,
    "copper": 0.07,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.16,
    "riboflavin": 0.2,
    "servingSize": null
  },
  {
    "id": "f312",
    "name": "Beef, head",
    "category": "Meat",
    "refuse": 73,
    "water": 64,
    "energy": 212,
    "protein": 20.2,
    "fat": 14.5,
    "ash": 1.2,
    "fiber": 0,
    "carbohydrate": 0.1,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f313",
    "name": "Beef, head meat (boiled)",
    "category": "Meat",
    "refuse": 0,
    "water": 57.4,
    "energy": 239,
    "protein": 25.1,
    "fat": 15.2,
    "ash": 1.9,
    "fiber": 0,
    "carbohydrate": 0.4,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": 5.5,
    "zinc": 4.4,
    "copper": 0.28,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f314",
    "name": "Beef, heart",
    "category": "Meat",
    "refuse": 0,
    "water": 76.5,
    "energy": 112,
    "protein": 16.5,
    "fat": 4.3,
    "ash": 1,
    "fiber": 0,
    "carbohydrate": 1.7,
    "sodium": 90,
    "potassium": 310,
    "calcium": 12,
    "phosphorus": 288,
    "magnesium": 17,
    "iron": 4.6,
    "zinc": 2,
    "copper": 0.41,
    "vitaminA": 5,
    "vitaminC": 5,
    "thiamin": 0.32,
    "riboflavin": 0.92,
    "servingSize": null
  },
  {
    "id": "f315",
    "name": "Beef, kidneys",
    "category": "Meat",
    "refuse": 0,
    "water": 75.9,
    "energy": 121,
    "protein": 16.5,
    "fat": 5.8,
    "ash": 1,
    "fiber": 0,
    "carbohydrate": 0.8,
    "sodium": 184,
    "potassium": 215,
    "calcium": 10,
    "phosphorus": 234,
    "magnesium": 12,
    "iron": 5.8,
    "zinc": 1.8,
    "copper": 0.41,
    "vitaminA": 266,
    "vitaminC": 10,
    "thiamin": 0.27,
    "riboflavin": 1.8,
    "servingSize": null
  },
  {
    "id": "f316",
    "name": "Beef, knuckles",
    "category": "Meat",
    "refuse": 65,
    "water": 71,
    "energy": 137,
    "protein": 23,
    "fat": 5,
    "ash": 1,
    "fiber": 0,
    "carbohydrate": 0,
    "sodium": 62,
    "potassium": 134,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.4,
    "zinc": 1.6,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f317",
    "name": "Beef, liver",
    "category": "Meat",
    "refuse": 0,
    "water": 70.5,
    "energy": 132,
    "protein": 19.5,
    "fat": 4,
    "ash": 1.6,
    "fiber": 0,
    "carbohydrate": 4.4,
    "sodium": 88,
    "potassium": 303,
    "calcium": 10,
    "phosphorus": 352,
    "magnesium": 14,
    "iron": 7.8,
    "zinc": 7.7,
    "copper": 0.8,
    "vitaminA": 12121,
    "vitaminC": 25,
    "thiamin": 0.26,
    "riboflavin": 3.1,
    "servingSize": null
  },
  {
    "id": "f318",
    "name": "Beef, luncheon",
    "category": "Meat",
    "refuse": 0,
    "water": 66.3,
    "energy": 155,
    "protein": 16.3,
    "fat": 7.5,
    "ash": 3.1,
    "fiber": 1.2,
    "carbohydrate": 5.6,
    "sodium": 1250,
    "potassium": 222,
    "calcium": 25,
    "phosphorus": 200,
    "magnesium": 17,
    "iron": 2.6,
    "zinc": 4,
    "copper": 0.1,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.25,
    "riboflavin": 0.24,
    "servingSize": null
  },
  {
    "id": "f319",
    "name": "Beef, lung",
    "category": "Meat",
    "refuse": 0,
    "water": 79.5,
    "energy": 88,
    "protein": 17.1,
    "fat": 2.2,
    "ash": 1.2,
    "fiber": 0,
    "carbohydrate": 0,
    "sodium": 50,
    "potassium": 218,
    "calcium": 10,
    "phosphorus": 204,
    "magnesium": 12,
    "iron": 5.8,
    "zinc": 2.4,
    "copper": 0.04,
    "vitaminA": 46,
    "vitaminC": 0,
    "thiamin": 0.07,
    "riboflavin": 0.28,
    "servingSize": null
  },
  {
    "id": "f320",
    "name": "Beef, meat",
    "category": "Meat",
    "refuse": 0,
    "water": 67.4,
    "energy": 186,
    "protein": 19.6,
    "fat": 11.9,
    "ash": 1.1,
    "fiber": 0,
    "carbohydrate": 0,
    "sodium": 72,
    "potassium": 375,
    "calcium": 15,
    "phosphorus": 171,
    "magnesium": 22,
    "iron": 3.4,
    "zinc": 4.8,
    "copper": 0.1,
    "vitaminA": 5,
    "vitaminC": 0,
    "thiamin": 0.07,
    "riboflavin": 0.11,
    "servingSize": null
  },
  {
    "id": "f321",
    "name": "Beef, meat ground",
    "category": "Meat",
    "refuse": 0,
    "water": 65,
    "energy": 216,
    "protein": 18,
    "fat": 16,
    "ash": 1,
    "fiber": 0,
    "carbohydrate": 0,
    "sodium": 69,
    "potassium": 210,
    "calcium": 14,
    "phosphorus": 180,
    "magnesium": 17,
    "iron": 2.7,
    "zinc": 4,
    "copper": 0.11,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f322",
    "name": "Beef, meat (Kofta, fried)",
    "category": "Meat",
    "refuse": 0,
    "water": 60,
    "energy": 245,
    "protein": 17.5,
    "fat": 18.1,
    "ash": 1.3,
    "fiber": 0.2,
    "carbohydrate": 2.9,
    "sodium": 418,
    "potassium": 155,
    "calcium": 12,
    "phosphorus": 196,
    "magnesium": null,
    "iron": 2.5,
    "zinc": 0.95,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f323",
    "name": "Beef, meat (Kofta, grilled)",
    "category": "Meat",
    "refuse": 0,
    "water": 52,
    "energy": 271,
    "protein": 21.2,
    "fat": 18.2,
    "ash": 3.1,
    "fiber": 0,
    "carbohydrate": 5.5,
    "sodium": 525,
    "potassium": 122,
    "calcium": 35,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.43,
    "zinc": 2.75,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f324",
    "name": "Beef, meat (Kofta/rice)",
    "category": "Meat",
    "refuse": 0,
    "water": 70,
    "energy": 149,
    "protein": 10.6,
    "fat": 7,
    "ash": 0.8,
    "fiber": 0.6,
    "carbohydrate": 11,
    "sodium": 418,
    "potassium": 122,
    "calcium": 20,
    "phosphorus": 122,
    "magnesium": null,
    "iron": 1.7,
    "zinc": 1.9,
    "copper": 0.09,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f325",
    "name": "Beef meat (Kubeba, fried)",
    "category": "Meat",
    "refuse": 0,
    "water": 48.2,
    "energy": 237,
    "protein": 11.5,
    "fat": 8.8,
    "ash": 2.8,
    "fiber": 0.7,
    "carbohydrate": 28,
    "sodium": 628,
    "potassium": 290,
    "calcium": 9,
    "phosphorus": null,
    "magnesium": 17,
    "iron": 2.5,
    "zinc": 1.8,
    "copper": 0.14,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f326",
    "name": "Beef meat (brown sauce)",
    "category": "Meat",
    "refuse": 0,
    "water": 72.1,
    "energy": 159,
    "protein": 14,
    "fat": 11,
    "ash": 1.7,
    "fiber": 0.3,
    "carbohydrate": 0.9,
    "sodium": 412,
    "potassium": 315,
    "calcium": 20,
    "phosphorus": 155,
    "magnesium": null,
    "iron": 1.6,
    "zinc": 2.2,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f327",
    "name": "Beef, meat corned (canned)",
    "category": "Meat",
    "refuse": 0,
    "water": 60,
    "energy": 213,
    "protein": 24.8,
    "fat": 11.8,
    "ash": 1.5,
    "fiber": 0,
    "carbohydrate": 1.9,
    "sodium": 880,
    "potassium": 152,
    "calcium": 12,
    "phosphorus": 150,
    "magnesium": 17,
    "iron": 3.3,
    "zinc": 4.5,
    "copper": 0.26,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.02,
    "riboflavin": 0.18,
    "servingSize": null
  },
  {
    "id": "f328",
    "name": "Beef, sausage",
    "category": "Meat",
    "refuse": 0,
    "water": 50,
    "energy": 312,
    "protein": 15,
    "fat": 24,
    "ash": 2,
    "fiber": 0,
    "carbohydrate": 9,
    "sodium": 810,
    "potassium": 175,
    "calcium": 35,
    "phosphorus": 172,
    "magnesium": 16,
    "iron": 1.4,
    "zinc": 1.6,
    "copper": 0.16,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.05,
    "riboflavin": 0.11,
    "servingSize": null
  },
  {
    "id": "f329",
    "name": "Beef, sausage grilled",
    "category": "Meat",
    "refuse": 0,
    "water": 48,
    "energy": 291,
    "protein": 17,
    "fat": 18.5,
    "ash": 2.5,
    "fiber": 0,
    "carbohydrate": 14,
    "sodium": 1090,
    "potassium": 180,
    "calcium": 45,
    "phosphorus": 210,
    "magnesium": 17,
    "iron": 1.8,
    "zinc": 1.7,
    "copper": 0.26,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f330",
    "name": "Beef, spleen",
    "category": "Meat",
    "refuse": 0,
    "water": 77,
    "energy": 97,
    "protein": 18,
    "fat": 2.1,
    "ash": 1.4,
    "fiber": 0,
    "carbohydrate": 1.5,
    "sodium": 108,
    "potassium": 415,
    "calcium": 9,
    "phosphorus": 218,
    "magnesium": 11,
    "iron": 10.6,
    "zinc": 2.4,
    "copper": 0.12,
    "vitaminA": 0.12,
    "vitaminC": 0.34,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f331",
    "name": "Beef, tongue",
    "category": "Meat",
    "refuse": 0,
    "water": 67,
    "energy": 201,
    "protein": 16.8,
    "fat": 14.6,
    "ash": 1,
    "fiber": 0,
    "carbohydrate": 0.6,
    "sodium": 88,
    "potassium": 304,
    "calcium": 10,
    "phosphorus": 174,
    "magnesium": 12,
    "iron": 4.6,
    "zinc": 3.1,
    "copper": 0.05,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.12,
    "riboflavin": 0.28,
    "servingSize": null
  },
  {
    "id": "f332",
    "name": "Beef, tongue boiled",
    "category": "Meat",
    "refuse": 11,
    "water": 52,
    "energy": 298,
    "protein": 23.4,
    "fat": 22.1,
    "ash": 1.2,
    "fiber": 0,
    "carbohydrate": 1.3,
    "sodium": 124,
    "potassium": 131,
    "calcium": 25,
    "phosphorus": 201,
    "magnesium": 16,
    "iron": 3,
    "zinc": 2,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f333",
    "name": "Buffalo, meat",
    "category": "Meat",
    "refuse": 0,
    "water": 65.4,
    "energy": 210,
    "protein": 18.8,
    "fat": 15,
    "ash": 0.8,
    "fiber": 0,
    "carbohydrate": 0,
    "sodium": 63,
    "potassium": 370,
    "calcium": 25,
    "phosphorus": 180,
    "magnesium": 30,
    "iron": 4.2,
    "zinc": 3.6,
    "copper": 0.24,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": 0.21,
    "riboflavin": 0.35,
    "servingSize": null
  },
  {
    "id": "f334",
    "name": "Camel, liver",
    "category": "Meat",
    "refuse": 0,
    "water": 68,
    "energy": 143,
    "protein": 20.7,
    "fat": 4.4,
    "ash": 1.7,
    "fiber": 0,
    "carbohydrate": 5.2,
    "sodium": 37,
    "potassium": 150,
    "calcium": 35,
    "phosphorus": null,
    "magnesium": 13,
    "iron": 7,
    "zinc": 2.2,
    "copper": 0.9,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
  "id": "f335",
  "name": "Camel, meat",
  "category": "Meat",
  "refuse": 0,
  "water": 76.2,
  "energy": 108,
  "protein": 19,
  "fat": 3.6,
  "ash": 1.2,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 187,
  "potassium": 347,
  "calcium": 15,
  "phosphorus": 160,
  "magnesium": 12,
  "iron": 7.8,
  "zinc": 8.2,
  "copper": 1.22,
  "vitaminA": 8,
  "vitaminC": 0,
  "thiamin": 0.38,
  "riboflavin": 0.76,
  "servingSize": null
},
{
  "id": "f336",
  "name": "Chicken, frankfurter",
  "category": "Meat",
  "refuse": 0,
  "water": 59.5,
  "energy": 221,
  "protein": 18.1,
  "fat": 15,
  "ash": 3,
  "fiber": 1.1,
  "carbohydrate": 3.3,
  "sodium": 995,
  "potassium": 258,
  "calcium": 18,
  "phosphorus": 164,
  "magnesium": null,
  "iron": 1.75,
  "zinc": 1.87,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f337",
  "name": "Chicken, gizzard",
  "category": "Meat",
  "refuse": 0,
  "water": 75,
  "energy": 106,
  "protein": 20.5,
  "fat": 2.3,
  "ash": 1.5,
  "fiber": 0,
  "carbohydrate": 0.7,
  "sodium": 65,
  "potassium": 240,
  "calcium": 10,
  "phosphorus": 105,
  "magnesium": null,
  "iron": 2.9,
  "zinc": null,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": 0.02,
  "riboflavin": 0.2,
  "servingSize": null
},
{
  "id": "f338",
  "name": "Chicken, liver",
  "category": "Meat",
  "refuse": 0,
  "water": 73.6,
  "energy": 122,
  "protein": 17.6,
  "fat": 4.6,
  "ash": 1.7,
  "fiber": 0,
  "carbohydrate": 2.5,
  "sodium": 45,
  "potassium": 166,
  "calcium": 20,
  "phosphorus": 240,
  "magnesium": 13,
  "iron": 10.5,
  "zinc": 3.5,
  "copper": 0.5,
  "vitaminA": 7500,
  "vitaminC": 20,
  "thiamin": 0.35,
  "riboflavin": 2.8,
  "servingSize": null
},
{
  "id": "f339",
  "name": "Chicken, luncheon",
  "category": "Meat",
  "refuse": 0,
  "water": 67.5,
  "energy": 144,
  "protein": 18,
  "fat": 5.5,
  "ash": 2,
  "fiber": 1.5,
  "carbohydrate": 5.5,
  "sodium": 1003,
  "potassium": 300,
  "calcium": 20,
  "phosphorus": null,
  "magnesium": null,
  "iron": 1.06,
  "zinc": 0.91,
  "copper": null,
  "vitaminA": "T",
  "vitaminC": 0,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f340",
  "name": "Chicken, boiled",
  "category": "Meat",
  "refuse": 30,
  "water": 65.2,
  "energy": 193,
  "protein": 21.6,
  "fat": 11.7,
  "ash": 1.2,
  "fiber": 0,
  "carbohydrate": 0.3,
  "sodium": 91,
  "potassium": 206,
  "calcium": 14,
  "phosphorus": 190,
  "magnesium": 26,
  "iron": 2.3,
  "zinc": 2.1,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f341",
  "name": "Chicken, fried",
  "category": "Meat",
  "refuse": 0,
  "water": 46.8,
  "energy": 316,
  "protein": 22.4,
  "fat": 22.2,
  "ash": 2,
  "fiber": 0,
  "carbohydrate": 6.6,
  "sodium": 540,
  "potassium": 348,
  "calcium": 15,
  "phosphorus": null,
  "magnesium": null,
  "iron": 1.23,
  "zinc": 1.37,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f342",
  "name": "Chicken, grilled",
  "category": "Meat",
  "refuse": 40,
  "water": 63,
  "energy": 213,
  "protein": 21.2,
  "fat": 13.8,
  "ash": 1.1,
  "fiber": 0,
  "carbohydrate": 0.9,
  "sodium": 86,
  "potassium": 311,
  "calcium": 16,
  "phosphorus": 180,
  "magnesium": 25,
  "iron": 2,
  "zinc": 2.2,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f343",
  "name": "Chicken, meat (dark)",
  "category": "Meat",
  "refuse": 0,
  "water": 75.1,
  "energy": 121,
  "protein": 19,
  "fat": 5,
  "ash": 0.9,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 67,
  "potassium": 250,
  "calcium": 12,
  "phosphorus": 186,
  "magnesium": 17,
  "iron": 1.6,
  "zinc": 2.12,
  "copper": null,
  "vitaminA": "T",
  "vitaminC": 0,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f344",
  "name": "Chicken, meat (tight)",
  "category": "Meat",
  "refuse": 0,
  "water": 75.6,
  "energy": 109,
  "protein": 20.5,
  "fat": 3,
  "ash": 0.9,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 58,
  "potassium": 330,
  "calcium": 11,
  "phosphorus": 214,
  "magnesium": 19,
  "iron": 1.1,
  "zinc": 0.69,
  "copper": null,
  "vitaminA": "T",
  "vitaminC": null,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f345",
  "name": "Chicken, meat and skin",
  "category": "Meat",
  "refuse": 36,
  "water": 65.6,
  "energy": 214,
  "protein": 17.4,
  "fat": 16,
  "ash": 1,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 77,
  "potassium": 340,
  "calcium": 11,
  "phosphorus": 194,
  "magnesium": 20,
  "iron": 1.6,
  "zinc": 2,
  "copper": 0.15,
  "vitaminA": 22,
  "vitaminC": 0,
  "thiamin": 0.13,
  "riboflavin": 0.18,
  "servingSize": null
},
{
  "id": "f346",
  "name": "Duck, meat and skin",
  "category": "Meat",
  "refuse": 32,
  "water": 55.2,
  "energy": 315,
  "protein": 15.4,
  "fat": 28.2,
  "ash": 1.2,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 90,
  "potassium": 312,
  "calcium": 12,
  "phosphorus": 194,
  "magnesium": 18,
  "iron": 2.4,
  "zinc": 2.9,
  "copper": 0.35,
  "vitaminA": null,
  "vitaminC": 0,
  "thiamin": 0.08,
  "riboflavin": 0.25,
  "servingSize": null
},
{
  "id": "f347",
  "name": "Goat, meat",
  "category": "Meat",
  "refuse": 0,
  "water": 70.3,
  "energy": 165,
  "protein": 18.4,
  "fat": 10.2,
  "ash": 1.1,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 53,
  "potassium": 286,
  "calcium": 12,
  "phosphorus": 150,
  "magnesium": 14,
  "iron": 2.3,
  "zinc": 2.4,
  "copper": 0.08,
  "vitaminA": null,
  "vitaminC": 0,
  "thiamin": 0.18,
  "riboflavin": 0.26,
  "servingSize": null
},
{
  "id": "f348",
  "name": "Goose, meat and skin",
  "category": "Meat",
  "refuse": 30,
  "water": 51.5,
  "energy": 346,
  "protein": 16,
  "fat": 31.3,
  "ash": 1.2,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 80,
  "potassium": 440,
  "calcium": 18,
  "phosphorus": 190,
  "magnesium": 25,
  "iron": 1.5,
  "zinc": 1.7,
  "copper": 0.26,
  "vitaminA": 21,
  "vitaminC": 0,
  "thiamin": 0.08,
  "riboflavin": 0.25,
  "servingSize": null
},
{
  "id": "f349",
  "name": "Lamb, kidneys",
  "category": "Meat",
  "refuse": 0,
  "water": 75,
  "energy": 109,
  "protein": 16,
  "fat": 5,
  "ash": 1,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 168,
  "potassium": 217,
  "calcium": 10,
  "phosphorus": 245,
  "magnesium": 12,
  "iron": 8.4,
  "zinc": 2.5,
  "copper": 0.42,
  "vitaminA": 100,
  "vitaminC": 8,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f350",
  "name": "Lamb, liver",
  "category": "Meat",
  "refuse": 0,
  "water": 70.7,
  "energy": 126,
  "protein": 19.9,
  "fat": 2.8,
  "ash": 1.4,
  "fiber": 0,
  "carbohydrate": 5.2,
  "sodium": 63,
  "potassium": 257,
  "calcium": 4,
  "phosphorus": 354,
  "magnesium": 12,
  "iron": 8.4,
  "zinc": 3.99,
  "copper": 1.4,
  "vitaminA": 2000,
  "vitaminC": 10,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f351",
  "name": "Lamb, meat",
  "category": "Meat",
  "refuse": 0,
  "water": 62.2,
  "energy": 249,
  "protein": 16.5,
  "fat": 20.3,
  "ash": 1,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 65,
  "potassium": 300,
  "calcium": 15,
  "phosphorus": 163,
  "magnesium": 15,
  "iron": 2.5,
  "zinc": 2.7,
  "copper": 0.6,
  "vitaminA": 9,
  "vitaminC": 0,
  "thiamin": 0.12,
  "riboflavin": 0.19,
  "servingSize": null
},
{
  "id": "f352",
  "name": "Lamb, meat (Kabab)",
  "category": "Meat",
  "refuse": 0,
  "water": 52.8,
  "energy": 273,
  "protein": 23,
  "fat": 19.3,
  "ash": 3.1,
  "fiber": 0,
  "carbohydrate": 1.8,
  "sodium": 505,
  "potassium": 383,
  "calcium": 22,
  "phosphorus": 220,
  "magnesium": 32,
  "iron": 3,
  "zinc": 3.1,
  "copper": null,
  "vitaminA": "T",
  "vitaminC": 0,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f353",
  "name": "Pigeon, meat and skin",
  "category": "Meat",
  "refuse": 25,
  "water": 59.6,
  "energy": 251,
  "protein": 20,
  "fat": 19,
  "ash": 1.4,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 58,
  "potassium": 275,
  "calcium": 27,
  "phosphorus": 276,
  "magnesium": 20,
  "iron": 2.2,
  "zinc": 2.4,
  "copper": 0.31,
  "vitaminA": null,
  "vitaminC": 0,
  "thiamin": 0.1,
  "riboflavin": 0.18,
  "servingSize": null
},
{
  "id": "f354",
  "name": "Pork, meat",
  "category": "Meat",
  "refuse": 0,
  "water": 51.1,
  "energy": 354,
  "protein": 15.9,
  "fat": 32.3,
  "ash": 0.7,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 62,
  "potassium": 315,
  "calcium": 10,
  "phosphorus": 172,
  "magnesium": 16,
  "iron": 1.9,
  "zinc": 1.8,
  "copper": 0.12,
  "vitaminA": null,
  "vitaminC": 0,
  "thiamin": 0.65,
  "riboflavin": 0.18,
  "servingSize": null
},
{
  "id": "f355",
  "name": "Rabbit, meat",
  "category": "Meat",
  "refuse": 36,
  "water": 72.4,
  "energy": 133,
  "protein": 20.7,
  "fat": 5.6,
  "ash": 1.3,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 72,
  "potassium": 345,
  "calcium": 15,
  "phosphorus": 234,
  "magnesium": 30,
  "iron": 1.6,
  "zinc": 1.6,
  "copper": 0.22,
  "vitaminA": 5,
  "vitaminC": 0,
  "thiamin": 0.08,
  "riboflavin": 0.16,
  "servingSize": null
},
{
  "id": "f356",
  "name": "Turkey, frankfurter",
  "category": "Meat",
  "refuse": 0,
  "water": 61.5,
  "energy": 187,
  "protein": 19,
  "fat": 9.9,
  "ash": 3,
  "fiber": 1.1,
  "carbohydrate": 5.5,
  "sodium": 1009,
  "potassium": 274,
  "calcium": 73,
  "phosphorus": null,
  "magnesium": 20,
  "iron": 1.15,
  "zinc": 1.23,
  "copper": 0.07,
  "vitaminA": "T",
  "vitaminC": 0,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f357",
  "name": "Turkey, luncheon",
  "category": "Meat",
  "refuse": 0,
  "water": 68,
  "energy": 129,
  "protein": 20,
  "fat": 3.5,
  "ash": 3,
  "fiber": 1.1,
  "carbohydrate": 4.4,
  "sodium": 996,
  "potassium": 315,
  "calcium": 87,
  "phosphorus": null,
  "magnesium": 34,
  "iron": 0.96,
  "zinc": 1.33,
  "copper": null,
  "vitaminA": "T",
  "vitaminC": 0,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f358",
  "name": "Turkey, meat and skin",
  "category": "Meat",
  "refuse": 30,
  "water": 70.4,
  "energy": 151,
  "protein": 21,
  "fat": 7.4,
  "ash": 1.2,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 70,
  "potassium": 321,
  "calcium": 15,
  "phosphorus": 220,
  "magnesium": 20,
  "iron": 0.99,
  "zinc": 1.6,
  "copper": 0.18,
  "vitaminA": "T",
  "vitaminC": 0,
  "thiamin": 0.11,
  "riboflavin": 0.12,
  "servingSize": null
},
{
  "id": "f359",
  "name": "Veal, meat",
  "category": "Meat",
  "refuse": 0,
  "water": 75.1,
  "energy": 123,
  "protein": 18.7,
  "fat": 5.4,
  "ash": 0.8,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 103,
  "potassium": 372,
  "calcium": 10,
  "phosphorus": 253,
  "magnesium": 27,
  "iron": 2,
  "zinc": 2.6,
  "copper": 0.03,
  "vitaminA": 3,
  "vitaminC": 0,
  "thiamin": 0.1,
  "riboflavin": 0.2,
  "servingSize": null
},
{
  "id": "f360",
  "name": "Eggs, chicken, whole",
  "category": "Eggs",
  "refuse": 12,
  "water": 75.2,
  "energy": 149,
  "protein": 12.6,
  "fat": 10.8,
  "ash": 1.1,
  "fiber": 0,
  "carbohydrate": 0.3,
  "sodium": 155,
  "potassium": 130,
  "calcium": 62,
  "phosphorus": 218,
  "magnesium": 15,
  "iron": 2.5,
  "zinc": 1.5,
  "copper": 0.14,
  "vitaminA": 128,
  "vitaminC": 0,
  "thiamin": 0.14,
  "riboflavin": 0.36,
  "servingSize": null
},
{
  "id": "f361",
  "name": "Eggs, chicken, white",
  "category": "Eggs",
  "refuse": 0,
  "water": 88,
  "energy": 46,
  "protein": 11,
  "fat": 0.1,
  "ash": 0.7,
  "fiber": 0,
  "carbohydrate": 0.2,
  "sodium": 172,
  "potassium": 133,
  "calcium": 10,
  "phosphorus": 22,
  "magnesium": 10,
  "iron": 0.15,
  "zinc": 0.03,
  "copper": 0.04,
  "vitaminA": 0,
  "vitaminC": 0,
  "thiamin": 0.02,
  "riboflavin": 0.21,
  "servingSize": null
},
{
  "id": "f362",
  "name": "Eggs, chicken, yolk",
  "category": "Eggs",
  "refuse": 0,
  "water": 50.4,
  "energy": 343,
  "protein": 16.5,
  "fat": 30.2,
  "ash": 1.7,
  "fiber": 0,
  "carbohydrate": 1.2,
  "sodium": 4,
  "potassium": 89,
  "calcium": 150,
  "phosphorus": 586,
  "magnesium": 18,
  "iron": 5,
  "zinc": 4,
  "copper": 0.02,
  "vitaminA": 384,
  "vitaminC": 0,
  "thiamin": 0.28,
  "riboflavin": 0.55,
  "servingSize": null
},
{
  "id": "f363",
  "name": "Eggs, duck, whole",
  "category": "Eggs",
  "refuse": 10,
  "water": 71.2,
  "energy": 177,
  "protein": 13.5,
  "fat": 13.3,
  "ash": 1.3,
  "fiber": 0,
  "carbohydrate": 0.7,
  "sodium": 115,
  "potassium": 233,
  "calcium": 58,
  "phosphorus": 217,
  "magnesium": 18,
  "iron": 3.5,
  "zinc": 1.4,
  "copper": 0.03,
  "vitaminA": 178,
  "vitaminC": 0,
  "thiamin": 0.14,
  "riboflavin": 0.36,
  "servingSize": null
},
{
  "id": "f364",
  "name": "Omelet (Ijaa)",
  "category": "Eggs",
  "refuse": 0,
  "water": 74.1,
  "energy": 148,
  "protein": 9.9,
  "fat": 10.5,
  "ash": 1.5,
  "fiber": 0.5,
  "carbohydrate": 3.5,
  "sodium": 452,
  "potassium": 143,
  "calcium": 42,
  "phosphorus": 162,
  "magnesium": null,
  "iron": 1.5,
  "zinc": 0.63,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f365",
  "name": "Bream",
  "category": "Fish and Fish-based Foods",
  "refuse": 49,
  "water": 76.5,
  "energy": 100,
  "protein": 19.4,
  "fat": 2.4,
  "ash": 1.4,
  "fiber": 0,
  "carbohydrate": 0.3,
  "sodium": 55,
  "potassium": 180,
  "calcium": 127,
  "phosphorus": 101,
  "magnesium": 55,
  "iron": 1.21,
  "zinc": 0.4,
  "copper": 0.21,
  "vitaminA": 21,
  "vitaminC": 0,
  "thiamin": 0.02,
  "riboflavin": 0.14,
  "servingSize": null
},
{
  "id": "f366",
  "name": "Bream, golden",
  "category": "Fish and Fish-based Foods",
  "refuse": 32,
  "water": 79.4,
  "energy": 79,
  "protein": 17.6,
  "fat": 1,
  "ash": 2,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 64,
  "potassium": 215,
  "calcium": 128,
  "phosphorus": 186,
  "magnesium": 33,
  "iron": 0.94,
  "zinc": 1,
  "copper": 0.18,
  "vitaminA": 18,
  "vitaminC": 0,
  "thiamin": 0.16,
  "riboflavin": 0.15,
  "servingSize": null
},
{
  "id": "f367",
  "name": "Catfish, bayad",
  "category": "Fish and Fish-based Foods",
  "refuse": 36,
  "water": 77.4,
  "energy": 102,
  "protein": 18.7,
  "fat": 3,
  "ash": 0.9,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 62,
  "potassium": 155,
  "calcium": 116,
  "phosphorus": 318,
  "magnesium": 60,
  "iron": 1.05,
  "zinc": 0.6,
  "copper": 0.23,
  "vitaminA": 13,
  "vitaminC": 2,
  "thiamin": 0.1,
  "riboflavin": 0.21,
  "servingSize": null
},
{
  "id": "f368",
  "name": "Crab",
  "category": "Fish and Fish-based Foods",
  "refuse": 83,
  "water": 77,
  "energy": 96,
  "protein": 18,
  "fat": 1.5,
  "ash": 0.9,
  "fiber": 0,
  "carbohydrate": 2.6,
  "sodium": 85,
  "potassium": 209,
  "calcium": 53,
  "phosphorus": 225,
  "magnesium": null,
  "iron": 1.5,
  "zinc": 4.5,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": 0.5,
  "riboflavin": 0.1,
  "servingSize": null
},
{
  "id": "f369",
  "name": "Eel, fish",
  "category": "Fish and Fish-based Foods",
  "refuse": 50,
  "water": 70,
  "energy": 166,
  "protein": 19,
  "fat": 10,
  "ash": 1,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 70,
  "potassium": 220,
  "calcium": 30,
  "phosphorus": 210,
  "magnesium": null,
  "iron": 0.8,
  "zinc": 0.5,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": 0.2,
  "riboflavin": 0.25,
  "servingSize": null
},
{
  "id": "f370",
  "name": "Fish finger, fried",
  "category": "Fish and Fish-based Foods",
  "refuse": 0,
  "water": 52,
  "energy": 252,
  "protein": 12,
  "fat": 13.4,
  "ash": 1.7,
  "fiber": 0,
  "carbohydrate": 20.9,
  "sodium": 490,
  "potassium": 275,
  "calcium": 74,
  "phosphorus": 222,
  "magnesium": null,
  "iron": 1.01,
  "zinc": 0.4,
  "copper": 0.04,
  "vitaminA": "T",
  "vitaminC": null,
  "thiamin": 0.09,
  "riboflavin": 0.07,
  "servingSize": null
},
{
  "id": "f371",
  "name": "Herring smoked",
  "category": "Fish and Fish-based Foods",
  "refuse": 35,
  "water": 62,
  "energy": 200,
  "protein": 20,
  "fat": 12.9,
  "ash": 4.2,
  "fiber": 0,
  "carbohydrate": 0.9,
  "sodium": 1056,
  "potassium": 257,
  "calcium": 66,
  "phosphorus": 254,
  "magnesium": 43,
  "iron": 1.53,
  "zinc": 1.2,
  "copper": 0.2,
  "vitaminA": 15,
  "vitaminC": 0,
  "thiamin": 0.02,
  "riboflavin": 0.12,
  "servingSize": null
},
{
  "id": "f372",
  "name": "Lizard fish",
  "category": "Fish and Fish-based Foods",
  "refuse": 49,
  "water": 76.8,
  "energy": 95,
  "protein": 19.4,
  "fat": 1.5,
  "ash": 1.4,
  "fiber": 0,
  "carbohydrate": 0.9,
  "sodium": 66,
  "potassium": 243,
  "calcium": 181,
  "phosphorus": 227,
  "magnesium": 24,
  "iron": 0.83,
  "zinc": 1.2,
  "copper": 0.16,
  "vitaminA": 0,
  "vitaminC": 0,
  "thiamin": 0.14,
  "riboflavin": 0.16,
  "servingSize": null
},
{
  "id": "f373",
  "name": "Lizard fish, fried",
  "category": "Fish and Fish-based Foods",
  "refuse": 20,
  "water": 53,
  "energy": 248,
  "protein": 23.2,
  "fat": 14.4,
  "ash": 2.9,
  "fiber": 0,
  "carbohydrate": 6.5,
  "sodium": 475,
  "potassium": 250,
  "calcium": 128,
  "phosphorus": 292,
  "magnesium": 45,
  "iron": 0.9,
  "zinc": 1.2,
  "copper": 0.18,
  "vitaminA": 0,
  "vitaminC": 0,
  "thiamin": 0.08,
  "riboflavin": 0.01,
  "servingSize": null
},
{
  "id": "f374",
  "name": "Mackerel, canned",
  "category": "Fish and Fish-based Foods",
  "refuse": 0,
  "water": 62.7,
  "energy": 215,
  "protein": 20,
  "fat": 15,
  "ash": 2.2,
  "fiber": 0,
  "carbohydrate": 0.1,
  "sodium": 152,
  "potassium": 349,
  "calcium": 10,
  "phosphorus": 283,
  "magnesium": 29,
  "iron": 1.93,
  "zinc": 0.61,
  "copper": 0.05,
  "vitaminA": 38,
  "vitaminC": 0,
  "thiamin": 0.09,
  "riboflavin": 0.29,
  "servingSize": null
},
{
  "id": "f375",
  "name": "Mullet",
  "category": "Fish and Fish-based Foods",
  "refuse": 47,
  "water": 73.4,
  "energy": 133,
  "protein": 19,
  "fat": 6.3,
  "ash": 1.3,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 88,
  "potassium": 280,
  "calcium": 21,
  "phosphorus": 208,
  "magnesium": 18,
  "iron": 1.3,
  "zinc": 1.53,
  "copper": 0.19,
  "vitaminA": 24,
  "vitaminC": 1,
  "thiamin": 0.07,
  "riboflavin": 0.15,
  "servingSize": null
},
{
  "id": "f376",
  "name": "Mullet, grilled",
  "category": "Fish and Fish-based Foods",
  "refuse": 42,
  "water": 69.1,
  "energy": 151,
  "protein": 21.5,
  "fat": 6.7,
  "ash": 1.5,
  "fiber": 0,
  "carbohydrate": 1.2,
  "sodium": 100,
  "potassium": 195,
  "calcium": 37,
  "phosphorus": 209,
  "magnesium": 30,
  "iron": 1.2,
  "zinc": 0.9,
  "copper": 0.21,
  "vitaminA": 31,
  "vitaminC": 0,
  "thiamin": 0.05,
  "riboflavin": 0.09,
  "servingSize": null
},
{
  "id": "f377",
  "name": "Mullet, salted",
  "category": "Fish and Fish-based Foods",
  "refuse": 40,
  "water": 62.8,
  "energy": 154,
  "protein": 20.8,
  "fat": 7.8,
  "ash": 8.5,
  "fiber": 0,
  "carbohydrate": 0.1,
  "sodium": 1805,
  "potassium": 200,
  "calcium": 38,
  "phosphorus": 205,
  "magnesium": 25,
  "iron": 1.21,
  "zinc": 1,
  "copper": 0.2,
  "vitaminA": 14,
  "vitaminC": 0,
  "thiamin": 0.05,
  "riboflavin": 0.12,
  "servingSize": null
},
{
  "id": "f378",
  "name": "Sardines",
  "category": "Fish and Fish-based Foods",
  "refuse": 44,
  "water": 70.6,
  "energy": 156,
  "protein": 19.2,
  "fat": 8.8,
  "ash": 1.4,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 91,
  "potassium": 212,
  "calcium": 34,
  "phosphorus": 210,
  "magnesium": 20,
  "iron": 0.92,
  "zinc": 0.8,
  "copper": 0.16,
  "vitaminA": 13,
  "vitaminC": 0,
  "thiamin": 0.11,
  "riboflavin": 0.08,
  "servingSize": null
},
{
  "id": "f379",
  "name": "Sardines, canned",
  "category": "Fish and Fish-based Foods",
  "refuse": 10,
  "water": 58.8,
  "energy": 227,
  "protein": 22,
  "fat": 15.4,
  "ash": 3.7,
  "fiber": 0,
  "carbohydrate": 0.1,
  "sodium": 630,
  "potassium": 257,
  "calcium": 320,
  "phosphorus": 354,
  "magnesium": 28,
  "iron": 3,
  "zinc": 2.9,
  "copper": 0.19,
  "vitaminA": 15,
  "vitaminC": 0,
  "thiamin": 0.04,
  "riboflavin": 0.25,
  "servingSize": null
},
{
  "id": "f380",
  "name": "Sardine, grilled",
  "category": "Fish and Fish-based Foods",
  "refuse": 40,
  "water": 64.1,
  "energy": 180,
  "protein": 22.5,
  "fat": 9.2,
  "ash": 2.5,
  "fiber": 0,
  "carbohydrate": 1.7,
  "sodium": 107,
  "potassium": 220,
  "calcium": 86,
  "phosphorus": 220,
  "magnesium": 36,
  "iron": 1,
  "zinc": 0.9,
  "copper": 0.18,
  "vitaminA": 16,
  "vitaminC": 0,
  "thiamin": 0.07,
  "riboflavin": 0.07,
  "servingSize": null
},
{
  "id": "f381",
  "name": "Sardines, salted",
  "category": "Fish and Fish-based Foods",
  "refuse": 34,
  "water": 56.6,
  "energy": 218,
  "protein": 23.3,
  "fat": 13.5,
  "ash": 5.8,
  "fiber": 0,
  "carbohydrate": 0.8,
  "sodium": 1440,
  "potassium": 307,
  "calcium": 45,
  "phosphorus": 383,
  "magnesium": 30,
  "iron": 1.23,
  "zinc": 1.2,
  "copper": 0.18,
  "vitaminA": 15,
  "vitaminC": 0,
  "thiamin": 0.06,
  "riboflavin": 0.06,
  "servingSize": null
},
{
  "id": "f382",
  "name": "Shrimp",
  "category": "Fish and Fish-based Foods",
  "refuse": 65,
  "water": 77.8,
  "energy": 88,
  "protein": 19.5,
  "fat": 0.9,
  "ash": 1.3,
  "fiber": 0,
  "carbohydrate": 0.5,
  "sodium": 125,
  "potassium": 205,
  "calcium": 57,
  "phosphorus": 152,
  "magnesium": 40,
  "iron": 1.41,
  "zinc": 2.08,
  "copper": 0.06,
  "vitaminA": 15,
  "vitaminC": 0,
  "thiamin": 0.02,
  "riboflavin": 0.1,
  "servingSize": null
},
{
  "id": "f383",
  "name": "Shrimp, boiled",
  "category": "Fish and Fish-based Foods",
  "refuse": 0,
  "water": 70.4,
  "energy": 116,
  "protein": 25,
  "fat": 1.1,
  "ash": 2,
  "fiber": 0,
  "carbohydrate": 1.5,
  "sodium": 77,
  "potassium": 250,
  "calcium": 65,
  "phosphorus": null,
  "magnesium": null,
  "iron": 1.42,
  "zinc": 2.9,
  "copper": 0.11,
  "vitaminA": 20,
  "vitaminC": 0,
  "thiamin": 0.04,
  "riboflavin": 0.09,
  "servingSize": null
},
{
  "id": "f384",
  "name": "Sole",
  "category": "Fish and Fish-based Foods",
  "refuse": 42,
  "water": 78.5,
  "energy": 88,
  "protein": 18,
  "fat": 1.6,
  "ash": 1.6,
  "fiber": 0,
  "carbohydrate": 0.3,
  "sodium": 85,
  "potassium": 210,
  "calcium": 15,
  "phosphorus": 158,
  "magnesium": 38,
  "iron": 0.9,
  "zinc": 1,
  "copper": 0.05,
  "vitaminA": 27,
  "vitaminC": 0,
  "thiamin": 0.11,
  "riboflavin": 0.18,
  "servingSize": null
},
{
  "id": "f385",
  "name": "Sole, fried",
  "category": "Fish and Fish-based Foods",
  "refuse": 33,
  "water": 50.5,
  "energy": 262,
  "protein": 21.5,
  "fat": 15.2,
  "ash": 3,
  "fiber": 0,
  "carbohydrate": 9.8,
  "sodium": 229,
  "potassium": 225,
  "calcium": 16,
  "phosphorus": null,
  "magnesium": null,
  "iron": 1.1,
  "zinc": 1.5,
  "copper": 0.12,
  "vitaminA": 31,
  "vitaminC": 0,
  "thiamin": 0.06,
  "riboflavin": 0.1,
  "servingSize": null
},
{
  "id": "f386",
  "name": "Tilapia",
  "category": "Fish and Fish-based Foods",
  "refuse": 44,
  "water": 77.8,
  "energy": 94,
  "protein": 19,
  "fat": 2,
  "ash": 1.2,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 85,
  "potassium": 280,
  "calcium": 92,
  "phosphorus": 197,
  "magnesium": 32,
  "iron": 1.9,
  "zinc": 0.8,
  "copper": 0.05,
  "vitaminA": 25,
  "vitaminC": 0,
  "thiamin": 0.08,
  "riboflavin": 0.15,
  "servingSize": null
},
{
  "id": "f387",
  "name": "Tilapia, fried",
  "category": "Fish and Fish-based Foods",
  "refuse": 29,
  "water": 58.4,
  "energy": 233,
  "protein": 20,
  "fat": 15,
  "ash": 2.2,
  "fiber": 0,
  "carbohydrate": 4.4,
  "sodium": 364,
  "potassium": 116,
  "calcium": 72,
  "phosphorus": null,
  "magnesium": null,
  "iron": 1.2,
  "zinc": 1,
  "copper": null,
  "vitaminA": null,
  "vitaminC": null,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f388",
  "name": "Tilapia, grilled",
  "category": "Fish and Fish-based Foods",
  "refuse": 25,
  "water": 71,
  "energy": 121,
  "protein": 22.5,
  "fat": 2.6,
  "ash": 2,
  "fiber": 0,
  "carbohydrate": 1.9,
  "sodium": 95,
  "potassium": 205,
  "calcium": null,
  "phosphorus": 129,
  "magnesium": null,
  "iron": 11,
  "zinc": 0.9,
  "copper": 0.18,
  "vitaminA": 25,
  "vitaminC": 0,
  "thiamin": 0.05,
  "riboflavin": 0.09,
  "servingSize": null
},
{
  "id": "f389",
  "name": "Tuna canned",
  "category": "Fish and Fish-based Foods",
  "refuse": 0,
  "water": 54.2,
  "energy": 267,
  "protein": 24.6,
  "fat": 18.5,
  "ash": 2.3,
  "fiber": 0,
  "carbohydrate": 0.4,
  "sodium": 450,
  "potassium": 285,
  "calcium": 10,
  "phosphorus": 189,
  "magnesium": 31,
  "iron": 2.75,
  "zinc": 0.4,
  "copper": 0.2,
  "vitaminA": 28,
  "vitaminC": 0,
  "thiamin": 0.04,
  "riboflavin": 0.1,
  "servingSize": null
},  
{
  "id": "f390",
  "name": "Butter (unsalted)",
  "category": "Dairy",
  "refuse": 0,
  "water": 16.7,
  "energy": 742,
  "protein": 0.7,
  "fat": 82,
  "ash": 0.3,
  "fiber": 0,
  "carbohydrate": 0.3,
  "sodium": 10,
  "potassium": 25,
  "calcium": 15,
  "phosphorus": 23,
  "magnesium": 2,
  "iron": 0.16,
  "zinc": 0.15,
  "copper": 0.2,
  "vitaminA": 727,
  "vitaminC": 0,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f391",
  "name": "Butter oil (ghee)",
  "category": "Dairy",
  "refuse": 0,
  "water": 0.3,
  "energy": 896,
  "protein": 0.2,
  "fat": 99.5,
  "ash": 0,
  "fiber": 0,
  "carbohydrate": 0,
  "sodium": 0,
  "potassium": 0,
  "calcium": 0,
  "phosphorus": 0,
  "magnesium": 0,
  "iron": 0,
  "zinc": 0,
  "copper": 0,
  "vitaminA": 833,
  "vitaminC": 0,
  "thiamin": 0,
  "riboflavin": 0,
  "servingSize": null
},
{
  "id": "f392",
  "name": "Cheese, camembert",
  "category": "Dairy",
  "refuse": 0,
  "water": 50.5,
  "energy": 301,
  "protein": 20,
  "fat": 23.9,
  "ash": 4.1,
  "fiber": 0,
  "carbohydrate": 1.5,
  "sodium": 1019,
  "potassium": 112,
  "calcium": 400,
  "phosphorus": 287,
  "magnesium": 18,
  "iron": 0.65,
  "zinc": 3.4,
  "copper": 0.09,
  "vitaminA": 280,
  "vitaminC": 0,
  "thiamin": 0.03,
  "riboflavin": 0.69,
  "servingSize": null
},
{
  "id": "f393",
  "name": "Cheese, cheddar",
  "category": "Dairy",
  "refuse": 0,
  "water": 35.8,
  "energy": 406,
  "protein": 26,
  "fat": 32.9,
  "ash": 3.8,
  "fiber": 0,
  "carbohydrate": 1.5,
  "sodium": 725,
  "potassium": 90,
  "calcium": 724,
  "phosphorus": 512,
  "magnesium": 42,
  "iron": 0.95,
  "zinc": 3,
  "copper": 0.03,
  "vitaminA": 305,
  "vitaminC": 0,
  "thiamin": 0.04,
  "riboflavin": 0.46,
  "servingSize": null
},
{
  "id": "f394",
  "name": "Cheese, couda",
  "category": "Dairy",
  "refuse": 0,
  "water": 41,
  "energy": 371,
  "protein": 24.5,
  "fat": 30,
  "ash": 3.7,
  "fiber": 0,
  "carbohydrate": 0.8,
  "sodium": 646,
  "potassium": 160,
  "calcium": 765,
  "phosphorus": 570,
  "magnesium": null,
  "iron": 0.3,
  "zinc": 3.5,
  "copper": null,
  "vitaminA": 250,
  "vitaminC": 0,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f395",
  "name": "Cheese, kareesh",
  "category": "Dairy",
  "refuse": 0,
  "water": 77.5,
  "energy": 100,
  "protein": 13,
  "fat": 3.5,
  "ash": 1.9,
  "fiber": 0,
  "carbohydrate": 4.1,
  "sodium": 130,
  "potassium": 95,
  "calcium": 300,
  "phosphorus": 161,
  "magnesium": 23,
  "iron": 0.41,
  "zinc": 2.4,
  "copper": 0.04,
  "vitaminA": 12,
  "vitaminC": 0,
  "thiamin": 0.03,
  "riboflavin": 0.19,
  "servingSize": null
},
{
  "id": "f396",
  "name": "Cheese, kareesh low fat",
  "category": "Dairy",
  "refuse": 0,
  "water": 76.5,
  "energy": 91,
  "protein": 19,
  "fat": 0.5,
  "ash": 1.3,
  "fiber": 0,
  "carbohydrate": 2.7,
  "sodium": 145,
  "potassium": 115,
  "calcium": 388,
  "phosphorus": 174,
  "magnesium": 28,
  "iron": 0.33,
  "zinc": 2,
  "copper": 0.04,
  "vitaminA": 0,
  "vitaminC": 0,
  "thiamin": 0.02,
  "riboflavin": 0.24,
  "servingSize": null
},
{
  "id": "f397",
  "name": "Cheese, kareesh (Mish)",
  "category": "Dairy",
  "refuse": 0,
  "water": 72.8,
  "energy": 93,
  "protein": 14,
  "fat": 3,
  "ash": 7.8,
  "fiber": 0,
  "carbohydrate": 2.4,
  "sodium": 4500,
  "potassium": 118,
  "calcium": 360,
  "phosphorus": 276,
  "magnesium": 32,
  "iron": 0.5,
  "zinc": 1.44,
  "copper": 0.05,
  "vitaminA": 0,
  "vitaminC": 0,
  "thiamin": 0.1,
  "riboflavin": 0.32,
  "servingSize": null
},
{
  "id": "f398",
  "name": "Cheese, mozzarella",
  "category": "Dairy",
  "refuse": 0,
  "water": 52.3,
  "energy": 265,
  "protein": 27,
  "fat": 16.5,
  "ash": 2,
  "fiber": 0,
  "carbohydrate": 2.2,
  "sodium": 555,
  "potassium": 35,
  "calcium": 724,
  "phosphorus": null,
  "magnesium": null,
  "iron": 0.4,
  "zinc": 2.6,
  "copper": null,
  "vitaminA": null,
  "vitaminC": 0,
  "thiamin": null,
  "riboflavin": null,
  "servingSize": null
},
{
  "id": "f399",
  "name": "Cheese, panaesan",
  "category": "Dairy",
  "refuse": 0,
  "water": 29.5,
  "energy": 399,
  "protein": 35,
  "fat": 27.5,
  "ash": 5.1,
  "fiber": 0,
  "carbohydrate": 2.9,
  "sodium": 982,
  "potassium": 144,
  "calcium": 1016,
  "phosphorus": 715,
  "magnesium": null,
  "iron": 0.4,
  "zinc": 3.54,
  "copper": 0.05,
  "vitaminA": 356,
  "vitaminC": 0,
  "thiamin": 0.04,
  "riboflavin": 0.52,
  "servingSize": null
},
{
  "id": "f400",
  "name": "Cheese, processed",
  "category": "Dairy",
  "refuse": 0,
  "water": 46.9,
  "energy": 324,
  "protein": 19,
  "fat": 25,
  "ash": 3.4,
  "fiber": 0,
  "carbohydrate": 5.7,
  "sodium": 1160,
  "potassium": 93,
  "calcium": 710,
  "phosphorus": 542,
  "magnesium": 30,
  "iron": 0.5,
  "zinc": 2.4,
  "copper": 0.09,
  "vitaminA": 205,
  "vitaminC": 0,
  "thiamin": 0.03,
  "riboflavin": 0.26,
  "servingSize": null
},
{
  "id": "f401",
  "name": "Cheese, spread",
  "category": "Dairy",
  "refuse": 0,
  "water": 51.5,
  "energy": 289,
  "protein": 15.9,
  "fat": 22.9,
  "ash": 4.9,
  "fiber": 0,
  "carbohydrate": 4.8,
  "sodium": 1027,
  "potassium": 190,
  "calcium": 420,
  "phosphorus": 480,
  "magnesium": 25,
  "iron": 0.3,
  "zinc": 2.1,
  "copper": null,
  "vitaminA": 150,
  "vitaminC": 0,
  "thiamin": 0.05,
  "riboflavin": 0.36,
  "servingSize": null
},
{
  "id": "f402",
  "name": "Cheese, white",
  "category": "Dairy",
  "refuse": 0,
  "water": 53.4,
  "energy": 256,
  "protein": 16.3,
  "fat": 19.8,
  "ash": 7.3,
  "fiber": 0,
  "carbohydrate": 3.2,
  "sodium": 2769,
  "potassium": 115,
  "calcium": 456,
  "phosphorus": 273,
  "magnesium": 45,
  "iron": 0.2,
  "zinc": 1.9,
  "copper": 0.07,
  "vitaminA": 166,
  "vitaminC": 0,
  "thiamin": 0.08,
  "riboflavin": 0.37,
  "servingSize": null
},
{
  "id": "f403",
  "name": "Cheese, while half cream",
  "category": "Dairy",
  "refuse": 0,
  "water": 69.5,
  "energy": 151,
  "protein": 13,
  "fat": 8.9,
  "ash": 4,
  "fiber": 0,
  "carbohydrate": 4.6,
  "sodium": 805,
  "potassium": 146,
  "calcium": 357,
  "phosphorus": 175,
  "magnesium": 32,
  "iron": 0.25,
  "zinc": 1.4,
  "copper": 0.08,
  "vitaminA": 95,
  "vitaminC": 0,
  "thiamin": 0.1,
  "riboflavin": 0.35,
  "servingSize": null
},
{
  "id": "f404",
  "name": "Cheese, Roquefort",
  "category": "Dairy",
  "refuse": 0,
  "water": 40.5,
  "energy": 361,
  "protein": 22.3,
  "fat": 29,
  "ash": 5.5,
  "fiber": 0,
  "carbohydrate": 2.7,
  "sodium": 1390,
  "potassium": 207,
  "calcium": 415,
  "phosphorus": 385,
  "magnesium": 36,
  "iron": 0.8,
  "zinc": 2.34,
  "copper": 0.09,
  "vitaminA": 295,
  "vitaminC": 0,
  "thiamin": 0.05,
  "riboflavin": 0.4,
  "servingSize": null
},
{
  "id": "f405",
  "name": "Cream, whipping",
  "category": "Dairy",
  "refuse": 0,
  "water": 56.6,
  "energy": 353,
  "protein": 3,
  "fat": 36.4,
  "ash": 0.7,
  "fiber": 0,
  "carbohydrate": 3.3,
  "sodium": 40,
  "potassium": 76,
  "calcium": 81,
  "phosphorus": 25,
  "magnesium": 5,
  "iron": 0.25,
  "zinc": 0.2,
  "copper": 0.08,
  "vitaminA": 275,
  "vitaminC": 0,
  "thiamin": 0.02,
  "riboflavin": 0.12,
  "servingSize": null
},
{
  "id": "f406",
  "name": "Fermented milk",
  "category": "Dairy",
  "refuse": 0,
  "water": 87.7,
  "energy": 62,
  "protein": 3.8,
  "fat": 3.2,
  "ash": 0.8,
  "fiber": 0,
  "carbohydrate": 4.5,
  "sodium": 51,
  "potassium": 144,
  "calcium": 122,
  "phosphorus": 90,
  "magnesium": 10,
  "iron": 0.05,
  "zinc": 0.31,
  "copper": 0.05,
  "vitaminA": 38,
  "vitaminC": "T",
  "thiamin": 0.23,
  "riboflavin": 1.03,
  "servingSize": null
},
{
  "id": "f407",
  "name": "Fermented skim milk",
  "category": "Dairy",
  "refuse": 0,
  "water": 88.4,
  "energy": 46,
  "protein": 4.9,
  "fat": 0.8,
  "ash": 1,
  "fiber": 0,
  "carbohydrate": 4.9,
  "sodium": 54,
  "potassium": 150,
  "calcium": 132,
  "phosphorus": 95,
  "magnesium": 13,
  "iron": 0.05,
  "zinc": 0.35,
  "copper": 0.03,
  "vitaminA": 0,
  "vitaminC": "T",
  "thiamin": 0.02,
  "riboflavin": 0.12,
  "servingSize": null
},
{
  "id": "f408",
  "name": "Milk, buffalo",
  "category": "Dairy",
  "refuse": 0,
  "water": 82.5,
  "energy": 102,
  "protein": 4.2,
  "fat": 7.1,
  "ash": 0.8,
  "fiber": 0,
  "carbohydrate": 5.4,
  "sodium": 52,
  "potassium": 142,
  "calcium": 180,
  "phosphorus": 86,
  "magnesium": 17,
  "iron": 0.2,
  "zinc": 0.22,
  "copper": 0.01,
  "vitaminA": 22,
  "vitaminC": 3,
  "thiamin": 0.1,
  "riboflavin": 0.35,
  "servingSize": null
},
{
  "id": "f409",
  "name": "Milk, cow",
  "category": "Dairy",
  "refuse": 0,
  "water": 87.8,
  "energy": 64,
  "protein": 3.3,
  "fat": 3.7,
  "ash": 0.8,
  "fiber": 0,
  "carbohydrate": 4.4,
  "sodium": 54,
  "potassium": 149,
  "calcium": 123,
  "phosphorus": 87,
  "magnesium": 17,
  "iron": 0.05,
  "zinc": 0.3,
  "copper": 0.02,
  "vitaminA": 35,
  "vitaminC": 0,
  "thiamin": 0.04,
  "riboflavin": 0.19,
  "servingSize": null
},
{
  "id": "f410",
  "name": "Milk, human",
  "category": "Dairy",
  "refuse": 0,
  "water": 87.1,
  "energy": 70,
  "protein": 1.2,
  "fat": 3.9,
  "ash": 0.3,
  "fiber": 0,
  "carbohydrate": 7.5,
  "sodium": 17,
  "potassium": 52,
  "calcium": 32,
  "phosphorus": 13,
  "magnesium": 30,
  "iron": 0.15,
  "zinc": 1.3,
  "copper": null,
  "vitaminA": 97,
  "vitaminC": 5,
  "thiamin": 0.02,
  "riboflavin": 0.04,
  "servingSize": null
},
{
  "id": "f411",
  "name": "Milk, chocolate",
  "category": "Dairy",
  "refuse": 0,
  "water": 81.2,
  "energy": 80,
  "protein": 3.2,
  "fat": 1.7,
  "ash": 1,
  "fiber": 0,
  "carbohydrate": 12.9,
  "sodium": 64,
  "potassium": 131,
  "calcium": 122,
  "phosphorus": 87,
  "magnesium": 18,
  "iron": 0.31,
  "zinc": 0.41,
  "copper": 0.8,
  "vitaminA": 24,
  "vitaminC": 0,
  "thiamin": 0.04,
  "riboflavin": 0.16,
  "servingSize": null
},
{
  "id": "f412",
  "name": "Milk, full cream",
  "category": "Dairy",
  "refuse": 0,
  "water": 87.8,
  "energy": 64,
  "protein": 3.3,
  "fat": 3.5,
  "ash": 0.7,
  "fiber": 0,
  "carbohydrate": 4.7,
  "sodium": 48,
  "potassium": 142,
  "calcium": 120,
  "phosphorus": 92,
  "magnesium": 15,
  "iron": 0.06,
  "zinc": 0.4,
  "copper": 0.01,
  "vitaminA": 37,
  "vitaminC": 0,
  "thiamin": 0.04,
  "riboflavin": 0.17,
  "servingSize": null
},
{
  "id": "f413",
  "name": "Milk, fruit",
  "category": "Dairy",
  "refuse": 0,
  "water": 82,
  "energy": 76,
  "protein": 3.2,
  "fat": 1.7,
  "ash": 1.1,
  "fiber": 0,
  "carbohydrate": 12,
  "sodium": 59,
  "potassium": 175,
  "calcium": 125,
  "phosphorus": 80,
  "magnesium": 17,
  "iron": 0.04,
  "zinc": 0.41,
  "copper": 0.04,
  "vitaminA": 22,
  "vitaminC": 2,
  "thiamin": 0.07,
  "riboflavin": 0.19,
  "servingSize": null
},
{
  "id": "f414",
  "name": "Milk, half cream",
  "category": "Dairy",
  "refuse": 0,
  "water": 89.1,
  "energy": 48,
  "protein": 3.4,
  "fat": 1.5,
  "ash": 0.9,
  "fiber": 0,
  "carbohydrate": 5.1,
  "sodium": 52,
  "potassium": 153,
  "calcium": 128,
  "phosphorus": 88,
  "magnesium": 17,
  "iron": 0.05,
  "zinc": 0.4,
  "copper": 0.02,
  "vitaminA": 23,
  "vitaminC": 0,
  "thiamin": 0.08,
  "riboflavin": 0.2,
  "servingSize": null
},
{
  "id": "f415",
  "name": "Milk, low fat skimmed",
  "category": "Dairy",
  "refuse": 0,
  "water": 89.6,
  "energy": 41,
  "protein": 3.5,
  "fat": 0.6,
  "ash": 1,
  "fiber": 0,
  "carbohydrate": 5.3,
  "sodium": 50,
  "potassium": 143,
  "calcium": 126,
  "phosphorus": 95,
  "magnesium": 16,
  "iron": 0.05,
  "zinc": 0.4,
  "copper": 0.03,
  "vitaminA": 0,
  "vitaminC": 0,
  "thiamin": 0.1,
  "riboflavin": 0.21,
  "servingSize": null
},
{
  "id": "f416",
  "name": "Milk powder, skim",
  "category": "Dairy",
  "refuse": 0,
  "water": 5,
  "energy": 362,
  "protein": 37,
  "fat": 1.5,
  "ash": 6.5,
  "fiber": 0,
  "carbohydrate": 50,
  "sodium": 462,
  "potassium": 1201,
  "calcium": 1045,
  "phosphorus": 803,
  "magnesium": 95,
  "iron": 0.45,
  "zinc": 4.3,
  "copper": null,
  "vitaminA": 0,
  "vitaminC": 5,
  "thiamin": 0.45,
  "riboflavin": 2.1,
  "servingSize": null
},
{
  "id": "f417",
  "name": "Milk, powder whole",
  "category": "Dairy",
  "refuse": 0,
  "water": 2.7,
  "energy": 497,
  "protein": 26.7,
  "fat": 26.2,
  "ash": 5.7,
  "fiber": 0,
  "carbohydrate": 38.7,
  "sodium": 350,
  "potassium": 1450,
  "calcium": 930,
  "phosphorus": 850,
  "magnesium": 105,
  "iron": 0.4,
  "zinc": 3.53,
  "copper": 0.2,
  "vitaminA": 353,
  "vitaminC": 10,
  "thiamin": 0.4,
  "riboflavin": 1.4,
  "servingSize": null
},
{
  "id": "f418",
  "name": "Yoghurt",
  "category": "Dairy",
  "refuse": 0,
  "water": 87,
  "energy": 64,
  "protein": 3.4,
  "fat": 3.2,
  "ash": 0.9,
  "fiber": 0,
  "carbohydrate": 5.5,
  "sodium": 62,
  "potassium": 180,
  "calcium": 168,
  "phosphorus": 101,
  "magnesium": 16,
  "iron": 0.2,
  "zinc": 0.2,
  "copper": 0.02,
  "vitaminA": 30,
  "vitaminC": "T",
  "thiamin": 0.06,
  "riboflavin": 0.18,
  "servingSize": null
},
{
  "id": "f419",
  "name": "Yoghurt, low faf",
  "category": "Dairy",
  "refuse": 0,
  "water": 87.5,
  "energy": 50,
  "protein": 4,
  "fat": 0.9,
  "ash": 1.2,
  "fiber": 0,
  "carbohydrate": 6.4,
  "sodium": 60,
  "potassium": 185,
  "calcium": 170,
  "phosphorus": 105,
  "magnesium": 14,
  "iron": 0.2,
  "zinc": 0.22,
  "copper": 0.01,
  "vitaminA": 0,
  "vitaminC": "T",
  "thiamin": 0.07,
  "riboflavin": 0.19,
  "servingSize": null
},
{
  "id": "f420",
  "name": "Yoghurt, fruits",
  "category": "Dairy",
  "refuse": 0,
  "water": 82.5,
  "energy": 68,
  "protein": 3,
  "fat": 1.2,
  "ash": 1.2,
  "fiber": 0.9,
  "carbohydrate": 11.2,
  "sodium": 85,
  "potassium": 199,
  "calcium": 162,
  "phosphorus": 140,
  "magnesium": 15,
  "iron": 0.25,
  "zinc": 0.7,
  "copper": 0.07,
  "vitaminA": 9,
  "vitaminC": 3,
  "thiamin": 0.08,
  "riboflavin": 0.23,
  "servingSize": null
},
{
    "id": "f421",
    "name": "Beer, nonalcoholic",
    "category": "Beverages",
    "refuse": 0,
    "water": 95.8,
    "energy": 16,
    "protein": 0.2,
    "fat": null,
    "ash": 0.2,
    "fiber": null,
    "carbohydrate": 3.8,
    "sodium": 8,
    "potassium": 25,
    "calcium": 2,
    "phosphorus": 19,
    "magnesium": null,
    "iron": 0.03,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f422",
    "name": "Carbonated, barley drinks",
    "category": "Beverages",
    "refuse": 0,
    "water": 89.4,
    "energy": 42,
    "protein": null,
    "fat": null,
    "ash": 0.1,
    "fiber": null,
    "carbohydrate": 10.5,
    "sodium": 2,
    "potassium": 25,
    "calcium": 12,
    "phosphorus": 10,
    "magnesium": null,
    "iron": 0.03,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f423",
    "name": "Carbonated, soft drinks",
    "category": "Beverages",
    "refuse": 0,
    "water": 89.2,
    "energy": 42,
    "protein": null,
    "fat": null,
    "ash": 0.2,
    "fiber": null,
    "carbohydrate": 10.6,
    "sodium": 7,
    "potassium": 2,
    "calcium": 3,
    "phosphorus": 15,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f424",
    "name": "Carbonated, soft drinks diet",
    "category": "Beverages",
    "refuse": 0,
    "water": 99.7,
    "energy": 1,
    "protein": null,
    "fat": null,
    "ash": 0.1,
    "fiber": null,
    "carbohydrate": 0.2,
    "sodium": 4,
    "potassium": 3,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f425",
    "name": "Carbonated, tonic water",
    "category": "Beverages",
    "refuse": 0,
    "water": 89.7,
    "energy": 40,
    "protein": null,
    "fat": null,
    "ash": 0.3,
    "fiber": null,
    "carbohydrate": 10,
    "sodium": 8,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f426",
    "name": "Drink, chocolate instant",
    "category": "Beverages",
    "refuse": 0,
    "water": 2,
    "energy": 412,
    "protein": 6.2,
    "fat": 6,
    "ash": 1.5,
    "fiber": 0.9,
    "carbohydrate": 83.4,
    "sodium": 280,
    "potassium": 408,
    "calcium": 29,
    "phosphorus": null,
    "magnesium": null,
    "iron": 2.4,
    "zinc": 1.8,
    "copper": 1.1,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 0.06,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f427",
    "name": "Drink, coffee instant powder",
    "category": "Beverages",
    "refuse": 0,
    "water": 2.5,
    "energy": 360,
    "protein": 20.5,
    "fat": 0.5,
    "ash": 8.2,
    "fiber": null,
    "carbohydrate": 68.3,
    "sodium": 110,
    "potassium": 1205,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": 3.29,
    "zinc": 0.5,
    "copper": 0.05,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f428",
    "name": "Drink, fruit instant powder",
    "category": "Beverages",
    "refuse": 0,
    "water": 0.3,
    "energy": 392,
    "protein": 1.1,
    "fat": null,
    "ash": 1.7,
    "fiber": null,
    "carbohydrate": 96.9,
    "sodium": 183,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 100,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f429",
    "name": "Drink, tea instant powder",
    "category": "Beverages",
    "refuse": 0,
    "water": 0.5,
    "energy": 396,
    "protein": 1,
    "fat": null,
    "ash": 0.6,
    "fiber": null,
    "carbohydrate": 97.9,
    "sodium": 44,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f430",
    "name": "Juice, apples",
    "category": "Beverages",
    "refuse": 0,
    "water": 87.7,
    "energy": 48,
    "protein": 0.1,
    "fat": 0,
    "ash": 0.2,
    "fiber": 0,
    "carbohydrate": 12,
    "sodium": 3,
    "potassium": 105,
    "calcium": 8,
    "phosphorus": 10,
    "magnesium": 6,
    "iron": 0.4,
    "zinc": 0.03,
    "copper": 0.4,
    "vitaminA": 16,
    "vitaminC": 4,
    "thiamin": 0.02,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f431",
    "name": "Juice, apples, sweetened",
    "category": "Beverages",
    "refuse": 0,
    "water": 85.8,
    "energy": 56,
    "protein": 0,
    "fat": 0,
    "ash": 0.2,
    "fiber": 0,
    "carbohydrate": 14,
    "sodium": 5,
    "potassium": 100,
    "calcium": 7,
    "phosphorus": 10,
    "magnesium": 6,
    "iron": 0.3,
    "zinc": 0.03,
    "copper": 0.4,
    "vitaminA": null,
    "vitaminC": 2,
    "thiamin": 0.02,
    "riboflavin": 0.02,
    "servingSize": null
  },
  {
    "id": "f432",
    "name": "Juice, grapes, sweetened",
    "category": "Beverages",
    "refuse": 0,
    "water": 83,
    "energy": 66,
    "protein": 0.3,
    "fat": 0,
    "ash": 0.5,
    "fiber": 0,
    "carbohydrate": 16.2,
    "sodium": 5,
    "potassium": 130,
    "calcium": 15,
    "phosphorus": 14,
    "magnesium": 9,
    "iron": 0.4,
    "zinc": 0.03,
    "copper": 0.03,
    "vitaminA": 5,
    "vitaminC": 3,
    "thiamin": 0.03,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f433",
    "name": "Juice, grapefruit",
    "category": "Beverages",
    "refuse": 0,
    "water": 89.9,
    "energy": 39,
    "protein": 0.5,
    "fat": 0,
    "ash": 0.3,
    "fiber": 0.1,
    "carbohydrate": 9.2,
    "sodium": 3,
    "potassium": 155,
    "calcium": 7,
    "phosphorus": 16,
    "magnesium": 13,
    "iron": 0.3,
    "zinc": 0.4,
    "copper": 0.02,
    "vitaminA": null,
    "vitaminC": 30,
    "thiamin": 0.04,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f434",
    "name": "Juice, orange",
    "category": "Beverages",
    "refuse": 0,
    "water": 88.4,
    "energy": 46,
    "protein": 0.6,
    "fat": 0.2,
    "ash": 0.3,
    "fiber": 0.1,
    "carbohydrate": 10.4,
    "sodium": 4,
    "potassium": 220,
    "calcium": 10,
    "phosphorus": 19,
    "magnesium": 10,
    "iron": 0.4,
    "zinc": 0.3,
    "copper": 0.04,
    "vitaminA": 3,
    "vitaminC": 35,
    "thiamin": 0.08,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f435",
    "name": "Juice, orange, sweetened",
    "category": "Beverages",
    "refuse": 0,
    "water": 85.7,
    "energy": 56,
    "protein": 0.1,
    "fat": 0,
    "ash": 0.2,
    "fiber": 0,
    "carbohydrate": 14,
    "sodium": 3,
    "potassium": 170,
    "calcium": 10,
    "phosphorus": 45,
    "magnesium": 11,
    "iron": 0.3,
    "zinc": 0.4,
    "copper": 0.05,
    "vitaminA": 0,
    "vitaminC": 20,
    "thiamin": 0.08,
    "riboflavin": 0.04,
    "servingSize": null
  },
  {
    "id": "f436",
    "name": "Juice, peach",
    "category": "Beverages",
    "refuse": 0,
    "water": 87.3,
    "energy": 47,
    "protein": 0.5,
    "fat": 0.2,
    "ash": 0.6,
    "fiber": 0.5,
    "carbohydrate": 10.9,
    "sodium": 3,
    "potassium": 200,
    "calcium": 7,
    "phosphorus": 20,
    "magnesium": 8,
    "iron": 0.4,
    "zinc": 0.01,
    "copper": 0.02,
    "vitaminA": 18,
    "vitaminC": 5,
    "thiamin": 0.02,
    "riboflavin": 0.3,
    "servingSize": null
  },
  {
    "id": "f437",
    "name": "Juice, pomegranate",
    "category": "Beverages",
    "refuse": 0,
    "water": 84.2,
    "energy": 62,
    "protein": 1,
    "fat": 0,
    "ash": 0.4,
    "fiber": 0,
    "carbohydrate": 14.4,
    "sodium": 5,
    "potassium": 280,
    "calcium": 5,
    "phosphorus": 30,
    "magnesium": 8,
    "iron": 0.26,
    "zinc": 0.14,
    "copper": 0.2,
    "vitaminA": null,
    "vitaminC": 8,
    "thiamin": 0.04,
    "riboflavin": 0.02,
    "servingSize": null
  },
  {
    "id": "f438",
    "name": "Juice, sugarcane",
    "category": "Beverages",
    "refuse": 0,
    "water": 88.5,
    "energy": 45,
    "protein": 0,
    "fat": 0,
    "ash": 0.2,
    "fiber": 0,
    "carbohydrate": 11.3,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": 0.15,
    "zinc": null,
    "copper": null,
    "vitaminA": 0,
    "vitaminC": 2,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f439",
    "name": "Juice, tomato",
    "category": "Beverages",
    "refuse": 0,
    "water": 93,
    "energy": 24,
    "protein": 0.2,
    "fat": 0,
    "ash": 0.9,
    "fiber": 0.1,
    "carbohydrate": 5.8,
    "sodium": 250,
    "potassium": 203,
    "calcium": 9,
    "phosphorus": 20,
    "magnesium": 13,
    "iron": 0.5,
    "zinc": 0.2,
    "copper": null,
    "vitaminA": 42,
    "vitaminC": 30,
    "thiamin": 0.04,
    "riboflavin": 0.01,
    "servingSize": null
  },
  {
    "id": "f440",
    "name": "Nectar, apricot",
    "category": "Beverages",
    "refuse": 0,
    "water": 84.8,
    "energy": 59,
    "protein": 0.5,
    "fat": 0.1,
    "ash": 0.4,
    "fiber": 0.2,
    "carbohydrate": 14,
    "sodium": 4,
    "potassium": 105,
    "calcium": 12,
    "phosphorus": 18,
    "magnesium": 10,
    "iron": 0.4,
    "zinc": 0.05,
    "copper": 0.05,
    "vitaminA": 257,
    "vitaminC": 5,
    "thiamin": 0.04,
    "riboflavin": 0.03,
    "servingSize": null
  },
  {
    "id": "f441",
    "name": "Nectar, fruit cocktail",
    "category": "Beverages",
    "refuse": 0,
    "water": 84.4,
    "energy": 61,
    "protein": 0.1,
    "fat": 0.1,
    "ash": 0.4,
    "fiber": 0,
    "carbohydrate": 15,
    "sodium": 8,
    "potassium": 180,
    "calcium": 12,
    "phosphorus": 15,
    "magnesium": 4,
    "iron": 0.39,
    "zinc": 0.06,
    "copper": 0.1,
    "vitaminA": 94,
    "vitaminC": 12,
    "thiamin": 0.06,
    "riboflavin": 0.02,
    "servingSize": null
  },
  {
    "id": "f442",
    "name": "Nectar, guava",
    "category": "Beverages",
    "refuse": 0,
    "water": 84.5,
    "energy": 60,
    "protein": 0.1,
    "fat": 0,
    "ash": 0.4,
    "fiber": 0.1,
    "carbohydrate": 14.9,
    "sodium": 7,
    "potassium": 75,
    "calcium": 8,
    "phosphorus": 13,
    "magnesium": 5,
    "iron": 0.3,
    "zinc": 0.07,
    "copper": 0.04,
    "vitaminA": null,
    "vitaminC": 60,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f443",
    "name": "Nectar, mango",
    "category": "Beverages",
    "refuse": 0,
    "water": 84.1,
    "energy": 62,
    "protein": 0.2,
    "fat": 0.1,
    "ash": 0.4,
    "fiber": 0.1,
    "carbohydrate": 15.1,
    "sodium": 9,
    "potassium": 83,
    "calcium": 5,
    "phosphorus": 3,
    "magnesium": 1,
    "iron": 0.21,
    "zinc": 0.04,
    "copper": 0.06,
    "vitaminA": 89,
    "vitaminC": 15,
    "thiamin": 0.01,
    "riboflavin": 0.01,
    "servingSize": null
  },
  {
    "id": "f444",
    "name": "Cardamom",
    "category": "Condiments",
    "refuse": 0,
    "water": 12.6,
    "energy": 288,
    "protein": 12,
    "fat": 4.3,
    "ash": 6.4,
    "fiber": 14.3,
    "carbohydrate": 50.4,
    "sodium": 3,
    "potassium": 30,
    "calcium": 124,
    "phosphorus": 97,
    "magnesium": 54,
    "iron": 6,
    "zinc": 3.8,
    "copper": 1.4,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": 2.5,
    "riboflavin": 1.13,
    "servingSize": null
  },
  {
    "id": "f445",
    "name": "Cinnamon",
    "category": "Condiments",
    "refuse": 0,
    "water": 7.8,
    "energy": 263,
    "protein": 4,
    "fat": 2,
    "ash": 4.8,
    "fiber": 24.1,
    "carbohydrate": 57.3,
    "sodium": 15,
    "potassium": 380,
    "calcium": 500,
    "phosphorus": 42,
    "magnesium": 50,
    "iron": 14.2,
    "zinc": 2.4,
    "copper": 0.32,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": 0.08,
    "riboflavin": 0.35,
    "servingSize": null
  },
  {
    "id": "f446",
    "name": "Clove",
    "category": "Condiments",
    "refuse": 0,
    "water": 8,
    "energy": 348,
    "protein": 6.5,
    "fat": 8.3,
    "ash": 7,
    "fiber": 8.5,
    "carbohydrate": 61.7,
    "sodium": 300,
    "potassium": 1200,
    "calcium": 690,
    "phosphorus": 80,
    "magnesium": 250,
    "iron": 5.4,
    "zinc": 3.5,
    "copper": 0.4,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": 0.14,
    "riboflavin": 0.26,
    "servingSize": null
  },
  {
    "id": "f447",
    "name": "Coriander",
    "category": "Condiments",
    "refuse": 0,
    "water": 10.6,
    "energy": 284,
    "protein": 13.6,
    "fat": 15.7,
    "ash": 4.6,
    "fiber": 33.5,
    "carbohydrate": 22,
    "sodium": 35,
    "potassium": 1020,
    "calcium": 380,
    "phosphorus": 408,
    "magnesium": 85,
    "iron": 18.2,
    "zinc": 8.7,
    "copper": 1.2,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": 0.18,
    "riboflavin": 0.27,
    "servingSize": null
  },
  {
    "id": "f448",
    "name": "Cumin",
    "category": "Condiments",
    "refuse": 0,
    "water": 20.4,
    "energy": 327,
    "protein": 18,
    "fat": 13.3,
    "ash": 7.5,
    "fiber": 7,
    "carbohydrate": 33.8,
    "sodium": 145,
    "potassium": 2150,
    "calcium": 1025,
    "phosphorus": 480,
    "magnesium": 410,
    "iron": 37,
    "zinc": 5,
    "copper": 1.1,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f449",
    "name": "Ginger",
    "category": "Condiments",
    "refuse": 0,
    "water": 9.7,
    "energy": 338,
    "protein": 9,
    "fat": 3.4,
    "ash": 7,
    "fiber": 3,
    "carbohydrate": 67.9,
    "sodium": 30,
    "potassium": 1200,
    "calcium": 120,
    "phosphorus": 240,
    "magnesium": 165,
    "iron": 10.7,
    "zinc": 5.1,
    "copper": 0.4,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": 0.11,
    "riboflavin": 0.2,
    "servingSize": null
  },
  {
    "id": "f450",
    "name": "Nutmeg",
    "category": "Condiments",
    "refuse": 0,
    "water": 9.8,
    "energy": 511,
    "protein": 7.5,
    "fat": 34.7,
    "ash": 2.8,
    "fiber": 3,
    "carbohydrate": 42.2,
    "sodium": 17,
    "potassium": 400,
    "calcium": 210,
    "phosphorus": 185,
    "magnesium": 172,
    "iron": 2.1,
    "zinc": 1.7,
    "copper": 1.1,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f451",
    "name": "Pepper, black",
    "category": "Condiments",
    "refuse": 0,
    "water": 10.5,
    "energy": 343,
    "protein": 14.7,
    "fat": 4.6,
    "ash": 4.5,
    "fiber": 5.1,
    "carbohydrate": 60.6,
    "sodium": 20,
    "potassium": 1400,
    "calcium": 360,
    "phosphorus": 185,
    "magnesium": 155,
    "iron": 12.5,
    "zinc": 1.4,
    "copper": 1.2,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": 0.05,
    "riboflavin": 0.02,
    "servingSize": null
  },
  {
    "id": "f452",
    "name": "Pepper, red",
    "category": "Condiments",
    "refuse": 0,
    "water": 11.8,
    "energy": 291,
    "protein": 15.6,
    "fat": 7.7,
    "ash": 7,
    "fiber": 18.1,
    "carbohydrate": 39.8,
    "sodium": 215,
    "potassium": 1150,
    "calcium": 140,
    "phosphorus": 230,
    "magnesium": 145,
    "iron": 8.7,
    "zinc": 3.4,
    "copper": 0.5,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": 0.2,
    "riboflavin": 0.81,
    "servingSize": null
  },
  {
    "id": "f453",
    "name": "Thyme",
    "category": "Condiments",
    "refuse": 0,
    "water": 11.6,
    "energy": 287,
    "protein": 12.2,
    "fat": 2.9,
    "ash": 8.4,
    "fiber": 11.8,
    "carbohydrate": 53.1,
    "sodium": 75,
    "potassium": 1000,
    "calcium": 210,
    "phosphorus": 180,
    "magnesium": 295,
    "iron": 41,
    "zinc": 5.8,
    "copper": 0.7,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": 1.8,
    "riboflavin": 0.9,
    "servingSize": null
  },
  {
    "id": "f454",
    "name": "Backing powder",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 2.1,
    "energy": 144,
    "protein": 0.9,
    "fat": null,
    "ash": 62,
    "fiber": null,
    "carbohydrate": 35,
    "sodium": 10953,
    "potassium": 170,
    "calcium": null,
    "phosphorus": 5404,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f455",
    "name": "Chicken stock",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 2,
    "energy": 305,
    "protein": 7.5,
    "fat": 25,
    "ash": 53,
    "fiber": 0,
    "carbohydrate": 12.5,
    "sodium": 6073,
    "potassium": 1113,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": 4.2,
    "zinc": 1.56,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f456",
    "name": "Coca powder",
    "category": "Miscellaneous",
    "refuse": null,
    "water": 4.5,
    "energy": 438,
    "protein": 17,
    "fat": 18.9,
    "ash": 5,
    "fiber": 4.6,
    "carbohydrate": 50,
    "sodium": 953,
    "potassium": 1420,
    "calcium": 130,
    "phosphorus": 650,
    "magnesium": 8.5,
    "iron": 6.6,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": 0,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f457",
    "name": "Pom powder",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 4.9,
    "energy": 312,
    "protein": 3.6,
    "fat": 1.4,
    "ash": 6.3,
    "fiber": 12.5,
    "carbohydrate": 71.3,
    "sodium": 119,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": 6.98,
    "zinc": null,
    "copper": null,
    "vitaminA": "T",
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f458",
    "name": "Gelatin",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 12.4,
    "energy": 345,
    "protein": 86,
    "fat": 0.1,
    "ash": 1.5,
    "fiber": null,
    "carbohydrate": null,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f459",
    "name": "Kishk",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 3.4,
    "energy": 380,
    "protein": 21.6,
    "fat": 7.2,
    "ash": 3.1,
    "fiber": 4.5,
    "carbohydrate": 57.2,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f460",
    "name": "Mayonnaise",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 14.7,
    "energy": 722,
    "protein": 1.3,
    "fat": 77.4,
    "ash": 1.5,
    "fiber": 0,
    "carbohydrate": 5.1,
    "sodium": 650,
    "potassium": 45,
    "calcium": 20,
    "phosphorus": 52,
    "magnesium": null,
    "iron": 0.5,
    "zinc": 0.4,
    "copper": null,
    "vitaminA": 80,
    "vitaminC": "T",
    "thiamin": 0.05,
    "riboflavin": 0.1,
    "servingSize": null
  },
  {
    "id": "f461",
    "name": "Oil (vegetable)",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 0.1,
    "energy": 899,
    "protein": null,
    "fat": 99.9,
    "ash": null,
    "fiber": null,
    "carbohydrate": null,
    "sodium": null,
    "potassium": null,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f462",
    "name": "Salad dressing",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 41,
    "energy": 370,
    "protein": 2.9,
    "fat": 29.1,
    "ash": 2.8,
    "fiber": 0,
    "carbohydrate": 24.2,
    "sodium": 520,
    "potassium": 18,
    "calcium": 27,
    "phosphorus": null,
    "magnesium": 10,
    "iron": 0.025,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f463",
    "name": "Salt, table/cooking",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 5,
    "energy": null,
    "protein": null,
    "fat": null,
    "ash": null,
    "fiber": null,
    "carbohydrate": null,
    "sodium": 38900,
    "potassium": 29,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f464",
    "name": "Salt, sodium reduced",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 5,
    "energy": null,
    "protein": null,
    "fat": null,
    "ash": null,
    "fiber": null,
    "carbohydrate": null,
    "sodium": 18000,
    "potassium": 25500,
    "calcium": null,
    "phosphorus": null,
    "magnesium": null,
    "iron": null,
    "zinc": null,
    "copper": null,
    "vitaminA": null,
    "vitaminC": null,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f465",
    "name": "Tamarind",
    "category": "Miscellaneous",
    "refuse": 50,
    "water": 22.5,
    "energy": 292,
    "protein": 3.1,
    "fat": 0.4,
    "ash": 2.1,
    "fiber": 0.3,
    "carbohydrate": 68.9,
    "sodium": null,
    "potassium": null,
    "calcium": 5,
    "phosphorus": null,
    "magnesium": null,
    "iron": 1,
    "zinc": "T",
    "copper": 6,
    "vitaminA": 0.44,
    "vitaminC": 0.16,
    "thiamin": null,
    "riboflavin": null,
    "servingSize": null
  },
  {
    "id": "f466",
    "name": "Yeast, compressed",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 72,
    "energy": 105,
    "protein": 13,
    "fat": 0.5,
    "ash": 2.4,
    "fiber": 0,
    "carbohydrate": 12.1,
    "sodium": 12,
    "potassium": 624,
    "calcium": 10,
    "phosphorus": 387,
    "magnesium": 50,
    "iron": 4.8,
    "zinc": 2.7,
    "copper": 1.7,
    "vitaminA": "T",
    "vitaminC": 4,
    "thiamin": 0.8,
    "riboflavin": 1.7,
    "servingSize": null
  },
  {
    "id": "f467",
    "name": "Yeast, dried",
    "category": "Miscellaneous",
    "refuse": 0,
    "water": 4.5,
    "energy": 357,
    "protein": 37,
    "fat": 1.5,
    "ash": 8.2,
    "fiber": 0,
    "carbohydrate": 48.8,
    "sodium": 85,
    "potassium": 1709,
    "calcium": 65,
    "phosphorus": 1595,
    "magnesium": 200,
    "iron": 16.8,
    "zinc": 8.2,
    "copper": 3.2,
    "vitaminA": "T",
    "vitaminC": 0,
    "thiamin": 2.51,
    "riboflavin": 5.94,
    "servingSize": null
  },
];

export const exercises: Exercise[] = [
    { id: "e1", name: "Bench Press", category: "Strength", targetMuscleGroup: "Chest", difficulty: "Intermediate", instructions: "Lie on a bench, lower the barbell to your chest, then push it back up until your arms are fully extended." },
    { id: "e2", name: "Incline Dumbbell Press", category: "Strength", targetMuscleGroup: "Chest", difficulty: "Intermediate", instructions: "Sit on an incline bench holding dumbbells, press them up until arms are extended, then lower to chest level." },
    { id: "e3", name: "Chest Fly", category: "Strength", targetMuscleGroup: "Chest", difficulty: "Beginner", instructions: "Lie on a flat bench holding dumbbells above your chest, open your arms wide, then bring them back together." },
    { id: "e4", name: "Push-ups", category: "Bodyweight", targetMuscleGroup: "Chest, Shoulders, Triceps", difficulty: "Beginner", instructions: "Start in plank position, lower your chest to the ground, then push back up." },
    { id: "e5", name: "Cable Chest Crossover", category: "Strength", targetMuscleGroup: "Chest", difficulty: "Intermediate", instructions: "Using cables at shoulder height, pull handles together in front of your chest, then return to start." },
    { id: "e6", name: "Dumbbell Pullover", category: "Strength", targetMuscleGroup: "Chest", difficulty: "Intermediate", instructions: "Lie on a bench holding a dumbbell over your chest, lower it behind your head, then pull it back up." },
    { id: "e7", name: "Machine Chest Press", category: "Strength", targetMuscleGroup: "Chest", difficulty: "Beginner", instructions: "Sit on the machine, press the handles forward until arms are extended, then return." },
    { id: "e8", name: "Squat", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Intermediate", instructions: "Stand with feet shoulder-width apart, lower your hips back and down, then push back up." },
    { id: "e9", name: "Leg Press", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Intermediate", instructions: "Sit on the leg press machine, push the platform away with your feet, then lower it back down." },
    { id: "e10", name: "Lunges", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Beginner", instructions: "Step forward with one leg, lower your body until both knees are bent at 90 degrees, push back up." },
    { id: "e11", name: "Romanian Deadlift", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Intermediate", instructions: "Hold a barbell, lower it by pushing hips back, keeping legs slightly bent, then stand back up." },
    { id: "e12", name: "Leg Extensions", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Beginner", instructions: "Sit on the machine, extend your legs out straight, then lower them back down." },
    { id: "e13", name: "Calf Raises", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Beginner", instructions: "Stand with feet shoulder-width apart, raise heels off the ground, then lower back down." },
    { id: "e14", name: "Bulgarian Split Squat", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Intermediate", instructions: "Place rear foot on bench, lower front leg into squat, then push back up." },
    { id: "e15", name: "Goblet Squat", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Beginner", instructions: "Hold a dumbbell close to your chest, squat down, then stand back up." },
    { id: "e16", name: "Hip Thrust", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Intermediate", instructions: "Rest upper back on a bench, place barbell on hips, thrust hips up, then lower." },
    { id: "e17", name: "Sumo Deadlift", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Advanced", instructions: "Stand with wide stance, lift barbell by extending hips and knees, then lower." },
    { id: "e18", name: "Step-Ups", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Beginner", instructions: "Step onto a bench or platform with one leg, then step down and repeat." },
    { id: "e19", name: "Glute Kickbacks", category: "Strength", targetMuscleGroup: "Legs", difficulty: "Beginner", instructions: "On hands and knees, extend one leg back and up, then return." },
    { id: "e20", name: "Deadlift", category: "Strength", targetMuscleGroup: "Back, Legs", difficulty: "Advanced", instructions: "Lift the barbell from the ground by extending your hips and knees, then lower it back down." },
    { id: "e21", name: "Pull-ups", category: "Bodyweight", targetMuscleGroup: "Back, Biceps", difficulty: "Intermediate", instructions: "Hang from a bar, pull your chin above the bar, then lower." },
    // Remaining exercises following the same pattern
  ];


export const businessMetrics = {
  clientGrowth: [
    { month: "Jan", clients: 10 },
    { month: "Feb", clients: 15 },
    { month: "Mar", clients: 18 },
    { month: "Apr", clients: 25 },
    { month: "May", clients: 28 },
    { month: "Jun", clients: 35 },
  ],
  revenue: [
    { month: "Jan", amount: 2000 },
    { month: "Feb", amount: 3200 },
    { month: "Mar", amount: 3800 },
    { month: "Apr", amount: 4500 },
    { month: "May", amount: 5100 },
    { month: "Jun", amount: 6500 },
  ],
  clientRetention: 85, // percentage
  averageSessionsPerClient: 4.2,
  newClientsThisMonth: 8,
};

// Update financial records to use client property instead of clientId
export const financialRecords: FinancialRecord[] = [
  {
    id: "fr1",
    date: new Date(today.getFullYear(), today.getMonth(), 15),
    type: "income",
    amount: 150,
    description: "Initial consultation fee",
    category: "Consultations",
    client: "c1",
    clientId: "c1" // Keep for backward compatibility
  },
  {
    id: "fr2",
    date: new Date(today.getFullYear(), today.getMonth(), 18),
    type: "income",
    amount: 100,
    description: "Follow-up session fee",
    category: "Consultations",
    client: "c2",
    clientId: "c2" // Keep for backward compatibility
  },
  {
    id: "fr3",
    date: new Date(today.getFullYear(), today.getMonth(), 10),
    type: "expense",
    amount: 75,
    description: "Office supplies",
    category: "Supplies"
  },
  {
    id: "fr4",
    date: new Date(today.getFullYear(), today.getMonth(), 5),
    type: "expense",
    amount: 200,
    description: "Software subscription",
    category: "Software"
  },
  {
    id: "fr5",
    date: new Date(today.getFullYear(), today.getMonth(), 20),
    type: "income",
    amount: 250,
    description: "Diet plan customization",
    category: "Diet Plans",
    client: "c3",
    clientId: "c3" // Keep for backward compatibility
  }
];
